import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import AudioPlayer from './AudioPlayer';
import FuriganaText, { renderFuriganaSegments, FuriganaFragment } from './FuriganaText';
import { useFurigana, useFuriganaAria } from '../contexts/FuriganaContext';
import { projectDecoratedText } from '../utils/furigana';
import { renderSvgFurigana, refreshSvgFurigana } from '../utils/svgFurigana';
import { realignAxisTitles, resetAxisTitles } from '../utils/axisTitlePlacement';
import { useSelectionCapture } from '../hooks/useSelectionCapture';
import api from '../utils/api';
import {
  createStageVisibilityChecker,
  createEffectiveVisibilityPredicate,
  isAxisElementId,
} from '../utils/overlayStageVisibility';
import { buildRenderStateSnapshot } from '../utils/renderStateSnapshot';
import { beginStageWrite, endStageWrite } from '../utils/stageWriteBarrier';
import { computeFunctionLabelBudget, FUNCTION_LABEL_BUDGET_MAX } from '../utils/supportLevelPolicy';
import { resolveFontFamily } from '../utils/fontFamilyResolver';
import {
  MIN_CHART_WIDTH,
  MAX_CHART_WIDTH,
  MIN_CHART_HEIGHT,
  MAX_CHART_HEIGHT,
  MIN_MARGIN,
  MIN_LEGEND_WIDTH,
  LEGEND_SNAP_THRESHOLD,
  LEGEND_KEYBOARD_STEP,
  LEGEND_KEYBOARD_FAST_STEP,
  clampValue,
  DEFAULT_LAYOUT,
  GLOBAL_TEXT_SIZE_STORAGE_KEY,
  cloneLayout,
} from '../utils/chartLayout';
import {
  computeLegendFontScale,
  legendVScaleCapped,
  vlScale,
  getLegendMetrics,
  countLegendItems,
} from '../utils/legendMetrics';
import {
  computeKeypointLabelOffsets,
  computeFuncLabelOffsets,
  placeFunctionLabels,
  computeFuncKeypoints,
  formatRadianLabel,
  FUNCTION_GRAPH_TYPE_SET,
  isNonTrigFunctionType,
} from '../utils/functionGraphLabels';
import {
  getCUDPalette,
  EXTENDED_COLOR_PALETTE,
  normalizeColorKey,
  mixColors,
  toRgba,
  getConsistentIndexFromId,
} from '../utils/colorUtils';
import {
  computeTrigKeypoints,
  generateSyntheticCurve,
  generateMathCurve,
} from '../utils/functionCurves';
import {
  collectXAxisLabels,
  determineXAxisLabelStrategy,
} from '../utils/axisLabelStrategy';
import { buildChartSummary, shouldAnnounceStage, isActivationKey } from '../utils/chartA11y';
import * as chartZoom from '../utils/chartZoom';
import { getSmartEmojiForLabel } from '../utils/emoji';
import {
  buildColorPreferenceStorageKey,
  createDefaultColorOverrides,
  createDefaultHighlightOptions,
  normalizeColorPreferenceScope,
} from '../utils/colorPreferences';
import {
  buildStageAnimationKey,
  getDifficultyTypeForStage,
  getStageVisibilityFlags,
  resolveTrigDisplayMode,
  resolveFuncDisplayMode,
  resolveInitialStageData,
} from '../utils/stageResolvers';
import {
  MAX_EXPLANATION_AUDIO_CACHE_ENTRIES,
  TTS_SLOW_WARNING_DELAY_MS,
  cleanTextForSpeech,
  enrichExplanationText,
  pickFirstNonEmptyText,
  pickFirstValidUrl,
  createAudioUrlFromBase64,
} from '../utils/explanationText';
import { getFocusVisualState } from '../utils/focusVisualState';
import {
  BAND_ELEMENT_TYPES,
  DOT_ELEMENT_TYPES,
  VENN_CIRCLE_ELEMENT_TYPES,
  VENN_REGION_ELEMENT_TYPES,
  TREE_NODE_ELEMENT_TYPES,
  TREE_BRANCH_ELEMENT_TYPES,
  DIFFICULTY_TYPE_INFO,
  DIFFICULTY_GROUP_HINTS,
  DEFAULT_GROUP_HINTS,
  ELEMENT_GROUP_DEFINITIONS,
  matchElementTypeToGroupKey,
} from '../utils/difficultyTaxonomy';
import {
  DEFAULT_STAT_OUTLINE_COLOR,
  STROKE_SHAPE_TAGS,
  enforceStrokes,
  applyAxisTextStyles,
} from '../utils/svgStyleHelpers';
import { logClientError, logClientWarn } from '../utils/safeLog';

const COMPACT_CHART_HEIGHT = 520;
const EMPTY_HIGHLIGHTED_ELEMENTS = Object.freeze([]);
const EMPTY_UI_ACTIONS = Object.freeze([]);
const sameJson = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);
const UPCOMING_CHART_TYPES = new Set();
const UPCOMING_CHART_LABELS = {};

// A2 (Tier-1 a11y / Gate B): dedicated localStorage key for the
// stage-transition aria-live announcer opt-in toggle (図の調整 panel).
// Follows the SAME load-once/save-on-change idiom as
// GLOBAL_TEXT_SIZE_STORAGE_KEY (see the axisPrefsLoaded load/save effects
// below) but is its OWN key with its OWN boolean payload — never shares a
// storage slot with the font-size prefs. Default OFF.
const ARIA_LIVE_ENABLED_STORAGE_KEY = 'graphTutor::ariaLiveEnabled::v1';

const DEFAULT_LABEL_COLOR = '#1e293b';
const DEFAULT_LEGEND_TEXT_COLOR = DEFAULT_LABEL_COLOR;
const DEFAULT_LEGEND_BACKGROUND = '#f8fafc';
const DEFAULT_EXPLANATION_COLOR = '#1f2532';
const DEFAULT_HIGHLIGHT_ACCENT_COLOR = '#facc15';
// Minimum duration (ms) that overlay effects remain visible before they can be cleared
const OVERLAY_MIN_DISPLAY_MS = 2000;
// A2 (Tier-1 a11y / Gate B): minimum interval (ms) between aria-live
// stage-change announcements. Aligned with the overlay re-application
// debounce delay (setTimeout(..., 500) inside the overlay useEffect below)
// so stage announcements settle on the same cadence as visual overlay
// updates, without literally sharing that timer/effect.
const ARIA_LIVE_ANNOUNCE_MIN_INTERVAL_MS = 500;

// Tier 2 overlay action types — renderer-owned visuals (grid lines, projection
// lines, pinned reference values, label simplification). Single source for the
// tier2 memo filter, the rendering-effect partition, and the overlay-effect
// exclusion filter (P4-3). applyOverlay must never gain a producer for these.
// Marks an explicitly EMPTY visible set (the student hid every element) so it
// stays distinguishable from 'no visibility constraints were given'. Matches no
// real element id or label by construction — the double underscores and the
// sentinel word cannot collide with a chart element or a legend label.
const STUDENT_HID_EVERYTHING = '__student_hid_everything__';

const TIER2_ACTION_TYPES = Object.freeze(['show_grid', 'show_projection_lines', 'pin_reference_value', 'simplify_labels']);

// Element type display names moved to i18n (ig.elementType.*), resolved at render time.

// normalizeVisibilityValue / extractVisibilityCandidates /
// createStageVisibilityChecker moved verbatim to
// utils/overlayStageVisibility.js (imported above) so the overlay engine's
// effectiveVisible predicate shares the exact renderer normalization.

const InteractiveGraph = ({ graphData, studentProfile, settings, highlightedElements = EMPTY_HIGHLIGHTED_ELEMENTS, focusElementId, supportLevel, onElementClick, sessionId, uiActions = EMPTY_UI_ACTIONS, replayState = null, onStageChange, onRenderStateChange , activeQuestionId = null, onElementHoverDwell = null, onTtsEvent = null }) => {
  const { t } = useTranslation();
  // Reading aid (ルビ) toggle — drives the SVG-native furigana pass below. HTML
  // chrome (tooltip / stage explanation / controls) uses <FuriganaText>, which
  // reads this same context internally; SVG <text> cannot host <ruby>, so the
  // chart labels get readings via renderSvgFurigana in the render effect.
  const { enabled: furiganaEnabled, ready: furiganaReady } = useFurigana();
  // Read by the render and overlay passes, which do NOT depend on the toggle: the
  // reading aid is applied to the drawn chart in place (see the effect after the
  // render effect, which also keeps this ref current), so its late arrival or a
  // toggle does not cost a full redraw — except in the overlay case noted there.
  const furiganaEnabledRef = useRef(furiganaEnabled);
  // Bumped by the in-place reading effect when it cannot apply the readings in
  // place (overlays measured from text are on screen) and needs a full redraw.
  const [readingRedrawTick, setReadingRedrawTick] = useState(0);
  // Reading aid for plain-text ATTRIBUTE surfaces (title / placeholder) that
  // cannot host <ruby>. No-op when the toggle is OFF. aria-label is deliberately
  // NOT wrapped (screen readers read kanji natively; a parenthetical double-announces).
  const fa = useFuriganaAria();
  const svgRef = useRef(null);
  const graphContainerRef = useRef(null);
  const [currentStageData, setCurrentStageData] = useState(() =>
    resolveInitialStageData(graphData)
  );
  const [currentStageNum, setCurrentStageNum] = useState(graphData?.current_stage ?? 0);
  const [isLoading, setIsLoading] = useState(false);
  const [stageExplanation, setStageExplanation] = useState(
    graphData?.stage_explanation || graphData?.current_stage_explanation || ''
  );
  // Which chart the shown stage data and the explanation belong to (Codex B-R9): right after a
  // chart switch both still hold the previous chart's content for a render or two.
  const [stageDataGraphId, setStageDataGraphId] = useState(graphData?.graph_id ?? null);
  const [explanationGraphId, setExplanationGraphId] = useState(graphData?.graph_id ?? null);
  const [explanationAudioUrl, setExplanationAudioUrl] = useState(
    graphData?.explanation_audio_url || graphData?.current_stage_audio_url || ''
  );
  const [generatedExplanationAudioUrl, setGeneratedExplanationAudioUrl] = useState('');
  const [isGeneratingExplanationAudio, setIsGeneratingExplanationAudio] = useState(false);
  const [explanationAudioError, setExplanationAudioError] = useState(null);
  const [isLayoutPanelOpen, setIsLayoutPanelOpen] = useState(false);
  // Close the「図の調整 / Adjust graph」modal on Escape (mirrors the backdrop click).
  useEffect(() => {
    if (!isLayoutPanelOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setIsLayoutPanelOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLayoutPanelOpen]);
  const [layoutOverrides, setLayoutOverrides] = useState({
    width: null,
    height: null,
    margin: { top: null, right: null, bottom: null, left: null },
    legendPosition: null,
    legendManual: null,
    legendFontScale: null,
    legendLineSpacing: null,
    valueLabelScale: null,
    // Function-graph coordinate-label controls (null = follow the AI default):
    // functionLabelDensity = # of EXTRA sampled labels beyond the always-kept
    // keypoints; functionLabelsVisible = show/hide those extras (keypoints stay).
    functionLabelDensity: null,
    functionLabelsVisible: null,
    axis: { xTickFontSize: null, yTickFontSize: null, xLabelFontSize: null, yLabelFontSize: null }
  });
  const [containerWidth, setContainerWidth] = useState(DEFAULT_LAYOUT.width);
  const [elementVisibilityOverrides, setElementVisibilityOverrides] = useState({});

  // A hide is only justified against the question that was active when the
  // student asked for it. Selecting a different question can make that same
  // element ESSENTIAL (it appears in the new question's graph_focus), and the
  // override would silently keep it off screen — the backend refuses such a hide
  // at request time, but nothing re-checked an override already granted.
  // Dropping them on question change restores the documented guarantee without
  // needing a second resolver round-trip. (Codex R4 P1.)
  //
  // Only on a REAL switch between two questions. The id starts null and becomes
  // the first question's id once the problem finishes loading, and treating that
  // as a "change" wiped overrides that had just been applied — a race the D5
  // absoluteness test caught only under full-suite timing (it passed standalone,
  // where the problem loaded before the action arrived).
  // Phase A research callbacks (both optional, null unless the parent's
  // instrumentation flag is on). Held in refs and read at call time so that
  // passing or changing them can never change a render dependency — a new
  // dependency here would add full-chart redraws.
  const hoverDwellRef = useRef(onElementHoverDwell);
  const ttsEventRef = useRef(onTtsEvent);
  // The chart on screen (committed), for responses that return after a chart switch.
  const currentGraphIdRef = useRef(graphData?.graph_id ?? null);
  useLayoutEffect(() => {
    currentGraphIdRef.current = graphData?.graph_id ?? null;
  });
  // Published after commit, so a render React abandons never repoints them (Codex B-R7).
  useLayoutEffect(() => {
    hoverDwellRef.current = onElementHoverDwell;
    ttsEventRef.current = onTtsEvent;
  });

  const previousQuestionIdRef = useRef(activeQuestionId);
  useEffect(() => {
    const previous = previousQuestionIdRef.current;
    previousQuestionIdRef.current = activeQuestionId;
    if (previous == null || previous === activeQuestionId) return;
    setElementVisibilityOverrides({});
  }, [activeQuestionId]);
  const [overlayRenderTick, setOverlayRenderTick] = useState(0);
  // Chart magnification (Tier-1 a11y). Ephemeral {scale, tx, ty}; intentionally
  // NOT in the D3 render effect's dep array — zoom is a CSS transform on a
  // React-owned wrapper OUTSIDE the <svg>, so it must never trigger a chart
  // redraw. isZoomDragging suppresses the transform transition during a
  // pointer-drag pan so the chart tracks the pointer without lag.
  const [zoomState, setZoomState] = useState(chartZoom.IDENTITY);
  // Mirror zoomState in a ref so pointer-drag handlers (startDrag) can read the
  // CURRENT scale without being recreated (and re-bound) on every zoom change.
  const zoomStateRef = useRef(zoomState);
  useEffect(() => { zoomStateRef.current = zoomState; }, [zoomState]);
  const [isZoomDragging, setIsZoomDragging] = useState(false);
  const zoomPointerRef = useRef(null);
  // Reset magnification when a different graph loads. Dashboard swaps graphData
  // in place WITHOUT remounting this component, so without this a new chart would
  // inherit the previous chart's scale/pan and could open cropped (Codex P2).
  useEffect(() => {
    setZoomState(chartZoom.reset());
  }, [graphData?.graph_id]);
  // Re-clamp the current pan whenever the container resizes (separator drag,
  // window resize, chart-height change) so magnified content can never get
  // stranded outside the overflow-clipped card until the next zoom op (Codex P2).
  useEffect(() => {
    const el = graphContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      setZoomState((s) =>
        chartZoom.isZoomed(s)
          ? chartZoom.clampState(s, { width: el.clientWidth, height: el.clientHeight })
          : s
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const [drilldownInfo, setDrilldownInfo] = useState(null);
  const [isElementPanelOpen, setIsElementPanelOpen] = useState(false);
  const [colorOverrides, setColorOverrides] = useState(createDefaultColorOverrides);
  const [highlightOptions, setHighlightOptions] = useState(createDefaultHighlightOptions);
  const [highlightAccentColor, setHighlightAccentColor] = useState(settings?.highlightAccentColor || DEFAULT_HIGHLIGHT_ACCENT_COLOR);
  const [explanationHighlights, setExplanationHighlights] = useState([]);
  const [colorPrefsLoaded, setColorPrefsLoaded] = useState(false);
  const [axisPrefsLoaded, setAxisPrefsLoaded] = useState(false);
  // A2 (Tier-1 a11y / Gate B): stage-transition aria-live announcer opt-in
  // (図の調整 panel checkbox), persisted under ARIA_LIVE_ENABLED_STORAGE_KEY.
  // Default OFF. stageAnnounceText is what actually renders inside the
  // visually-hidden aria-live region — kept as its own state (rather than
  // just reading the latest computed message directly) so it can be forced
  // back to '' the instant the toggle is switched off.
  const [ariaLiveEnabled, setAriaLiveEnabled] = useState(false);
  const [ariaLiveEnabledLoaded, setAriaLiveEnabledLoaded] = useState(false);
  const [stageAnnounceText, setStageAnnounceText] = useState('');
  // show_value_tags overlay が現在の値ラベル倍率を read できるよう常時同期する ref。
  const valueLabelScaleRef = useRef(1);
  // A2: stage-transition identity/throttle bookkeeping for the aria-live
  // announcer only — never read or written by applyOverlay/restoreOverlay
  // or any overlay/highlight/dim/focus code path.
  const lastAnnouncedStageKeyRef = useRef(null);
  const lastAnnounceTsRef = useRef(null);
  const ariaLiveWasEnabledRef = useRef(false);
  // Latest stage-visibility context for applyOverlay's ghost-dim guard.
  // applyOverlay's deps deliberately exclude stage data (a direct reference
  // would be a stale closure since stage state changes do not re-create the
  // callback) — a latest-value ref delivers the current stage on every apply
  // pass. Synced by an effect declared right after stageDataForDisplay, which
  // runs before the overlay useEffect within the same commit (declaration
  // order), and the overlay apply itself executes later via RAF/debounce.
  const overlayVisibilityContextRef = useRef({
    visibleElements: null,
    showAllWithOriginalStyle: false,
    highlightedElements: [],
  });
  const [ttsRetryToken, setTtsRetryToken] = useState(0);
  const [isTtsSlowWarning, setIsTtsSlowWarning] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [trigViewMode, setTrigViewMode] = useState('curve_only'); // 'unit_circle' | 'curve_only' | 'dual_pane'
  const stageCatalog = useMemo(
    () => graphData?.stages || graphData?.graph_data?.stages || [],
    [graphData?.graph_data?.stages, graphData?.stages]
  );

  // F2: shared PERSISTED stage-jump. `sessions.current_stage` is the authoritative store
  // (backend A-3), but only the next/prev buttons + reset hit an endpoint. Every OTHER stage
  // transition — arrow-key nav (handleJumpToStage), AI set_stage/restore_stage, and the
  // focus-triggered last-stage jump — updated React state ONLY, so a reload / cold restore /
  // another (horizontally-scaled) Cloud Run instance resumed from a STALE persisted stage.
  // Route all of those through this single best-effort set-stage POST so the DB matches what
  // the learner sees. Ref-based (stable identity, empty deps) so the consume-once overlay
  // branches can call it without widening their dependency arrays. Best-effort: the visual
  // jump already applied via React state; a failed POST only lags the persisted column,
  // exactly like the pre-existing next/prev error path.
  const stagePersistRef = useRef({ graphId: graphData?.graph_id, sessionId });
  stagePersistRef.current = { graphId: graphData?.graph_id, sessionId };
  // G2: latest-wins coalesce. Absolute set-stage is last-write-wins on the backend (it commits
  // every request unconditionally), so rapid jumps (arrow spam, or an AI jump then a manual one)
  // firing CONCURRENT POSTs let a slower OLDER request land last and overwrite the newer visual
  // stage. Serialise instead: keep at most ONE set-stage POST in flight; while one is outstanding
  // a later jump only records the NEWEST requested stage (older queued values are dropped), and
  // that latest value is flushed once the in-flight request settles. The LAST POST the backend
  // sees therefore always carries the newest stage, so an older value can never overwrite it.
  // NOTE: this guards the common single-instance rapid-jump case. A fully-correct guarantee across
  // horizontally-scaled instances (two tabs / peers) would need a backend monotonic stage-version
  // (reject-if-older) — FLAGGED, not implemented here (no migration added).
  const stageCoalesceRef = useRef({ inFlight: false, pending: {} });
  const persistStageJump = useCallback((stage) => {
    const { graphId, sessionId: sid } = stagePersistRef.current;
    if (!graphId || stage == null || !Number.isFinite(stage)) return;
    const coalesce = stageCoalesceRef.current;
    // Snapshot the graph/session identity WITH the stage so a queued flush persists the value
    // against the graph it was requested for (never a graph that was switched-to mid-flight).
    const req = { graphId, sessionId: sid, stage };
    // Queue PER (graph, session). Dashboard reuses one InteractiveGraph instance across session
    // switches, so a single pending slot let a jump in the new session overwrite an already
    // queued final jump for the old one — leaving that session's persisted stage stale.
    const slot = `${graphId}|${sid ?? ''}`;
    if (coalesce.inFlight) {
      // A set-stage POST is already outstanding — record only the newest request PER SLOT; it is
      // flushed when the in-flight request settles. Supersedes any earlier queued value for the
      // SAME slot (latest wins), while other slots keep their own.
      // The barrier counts QUEUED writes too. Counting only in-flight ones let the count reach
      // zero in the gap between a request settling and its queued flush starting, which woke a
      // waiting chat retry that then raced the very write it was waiting for.
      if (coalesce.pending[slot] == null) beginStageWrite();
      coalesce.pending[slot] = req;
      return;
    }
    const send = (r) => {
      coalesce.inFlight = true;
      let persisted = false;
      api.post(`/api/graphs/${r.graphId}/set-stage`, null, {
        params: { stage: r.stage, session_id: r.sessionId || undefined },
      }).then(() => {
        persisted = true;
      }).catch((e) => {
        logClientWarn('Failed to persist stage jump to backend:', e);
      }).finally(() => {
        coalesce.inFlight = false;
        const slots = Object.keys(coalesce.pending);
        const nextSlot = slots.length ? slots[0] : null;
        const next = nextSlot == null ? null : coalesce.pending[nextSlot];
        if (nextSlot != null) delete coalesce.pending[nextSlot];
        // Flush the newest queued request, unless it is an exact duplicate of what just
        // persisted SUCCESSFULLY. Skipping it on a FAILED POST left the DB on the old stage
        // while a second request for that very value was thrown away.
        const isRedundant =
          persisted && next != null &&
          next.graphId === r.graphId && next.stage === r.stage &&
          (next.sessionId ?? null) === (r.sessionId ?? null);
        if (next != null && !isRedundant) {
          // Hand this request's count straight to the flush — never release to zero in between.
          send(next);
        } else if (next != null) {
          endStageWrite(); // queued duplicate skipped: release the count it took
        }
        endStageWrite(); // this request's own count
      });
    };
    // Announce the write so a chat retry can wait for it: until this lands, the DB still says
    // the OLD stage, and a retry sent now loses the race and burns the student's only retry.
    beginStageWrite();
    send(req);
  }, []);
  const tier2ActionsRef = useRef([]);
  const tier2UiActions = useMemo(
    () => (uiActions || []).filter((action) => TIER2_ACTION_TYPES.includes(action?.type)),
    [uiActions]
  );

  const isTrigFunction = useMemo(() => {
    const gt = (graphData?.graph_data?.graph_type || graphData?.graph_type || '').toLowerCase();
    return gt === 'trigonometric_function';
  }, [graphData]);

  // ── 双方向ハイライト: getFocusVisualState に highlightedElements を自動注入 ──
  const getElementFocusStyle = useCallback((params) => {
    return getFocusVisualState({
      ...params,
      highlightedElements,
    });
  }, [highlightedElements]);

  // ── Overlay engine: restoreOverlay clears all overlay-added DOM elements ──
  // force=true bypasses the overlay lock (used only by the rendering useEffect
  // which does a full svg.selectAll('*').remove() anyway).
  // preserveTier2=true keeps the renderer-owned tier2 visuals (.overlay-grid-line /
  // .overlay-projection / .overlay-pin-reference / .overlay-pin-label) in place —
  // those are drawn by the renderers from tier2ActionsRef, NOT by applyOverlay,
  // so a clearing pass that cannot redraw them must not remove them (P4-1).
  // Tier1 removal and data-element resets stay unconditional.
  const restoreOverlay = useCallback((force = false, { preserveTier2 = false } = {}) => {
    if (!svgRef.current) return;
    // Respect overlay lock: if overlays were recently applied, skip clearing
    if (!force && Date.now() < overlayLockedUntilRef.current) {
      return;
    }
    const svg = d3.select(svgRef.current);
    // Remove overlay-specific elements (NOT .overlay-highlight — those are existing
    // chart elements that got the class added; removing them destroys the chart).
    svg.selectAll('.overlay-tag').remove();
    svg.selectAll('.overlay-bold-bg').remove();
    if (!preserveTier2) {
      svg.selectAll('.overlay-grid-line').remove();
      svg.selectAll('.overlay-projection').remove();
      svg.selectAll('.overlay-pin-reference').remove();
      svg.selectAll('.overlay-pin-label').remove();
    }
    // Reset opacity and overlay-specific stroke on data elements
    svg.selectAll('[data-element-id]').each(function () {
      const el = d3.select(this);
      el.classed('dimmed', false);
      // Reset opacity (overlay may have dimmed elements)
      el.style('opacity', null);
      // Only null out stroke on elements that were overlay-highlighted.
      // Shape elements that were NOT highlighted keep their renderer-set stroke intact.
      if (el.classed('overlay-highlight')) {
        const tag = (this.tagName || '').toLowerCase();
        if (tag === 'text') {
          // Text elements: reset overlay styles (renderer will re-apply if needed)
          el.style('font-weight', null);
          el.style('font-size', null);
          el.style('fill', null);
        } else {
          el.attr('stroke', null);
          el.attr('stroke-width', null);
        }
        el.classed('overlay-highlight', false);
      }
    });
    // Re-enforce base strokes on shape elements that lost theirs
    enforceStrokes(svg);
    // Reset axis groups
    svg.selectAll('.axis').each(function () {
      const g = d3.select(this);
      g.selectAll('path').attr('stroke-width', 2);
      g.selectAll('text').style('font-weight', null);
    });
  }, []);

  // ── Overlay engine: applyOverlay processes an array of ui_action objects ──
  const applyOverlay = useCallback((actions) => {
    if (!svgRef.current || !Array.isArray(actions) || actions.length === 0) return;
    const svg = d3.select(svgRef.current);
    const actualGraphData = graphData?.graph_data || graphData;
    const elements = actualGraphData?.elements || [];
    // Per-pass ghost-dim guard: rebuilt on EVERY apply pass from the latest
    // synced stage data, so the SAME action object re-applied after a stage
    // change is re-evaluated against the new stage. Action objects (including
    // their element_ids arrays) are never mutated below — consume-once
    // (consumedOneShotActionsRef WeakSet) and the voice dedup key both rely
    // on object/array identity. Filters are pass-local variables only.
    const effectiveVisible = createEffectiveVisibilityPredicate({
      ...overlayVisibilityContextRef.current,
      elements,
    });
    actions.forEach((action) => {
      const { type, params = {} } = action;
      // null-safe: backend model_dump() emits element_ids: null (not undefined), so a
      // destructure default would NOT apply; coerce null/non-array to [] to avoid
      // null.filter() in the element-targeted cases (highlight_elements / show_value_tags / ...).
      const element_ids = Array.isArray(action.element_ids) ? action.element_ids : [];

      switch (type) {
        case 'highlight_elements': {
          // Pass-local filter (action.element_ids stays untouched): never paint
          // a gold edge on a stage-invisible ghost.
          const highlightTargetIds = element_ids.filter((eid) => effectiveVisible(eid));
          highlightTargetIds.forEach((eid) => {
            svg.selectAll(`[data-element-id="${eid}"]`).each(function () {
              const el = d3.select(this);
              const tag = (this.tagName || '').toLowerCase();
              el.classed('overlay-highlight', true);
              // Text elements: bold + font-size pulse
              if (tag === 'text') {
                el.style('font-weight', '900');
                const origSize = parseFloat(el.style('font-size')) || 12;
                el.transition('overlay-pulse')
                  .duration(800)
                  .style('font-size', (origSize * 1.25) + 'px')
                  .transition()
                  .duration(800)
                  .style('font-size', origSize + 'px');
                return;
              }
              // <g> containers: find child text elements (value labels) and emphasize them
              if (tag === 'g') {
                el.selectAll('text').each(function () {
                  const textEl = d3.select(this);
                  textEl.classed('overlay-highlight', true);
                  const origSize = parseFloat(textEl.style('font-size')) || 12;
                  const emphSize = Math.max(origSize * 1.4, 14);
                  textEl.style('font-weight', '900')
                    .style('font-size', emphSize + 'px')
                    .style('fill', '#b45309');  // warm amber for emphasis
                });
                return;
              }
              if (!STROKE_SHAPE_TAGS.has(tag)) return; // skip other non-shape tags
              el.attr('stroke', params.color || '#FFD700');
              el.attr('stroke-width', parseFloat(params.stroke_width) || 4);
              el.raise();
              // Pulse animation (1.6s total)
              el.transition('overlay-pulse')
                .duration(800)
                .attr('stroke-width', (parseFloat(params.stroke_width) || 4) + 3)
                .transition()
                .duration(800)
                .attr('stroke-width', parseFloat(params.stroke_width) || 4);
            });
          });
          break;
        }
        case 'dim_irrelevant': {
          // element_ids = elements TO dim (not a keep-set)
          const dimSet = new Set(element_ids);
          // Ghost-dim guard, whole-action skip: the implied keep-set (every
          // rendered element NOT in dimSet) must contain at least one
          // effectively-visible element. Dimming visible elements while the
          // entire keep-set is stage-invisible would darken the whole chart
          // with nothing highlighted — same skip principle as
          // focus_single_element below.
          // Axis nodes are EXCLUDED from the keep-walk (isAxisElementId,
          // single source with predicate rule 5): rule 5 is constant-true for
          // them, so counting axes here would make the skip unreachable
          // whenever the dim list omits the axes. Skip semantics: "no NON-AXIS
          // keep element is effectively visible → skip the whole action". Axes
          // that appear IN dimSet are still dimmed by the loop below
          // (unchanged).
          let keepHasVisible = false;
          svg.selectAll('[data-element-id]').each(function () {
            if (keepHasVisible) return;
            const eid = d3.select(this).attr('data-element-id');
            if (eid && !dimSet.has(eid) && !isAxisElementId(eid) && effectiveVisible(eid)) {
              keepHasVisible = true;
            }
          });
          if (!keepHasVisible) break;
          svg.selectAll('[data-element-id]').each(function () {
            const el = d3.select(this);
            const eid = el.attr('data-element-id');
            // Only dim effectively-visible nodes — stage-invisible ghosts keep
            // their renderer-set 0.15 ghost style untouched.
            if (dimSet.has(eid) && effectiveVisible(eid)) {
              el.style('opacity', params.opacity != null ? params.opacity : 0.15);
              el.classed('dimmed', true);
            }
          });
          break;
        }
        case 'focus_single_element': {
          const focusId = element_ids[0];
          if (!focusId) break;
          // Ghost-dim guard, whole-action skip: focusing a stage-invisible
          // ghost would paint a gold edge on a 0.15 phantom AND dim every
          // stage-visible element ("everything darkens, nothing lights up").
          if (!effectiveVisible(focusId)) break;
          // Highlight the target
          svg.selectAll(`[data-element-id="${focusId}"]`).each(function () {
            const el = d3.select(this);
            const tag = (this.tagName || '').toLowerCase();
            el.classed('overlay-highlight', true);
            // Text elements: bold + font-size pulse
            if (tag === 'text') {
              el.style('font-weight', '900');
              const origSize = parseFloat(el.style('font-size')) || 12;
              el.transition('overlay-pulse')
                .duration(800)
                .style('font-size', (origSize * 1.25) + 'px')
                .transition()
                .duration(800)
                .style('font-size', origSize + 'px');
              return;
            }
            // <g> containers: find child text elements (value labels) and emphasize them
            if (tag === 'g') {
              el.selectAll('text').each(function () {
                const textEl = d3.select(this);
                textEl.classed('overlay-highlight', true);
                const origSize = parseFloat(textEl.style('font-size')) || 12;
                const emphSize = Math.max(origSize * 1.4, 14);
                textEl.style('font-weight', '900')
                  .style('font-size', emphSize + 'px')
                  .style('fill', '#b45309');  // warm amber for emphasis
              });
              return;
            }
            if (!STROKE_SHAPE_TAGS.has(tag)) return; // skip other non-shape tags
            el.attr('stroke', params.color || '#FFD700');
            el.attr('stroke-width', parseFloat(params.stroke_width) || 4);
            el.raise();
            // Pulse animation (1.6s total)
            el.transition('overlay-pulse')
              .duration(800)
              .attr('stroke-width', (parseFloat(params.stroke_width) || 4) + 3)
              .transition()
              .duration(800)
              .attr('stroke-width', parseFloat(params.stroke_width) || 4);
          });
          // Dim everything else (effectively-visible nodes only — ghosts keep
          // their renderer-set 0.15 ghost style untouched)
          svg.selectAll('[data-element-id]').each(function () {
            const el = d3.select(this);
            const eid = el.attr('data-element-id');
            if (eid !== focusId && effectiveVisible(eid)) {
              el.style('opacity', params.dim_opacity != null ? params.dim_opacity : 0.15);
              el.classed('dimmed', true);
            }
          });
          break;
        }
        case 'bold_axes': {
          svg.selectAll('.axis').each(function () {
            const g = d3.select(this);
            g.selectAll('path').attr('stroke-width', params.stroke_width || 3);
            g.selectAll('text').style('font-weight', params.font_weight || '800');
            // Add background rectangles behind tick labels
            g.selectAll('.tick text').each(function () {
              const bbox = this.getBBox();
              if (bbox.width > 0 && bbox.height > 0) {
                const parent = d3.select(this.parentNode);
                parent.insert('rect', 'text')
                  .attr('class', 'overlay-bold-bg')
                  .attr('x', bbox.x - 2)
                  .attr('y', bbox.y - 1)
                  .attr('width', bbox.width + 4)
                  .attr('height', bbox.height + 2)
                  .attr('fill', params.bg_color || '#fffde7')
                  .attr('rx', 2);
              }
            });
          });
          break;
        }
        case 'show_value_tags': {
          // Bold the existing value text labels instead of adding duplicate tags
          element_ids.filter((eid) => effectiveVisible(eid)).forEach((eid) => {
            // Find associated text labels (value labels rendered by the chart)
            svg.selectAll(`[data-element-id="${eid}"]`).each(function () {
              const el = d3.select(this);
              const tag = (this.tagName || '').toLowerCase();
              if (tag === 'text') {
                // Direct text element — bold it
                el.style('font-weight', '900')
                  .style('font-size', params.font_size || `${20 * (valueLabelScaleRef.current || 1)}px`)
                  .classed('overlay-highlight', true);
              }
            });
            // Also bold the value-label text tagged for this element. value labels use a
            // SEPARATE attribute (data-value-for) — NOT data-element-id — so they stay out
            // of the [data-element-id] element namespace that dim/focus keep-walks enumerate
            // (A' fix 2026-06-15; a _value suffix on data-element-id polluted that walk).
            svg.selectAll(`text[data-value-for="${eid}"]`).each(function () {
              d3.select(this)
                .style('display', null) // reveal value labels that line/scatter/area hide by default
                .style('font-weight', '900')
                .style('font-size', params.font_size || `${20 * (valueLabelScaleRef.current || 1)}px`)
                .classed('overlay-highlight', true);
            });
          });
          break;
        }
        case 'show_overview_inset': {
          const nextViewMode = params.view_mode || 'unit_circle';
          if (isTrigFunction && nextViewMode && trigViewMode !== nextViewMode) {
            setTrigViewMode(nextViewMode);
          }
          break;
        }
        case 'set_element_visibility': {
          // D5: the student asked to SEE something the stage is hiding (or to
          // put something away). Absolute — {visible: true|false}, never a
          // toggle — so re-applying lands on the same screen. The backend
          // resolver has already refused anything the active question needs.
          if (typeof params.visible !== 'boolean' || !element_ids.length) break;
          setElementVisibilityOverrides((prev) => {
            let changed = false;
            const next = { ...prev };
            element_ids.forEach((eid) => {
              if (!eid) return;
              if (next[eid] !== params.visible) {
                next[eid] = params.visible;
                changed = true;
              }
            });
            // Returning `prev` unchanged matters: this case runs on EVERY
            // overlay re-application, and a fresh object each time would
            // re-render forever.
            return changed ? next : prev;
          });
          break;
        }
        case 'set_chart_view': {
          // Consume-once like set_stage, and for the same reason: the action is
          // idempotent as a REPLAY of one delivery, but re-running it on every
          // overlay pass would drag the student back each time they navigated
          // away themselves.
          if (consumedOneShotActionsRef.current.has(action)) break;
          consumedOneShotActionsRef.current.add(action);
          const viewStage = params.stage_index;
          if (Number.isInteger(viewStage) && stageCatalog[viewStage]) {
            setCurrentStageData(stageCatalog[viewStage]);
            setCurrentStageNum(viewStage);
            // Clear per-element overrides on the way, exactly as every manual
            // stage handler does. Without this a reveal granted on one stage
            // leaks into the target stage — reveal a late bar, jump to stage 0,
            // and it stays on screen, defeating the staged view the student
            // just asked to see. An override is only justified by the stage it
            // was requested on.
            setElementVisibilityOverrides({});
            onStageChange?.(viewStage);
            persistStageJump(viewStage);
          }
          break;
        }
        case 'focus_view_on_element': {
          // D5: bring ONE element to the student's attention. The resolver
          // guarantees exactly one id, because choosing for them would move
          // their chart somewhere they did not pick.
          //
          // SCOPE: this is the attentional half — highlight the target and damp
          // the rest. It does NOT magnify yet; chart zoom lives on a
          // React-owned wrapper outside the <svg> and driving it from here
          // needs the element's screen box, which is a separate change. A
          // student asking "make that bigger" currently gets it singled out,
          // not enlarged.
          const viewId = element_ids[0];
          if (!viewId) break;
          if (!effectiveVisible(viewId)) {
            // The student asked to look at something this stage has not revealed
            // yet. Ruled 2026-07-26: SHOW IT — jump to the stage where it appears
            // rather than doing nothing. Previously this branch just broke out,
            // so the tutor said "here you go" and the screen never changed, which
            // reads to the student as being ignored.
            //
            // Example: the chart is on Tokyo's stage and the student asks about
            // Sapporo — go to Sapporo's stage and show it there.
            // Hidden by the student's OWN earlier hide, not by the stage? Then
            // no jump can help — findStageIndexShowingElement answers "this
            // stage", which is neither a jump nor null, so the request used to
            // fall through both branches and do nothing at all. Clear that one
            // override: asking to look at something is asking to see it again
            // (Codex R5 P1).
            if (elementVisibilityOverrides[viewId] === false) {
              setElementVisibilityOverrides((prev) => {
                const next = { ...prev };
                delete next[viewId];
                return next;
              });
              break;
            }
            const targetStage = findStageIndexShowingElement(viewId);
            if (targetStage != null && targetStage !== currentStageNum && stageCatalog[targetStage]) {
              pendingOverlayApplicationRef.current = true;  // re-apply focus after the render
              setCurrentStageData(stageCatalog[targetStage]);
              setCurrentStageNum(targetStage);
              onStageChange?.(targetStage);
              persistStageJump(targetStage);
            } else if (targetStage == null) {
              // No stage ever shows it (an element outside the staged sequence).
              // Reveal it in place instead of leaving the request unanswered.
              setElementVisibilityOverrides((prev) => (
                prev[viewId] === true ? prev : { ...prev, [viewId]: true }
              ));
            }
            break;
          }
          svg.selectAll(`[data-element-id="${viewId}"]`).each(function () {
            d3.select(this).classed('overlay-highlight', true);
          });
          svg.selectAll('[data-element-id]').each(function () {
            const el = d3.select(this);
            const eid = el.attr('data-element-id');
            if (!eid || eid === viewId || isAxisElementId(eid)) return;
            if (!effectiveVisible(eid)) return;
            el.style('opacity', 0.3);
          });
          break;
        }
        case 'set_stage': {
          // One-shot stage mutation: consume per action object so overlay
          // re-application passes never re-run a jump that already executed.
          if (consumedOneShotActionsRef.current.has(action)) break;
          consumedOneShotActionsRef.current.add(action);
          const stageNum = params.stage ?? (element_ids.length > 0 ? parseInt(element_ids[0], 10) : null);
          if (stageNum != null && Number.isFinite(stageNum)) {
            if (stageCatalog[stageNum]) {
              setCurrentStageData(stageCatalog[stageNum]);
            }
            setCurrentStageNum(stageNum);
            onStageChange?.(stageNum);
            persistStageJump(stageNum); // F2: AI stage jump must persist to sessions.current_stage
          }
          break;
        }
        case 'restore_stage': {
          // One-shot stage mutation: consumed once per action object (see set_stage).
          if (consumedOneShotActionsRef.current.has(action)) break;
          consumedOneShotActionsRef.current.add(action);
          // Delegate to stage navigation: go to stage 0 or specified stage
          const targetStage = params.stage ?? 0;
          if (stageCatalog[targetStage]) {
            setCurrentStageData(stageCatalog[targetStage]);
          }
          setCurrentStageNum(targetStage);
          onStageChange?.(targetStage);
          persistStageJump(targetStage); // F2: AI restore_stage must persist too
          break;
        }
        case 'show_micro_step': {
          // Pass-local target filter: stage-invisible ghosts are dropped. When
          // ALL targets are ghosts the set is empty, so neither dim nor
          // highlight nor the step panel runs (whole-action no-op).
          const microTargetIds = element_ids.filter((eid) => effectiveVisible(eid));
          // Dim all non-target elements to focus attention
          const microStepTargetSet = new Set(microTargetIds);
          if (microStepTargetSet.size > 0) {
            svg.selectAll('[data-element-id]').each(function () {
              const el = d3.select(this);
              const eid = el.attr('data-element-id');
              if (!microStepTargetSet.has(eid) && effectiveVisible(eid)) {
                el.style('opacity', 0.2);
                el.classed('dimmed', true);
              }
            });
          }

          // Highlight target elements
          microTargetIds.forEach((eid) => {
            svg.selectAll(`[data-element-id="${eid}"]`).each(function () {
              const el = d3.select(this);
              el.classed('overlay-highlight', true);
              el.attr('stroke', params.highlight_color || '#D07B4A');
              el.attr('stroke-width', parseFloat(params.stroke_width) || 3);
              el.raise();
            });
          });

          // Render step indicator panel near the first target element
          const firstTargetId = microTargetIds[0];
          if (firstTargetId) {
            svg.selectAll(`[data-element-id="${firstTargetId}"]`).each(function () {
              if (!this.getBBox) return;
              const bbox = this.getBBox();
              const panelX = bbox.x + bbox.width / 2;
              const panelY = Math.max(bbox.y - 40, 10);
              const stepText = params.step_text || t('ig.chart.lookHere');
              const stepIndex = params.step_index;
              const totalSteps = params.total_steps;
              const parent = d3.select(this.parentNode || svgRef.current);

              // Step counter badge (if step_index and total_steps provided)
              if (stepIndex != null && totalSteps != null) {
                const badgeText = `${stepIndex}/${totalSteps}`;
                parent.append('rect')
                  .attr('class', 'overlay-tag')
                  .attr('x', panelX - 16)
                  .attr('y', panelY - 32)
                  .attr('width', 32)
                  .attr('height', 20)
                  .attr('fill', '#3D5A6E')
                  .attr('rx', 10)
                  .attr('opacity', 0.95);
                parent.append('text')
                  .attr('class', 'overlay-tag')
                  .attr('x', panelX)
                  .attr('y', panelY - 18)
                  .attr('text-anchor', 'middle')
                  .style('font-size', '9px')
                  .style('font-weight', '700')
                  .style('fill', '#ffffff')
                  .text(badgeText);
              }

              // Step text panel
              const textLen = stepText.length;
              const panelWidth = Math.max(textLen * 14 + 24, 80);
              parent.append('rect')
                .attr('class', 'overlay-tag')
                .attr('x', panelX - panelWidth / 2)
                .attr('y', panelY - 10)
                .attr('width', panelWidth)
                .attr('height', 26)
                .attr('fill', '#1e293b')
                .attr('rx', 6)
                .attr('opacity', 0.92);
              parent.append('text')
                .attr('class', 'overlay-tag')
                .attr('x', panelX)
                .attr('y', panelY + 8)
                .attr('text-anchor', 'middle')
                .style('font-size', params.font_size || '9px')
                .style('font-weight', '600')
                .style('fill', '#ffffff')
                .text(stepText);
            });
          }
          break;
        }
        case 'annotate_element': {
          // Minimal stub: render text annotation near the first target element
          // (skipped when the target is a stage-invisible ghost)
          const annotateId = element_ids[0];
          if (annotateId && effectiveVisible(annotateId)) {
            svg.selectAll(`[data-element-id="${annotateId}"]`).each(function () {
              const bbox = this.getBBox();
              const annotX = bbox.x + bbox.width / 2;
              const annotY = bbox.y - 10;
              const parent = d3.select(this.parentNode || svgRef.current);
              parent.append('text')
                .attr('class', 'overlay-tag')
                .attr('x', annotX)
                .attr('y', annotY)
                .attr('text-anchor', 'middle')
                .style('font-size', params.font_size || '9px')
                .style('font-weight', '600')
                .style('fill', params.color || '#3D5A6E')
                .text(String(params.text || params.label || ''));
            });
          }
          break;
        }
        case 'attention_guide': {
          // Handled by separate AttentionGuide.js component; no inline SVG rendering needed
          console.warn('[applyOverlay] attention_guide received — delegating to AttentionGuide component', { element_ids, params });
          break;
        }
        default:
          break;
      }
    });

    // Lock overlays for OVERLAY_MIN_DISPLAY_MS so subsequent render cycles
    // do not immediately clear the visual effects the student needs to see.
    //
    // focus_view_on_element belongs here for the same reason focus_single_element
    // does: both paint straight onto the live D3 selection (a class plus an inline
    // opacity), so a redraw between the apply and the student's eyes destroys them.
    // Its two D5 siblings deliberately stay OUT — set_element_visibility survives
    // through React state (elementVisibilityOverrides) and set_chart_view through
    // the stage cursor, and neither is something a repaint can erase.
    //
    // HONESTY NOTE (2026-07-26): this addition is currently INERT and no test can
    // prove it. overlayLockedUntilRef is read in exactly one place — restoreOverlay's
    // `!force` branch — and every one of the five restoreOverlay call sites passes
    // force=true, so the lock never actually holds anything back today. It is listed
    // here so the classification is right if a non-forced clear is ever introduced;
    // it is NOT the fix that makes D5 work (that is the sanitize allowlist).
    const hasVisualOverlay = actions.some(a =>
      ['highlight_elements', 'dim_irrelevant', 'focus_single_element', 'bold_axes',
       'show_value_tags', 'show_micro_step', 'annotate_element',
       'focus_view_on_element'].includes(a?.type)
    );
    if (hasVisualOverlay) {
      overlayLockedUntilRef.current = Date.now() + OVERLAY_MIN_DISPLAY_MS;
    }
  }, [graphData, isTrigFunction, onStageChange, persistStageJump, stageCatalog, trigViewMode]);

  // ── resolveStageIndexForElement: キーワードクリック時に最終ステージへジャンプ ──
  // The FIRST stage that actually reveals this element.
  //
  // Distinct from resolveStageIndexForElement below, which always answers "the
  // last stage" — fine for a keyword click that wants the complete picture, but
  // wrong for "let me see Sapporo", where the student should land on the stage
  // that introduces it rather than being thrown to the end of the sequence.
  // Returns null when no stage lists it, which the caller treats as "reveal it
  // where we are" rather than as a failure.
  const findStageIndexShowingElement = useCallback((elementId) => {
    if (!elementId || !Array.isArray(graphData?.stages)) return null;
    for (let i = 0; i < graphData.stages.length; i += 1) {
      const stage = graphData.stages[i];
      if (stage?.show_all_with_original_style === true) return i;
      const visible = stage?.visible_elements;
      if (Array.isArray(visible) && visible.includes(elementId)) return i;
    }
    return null;
  }, [graphData?.stages]);

  const resolveStageIndexForElement = useCallback((elementId) => {
    if (!elementId || !Array.isArray(graphData?.stages) || graphData.stages.length === 0) {
      return null;
    }
    return graphData.stages.length - 1;
  }, [graphData?.stages]);

  useEffect(() => {
    if (!focusElementId?.startsWith('__trigger_last_stage__')) return;
    const fallbackStageIndex = resolveStageIndexForElement(focusElementId);
    if (fallbackStageIndex == null || !stageCatalog[fallbackStageIndex]) {
      return;
    }
    // Mark that overlay re-application is needed after the rendering cycle completes
    pendingOverlayApplicationRef.current = true;
    if (fallbackStageIndex === currentStageNum) {
      // Already at target stage — force overlay re-application
      setElementVisibilityOverrides({});
      setOverlayRenderTick(t => t + 1);
      return;
    }
    setCurrentStageData(stageCatalog[fallbackStageIndex]);
    setCurrentStageNum(fallbackStageIndex);
    setElementVisibilityOverrides({});
    onStageChange?.(fallbackStageIndex);
    persistStageJump(fallbackStageIndex); // F2: focus-triggered jump must persist too
  }, [currentStageNum, focusElementId, onStageChange, persistStageJump, resolveStageIndexForElement, stageCatalog]);

  // When new uiActions arrive from AI, reset the manual-change flag so highlights show.
  useEffect(() => {
    if (uiActions && uiActions.length > 0) {
      stageManuallyChangedRef.current = false;
    }
  }, [uiActions]);

  // Force overlay re-application after uiActions change (with delay for D3 rendering).
  // Single 400ms timer — the overlay useEffect's 500ms debounce provides a second safety net.
  useEffect(() => {
    if (!uiActions || uiActions.length === 0) return;
    const timer = setTimeout(() => {
      setOverlayRenderTick(t => t + 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [uiActions]);

  const voiceOutputEnabled = settings?.voiceOutputEnabled !== false;
  const preferredVoiceType = settings?.voiceType || 'female_neural';
  const preferredSpeechRate = settings?.speechRate || 1.0;
  const preferredSpeechVolume = settings?.speechVolume || 1.0;
  const explanationAudioCacheRef = useRef(new Map());
  const generatedAudioUrlsRef = useRef(new Set());
  const legendMetricsRef = useRef(null);
  const ttsSlowTimerRef = useRef(null);
  const playedStageAnimationKeysRef = useRef(new Set());
  const overlayOuterRafRef = useRef(null);
  const overlayInnerRafRef = useRef(null);
  // Overlay lock: timestamp until which overlays should not be cleared
  const overlayLockedUntilRef = useRef(0);
  // Pending overlay flag: set when focusElementId triggers a stage change,
  // so the overlay useEffect re-applies after the rendering useEffect completes
  const pendingOverlayApplicationRef = useRef(false);
  const renderGenerationRef = useRef(0);
  // Flag set by manual stage navigation (next/prev/jump) to suppress stale
  // uiActions highlights. Reset when new uiActions arrive from AI response.
  const stageManuallyChangedRef = useRef(false);
  // One-shot stage mutations (set_stage / restore_stage) are consumed ONCE per
  // action OBJECT (identity by reference, WeakSet). Overlay re-application
  // passes (overlayRenderTick bumps, stage changes, slider drags) must NOT
  // re-execute a stage jump that already ran — a stale set_stage replayed
  // after the user manually navigated would hijack their navigation.
  // Contract: producers keep action objects stable across re-renders; a NEW
  // object means a legitimate re-emit and executes again (voice same-key
  // replacement builds a new object per tool call; PIV builds new objects per
  // applyLinkageActions call; chat delivers a fresh array per response).
  const consumedOneShotActionsRef = useRef(new WeakSet());

  const activeStageData = useMemo(() => {
    if (currentStageData) return currentStageData;
    if (Array.isArray(graphData?.stages) && graphData.stages[currentStageNum]) {
      return graphData.stages[currentStageNum];
    }
    return graphData?.current_stage_data || graphData?.graph_data || graphData || null;
  }, [currentStageData, graphData, currentStageNum]);

  const baselineVisibleSet = useMemo(
    () => new Set(activeStageData?.visible_elements || []),
    [activeStageData]
  );

  const colorPreferenceScope = useMemo(
    () => normalizeColorPreferenceScope(
      settings?.colorPreferenceScope ||
      graphData?.color_preference_scope ||
      studentProfile?.preferred_color_scope ||
      studentProfile?.color_preference_scope ||
      graphData?.preference_scope
    ),
    [
      graphData?.color_preference_scope,
      graphData?.preference_scope,
      settings?.colorPreferenceScope,
      studentProfile?.color_preference_scope,
      studentProfile?.preferred_color_scope
    ]
  );

  const colorPreferenceStorageKey = useMemo(
    () => buildColorPreferenceStorageKey(
      graphData,
      studentProfile,
      colorPreferenceScope,
      currentStageData,
      currentStageNum
    ),
    [
      colorPreferenceScope,
      currentStageData?.stage_id,
      currentStageData?.id,
      currentStageNum,
      graphData?.graph_id,
      graphData?.id,
      graphData?.graph_key,
      graphData?.title,
      studentProfile?.student_id
    ]
  );

  const stageDataForDisplay = useMemo(() => {
    if (!activeStageData) return null;
    const overrideEntries = Object.entries(elementVisibilityOverrides || {});
    if (!overrideEntries.length) {
      return activeStageData;
    }
    const mergedVisible = new Set(activeStageData.visible_elements || []);
    overrideEntries.forEach(([elementId, shouldShow]) => {
      if (!elementId) return;
      if (shouldShow) {
        mergedVisible.add(elementId);
      } else {
        mergedVisible.delete(elementId);
      }
    });
    // An explicit HIDE must win over the final "show everything" stage.
    //
    // Ruled 2026-07-26. Stages reveal a FIXED set of elements, so the stage
    // system alone cannot serve "show me only Osaka's January bar" when that
    // stage carries both months. Per-element visibility is what makes that
    // possible, which means the student's explicit intent has to outrank the
    // stage's own idea of what should be on screen — including the last stage,
    // where hiding the rest to isolate one bar is exactly the point.
    //
    // Every renderer decides element visibility with `isShowAllStage || x.visible`
    // (27 sites across 17 renderers), so patching them individually would be both
    // large and easy to get half-right. Instead drop the flag ONCE here and
    // re-assert the five display properties it implies, so the chrome (axes,
    // labels, ticks, legend, data layer) is untouched and only ELEMENT visibility
    // falls back to `visible_elements` — which already reflects the override.
    // A pure reveal does not trigger this: show-all keeps its meaning unless the
    // student actually put something away.
    // "Nothing is visible" and "no visibility constraints" are OPPOSITE states
    // that an empty array cannot tell apart. Three renderers read
    // `visible_elements.length > 0` as "has constraints", so a student who hides
    // every element of a small venn/tree (no axis elements to keep the array
    // non-empty; the resolver allows up to 6 ids at once) would have show-all
    // re-enabled and see EVERYTHING — the exact inverse of the request
    // (Codex R5 P2). A sentinel keeps the array non-empty while matching no real
    // element id or label, so the explicit empty set survives.
    if (mergedVisible.size === 0 && (activeStageData.visible_elements || []).length > 0) {
      mergedVisible.add(STUDENT_HID_EVERYTHING);
    }

    const hasExplicitHide = overrideEntries.some(([, shouldShow]) => shouldShow === false);
    if (hasExplicitHide && activeStageData.show_all_with_original_style === true) {
      return {
        ...activeStageData,
        show_all_with_original_style: false,
        show_axis_labels: true,   // keeps showAxisLabels + showTickLabels
        show_legend: true,        // keeps showLegend
        visible_elements: Array.from(mergedVisible)
      };
    }
    return {
      ...activeStageData,
      visible_elements: Array.from(mergedVisible)
    };
  }, [activeStageData, elementVisibilityOverrides]);

  // Sync applyOverlay's ghost-dim guard context. MUST stay declared before the
  // overlay useEffect (React runs same-commit effects in declaration order);
  // the overlay apply additionally runs via RAF/debounce after the commit, so
  // the predicate never reads a stale stage.
  useEffect(() => {
    overlayVisibilityContextRef.current = {
      visibleElements: stageDataForDisplay?.visible_elements || null,
      showAllWithOriginalStyle: stageDataForDisplay?.show_all_with_original_style === true,
      highlightedElements: Array.isArray(highlightedElements) ? highlightedElements : [],
    };
  }, [stageDataForDisplay, highlightedElements]);

  // ── Part 2 PR1: report screen identity so a reply can be matched to it ───
  // NOT a visibility receipt — see utils/renderStateSnapshot.js for why a
  // generic helper cannot speak for 17 renderers. This reports identity and
  // revisions, which is what the stale/retry race control needs.
  //
  // Both counters are TRUE monotonic counters bumped on change. An earlier
  // version derived a value from the override count plus a trig boolean, under
  // which `unit_circle` and `dual_pane` both scored 1 and any two override maps
  // of the same size collided — i.e. the screen changed but compared equal, so
  // a stale reply was applied to a different picture.
  const interactionRevisionRef = useRef(0);
  const [interactionRevision, setInteractionRevision] = useState(0);
  // Graph identity participates: the Dashboard can swap currentSession/graphData
  // mid-flight, and if the two graphs happen to share a stage index a counter
  // watching only the stage would not advance — so a reply for the PREVIOUS
  // graph would compare equal and be applied to the new one.
  // Zoom and pan participate too. They visibly transform the chart — a student
  // who zooms into one bar while the reply is generating is looking at a
  // different picture — but they never reach the server, so nothing else can
  // detect them. Leaving them out let the grounding gate accept a reply as if
  // the screen had not moved, which is exactly what the gate exists to prevent.
  const interactionFingerprint = useMemo(
    () => JSON.stringify({
      o: Object.entries(elementVisibilityOverrides || {}).sort(),
      t: trigViewMode || null,
      g: graphData?.graph_id || graphData?.graph_data?.graph_id || null,
      s: sessionId ?? null,
      z: [zoomState?.scale ?? 1, zoomState?.tx ?? 0, zoomState?.ty ?? 0],
    }),
    [elementVisibilityOverrides, trigViewMode, graphData, sessionId, zoomState]
  );
  const lastInteractionFingerprintRef = useRef(interactionFingerprint);
  useEffect(() => {
    if (lastInteractionFingerprintRef.current === interactionFingerprint) return;
    lastInteractionFingerprintRef.current = interactionFingerprint;
    interactionRevisionRef.current += 1;
    setInteractionRevision(interactionRevisionRef.current);
  }, [interactionFingerprint]);

  const stageRevisionRef = useRef(0);
  const [stageRevision, setStageRevision] = useState(0);
  const lastStageRef = useRef(currentStageNum);
  useEffect(() => {
    if (lastStageRef.current === currentStageNum) return;
    lastStageRef.current = currentStageNum;
    stageRevisionRef.current += 1;
    setStageRevision(stageRevisionRef.current);
  }, [currentStageNum]);

  useEffect(() => {
    if (typeof onRenderStateChange !== 'function') return;
    onRenderStateChange(
      buildRenderStateSnapshot({
        stageIndex: currentStageNum,
        stageRevision,
        interactionRevision,
        trigViewMode,
        graphId: graphData?.graph_id || graphData?.graph_data?.graph_id || null,
        sessionId: sessionId ?? null,
        // funcDisplayMode is omitted deliberately: it is a pure function of the
        // stage data, so any change to it implies a stage change, which
        // stage_revision already captures. trigViewMode is different — it is a
        // user toggle that changes the picture WITHIN a stage.
      })
    );
  }, [
    onRenderStateChange,
    currentStageNum,
    stageRevision,
    interactionRevision,
    trigViewMode,
    graphData,
    sessionId,
  ]);

  const graphElements = useMemo(() => {
    const dataset = graphData?.graph_data || graphData || {};
    return Array.isArray(dataset.elements) ? dataset.elements : [];
  }, [graphData]);

  const presetColorMappings = useMemo(() => {
    const presetName = settings?.colorAccessibilityPreset || 'auto';
    if (presetName === 'auto') {
      return {
        presetName,
        palette: getCUDPalette('auto'),
        elementMap: new Map(),
        sourceColorMap: new Map()
      };
    }

    const palette = getCUDPalette(presetName);
    const elementMap = new Map();
    const sourceColorMap = new Map();
    const fallbackGroupMap = new Map();
    let paletteIndex = 0;

    graphElements.forEach((element, index) => {
      const originalColorKey = normalizeColorKey(element?.color || element?.baseColor);
      let assignedColor = null;

      if (originalColorKey) {
        if (!sourceColorMap.has(originalColorKey)) {
          sourceColorMap.set(originalColorKey, palette[paletteIndex % palette.length]);
          paletteIndex += 1;
        }
        assignedColor = sourceColorMap.get(originalColorKey);
      } else {
        const fallbackGroupKey = `${element?.type || 'element'}:${element?.label || element?.id || index}`;
        if (!fallbackGroupMap.has(fallbackGroupKey)) {
          fallbackGroupMap.set(fallbackGroupKey, palette[paletteIndex % palette.length]);
          paletteIndex += 1;
        }
        assignedColor = fallbackGroupMap.get(fallbackGroupKey);
      }

      if (element?.id && assignedColor) {
        elementMap.set(element.id, assignedColor);
      }
    });

    return {
      presetName,
      palette,
      elementMap,
      sourceColorMap
    };
  }, [graphElements, settings?.colorAccessibilityPreset]);

  const hasElementOverrides = Object.keys(elementVisibilityOverrides || {}).length > 0;
  const visibleElementCount = stageDataForDisplay?.visible_elements?.length || 0;

  const difficultyType = useMemo(
    () => getDifficultyTypeForStage(stageDataForDisplay, graphData, studentProfile),
    [stageDataForDisplay, graphData, studentProfile]
  );
  const difficultyInfo = DIFFICULTY_TYPE_INFO[difficultyType] || DIFFICULTY_TYPE_INFO.default;

  const sidebarElements = useMemo(() => {
    if (!stageDataForDisplay) return [];
    const visibleSet = new Set(stageDataForDisplay.visible_elements || []);
    return graphElements
      .filter((element) => element?.id && !(element.type || '').includes('axis'))
      .map((element, index) => ({
        id: element.id,
        label: element.label || element.name || t('ig.elementFallback', { n: index + 1 }),
        type: element.type || 'generic',
        color: element.color || element.baseColor || '#94a3b8',
        isVisible: visibleSet.has(element.id),
        baselineVisible: baselineVisibleSet.has(element.id),
        groupKey: matchElementTypeToGroupKey(element.type)
      }));
  }, [baselineVisibleSet, graphElements, stageDataForDisplay, t]);

  const groupedSidebarElements = useMemo(() => {
    if (!sidebarElements.length) return [];
    const baseGroups = ELEMENT_GROUP_DEFINITIONS.map((definition) => ({
      key: definition.key,
      label: definition.label,
      baseDescription: definition.description,
      accent: definition.accent,
      elements: []
    }));
    sidebarElements.forEach((element) => {
      const groupKey = element.groupKey || 'other';
      const targetGroup = baseGroups.find((group) => group.key === groupKey) || baseGroups[baseGroups.length - 1];
      targetGroup.elements.push(element);
    });
    const difficultyHints = DIFFICULTY_GROUP_HINTS[difficultyType] || {};
    return baseGroups
      .filter((group) => group.elements.length > 0 && group.key !== 'other')
      .map((group) => ({
        key: group.key,
        label: t('ig.elementGroup.' + group.key, { defaultValue: group.label }),
        accent: group.accent,
        elements: group.elements,
        description: [difficultyHints[group.key], group.baseDescription || DEFAULT_GROUP_HINTS[group.key]].filter(Boolean).join(' ') ||
          DEFAULT_GROUP_HINTS[group.key] ||
        group.baseDescription
      }));
  }, [difficultyType, sidebarElements, t]);

  const stageSummaryText = useMemo(() => {
    if (activeStageData?.stage_summary) return activeStageData.stage_summary;
    if (activeStageData?.description) return activeStageData.description;
    if (stageExplanation) {
      const [firstLine] = stageExplanation.split('\n');
      return firstLine;
    }
    return '現在のステップに集中しましょう。';
  }, [activeStageData, stageExplanation]);

  // A1 (Tier-1 a11y / Gate B): buildChartSummary (utils/chartA11y.js) is
  // i18n-agnostic and calls its own injected `t` with generic
  // `a11y.chartSummary.*` template keys. This app's actual locale files
  // (added by the T1 i18n task) expose the equivalent content under
  // `ig.a11y.chartSummary` (different placeholder names) plus the existing
  // `ig.chart.xAxis` / `ig.chart.yAxis` fallback labels — so this adapter
  // remaps those specific lookups onto the real keys and passes everything
  // else (the existing `graphType.*` name map) straight through to `t`.
  const chartA11yTranslate = useCallback((key, params) => {
    switch (key) {
      case 'a11y.chartSummary.full':
        return t('ig.a11y.chartSummary', {
          type: params.graphType,
          xLabel: params.xAxisLabel,
          yLabel: params.yAxisLabel,
          seriesCount: params.elementCount,
          stageNum: params.stageNumber,
          totalStages: params.totalStages,
          stageTitle: params.stageTitle
        });
      case 'a11y.chartSummary.xAxisFallback':
        return t('ig.chart.xAxis');
      case 'a11y.chartSummary.yAxisFallback':
        return t('ig.chart.yAxis');
      case 'a11y.chartSummary.stageTitleFallback':
        // Defensive only: stageSummaryText (below) already guarantees a
        // non-empty stage description is fed into buildChartSummary, so this
        // branch is not expected to fire in practice.
        return t('ig.a11y.chartRoleLabel');
      default:
        return t(key, params);
    }
  }, [t]);

  // Guarantee resolveStageTitle (inside buildChartSummary) always finds a
  // real value: reuse the already-computed stageSummaryText fallback chain
  // instead of letting it fall through to the defensive key above.
  const chartSummaryStageData = useMemo(() => {
    if (stageDataForDisplay?.stage_summary || stageDataForDisplay?.description || stageDataForDisplay?.title) {
      return stageDataForDisplay;
    }
    return { ...(stageDataForDisplay || {}), description: stageSummaryText };
  }, [stageDataForDisplay, stageSummaryText]);

  const chartSummaryTotalStages = graphData?.total_stages || stageCatalog.length;

  const chartSummaryText = useMemo(() => buildChartSummary({
    graphData,
    stageData: chartSummaryStageData,
    currentStageNum,
    totalStages: chartSummaryTotalStages,
    t: chartA11yTranslate
  }), [graphData, chartSummaryStageData, currentStageNum, chartSummaryTotalStages, chartA11yTranslate]);

  // A2 (Tier-1 a11y / Gate B): short per-stage announcement text — a
  // DIFFERENT string from chartSummaryText above (that one is the long
  // chart description used for the <svg> aria-label/title/desc). This one
  // uses the dedicated `ig.a11y.stageAnnounce` template (T1 i18n) and also
  // doubles as the identity key fed into shouldAnnounceStage below: it only
  // changes when currentStageNum / chartSummaryTotalStages / the resolved
  // stage title actually change (a real stage-summary level transition),
  // never on per-highlight/dim/focus/overlay micro-actions — none of those
  // touch this computation.
  const stageAnnounceTitle = chartSummaryStageData?.stage_summary ||
    chartSummaryStageData?.description ||
    chartSummaryStageData?.title ||
    stageSummaryText;

  const stageAnnounceMessage = useMemo(() => t('ig.a11y.stageAnnounce', {
    stageNum: currentStageNum + 1,
    totalStages: chartSummaryTotalStages,
    stageTitle: stageAnnounceTitle
  }), [t, currentStageNum, chartSummaryTotalStages, stageAnnounceTitle]);

  // A2: fires the throttled aria-live announcement. Entirely gated on the
  // user's opt-in toggle (default OFF, 図の調整 panel):
  //  - toggle OFF: reset all bookkeeping and force the live region back to
  //    '' — stage changes must leave it empty while the feature is off.
  //  - toggle just turned ON (or mounted already-ON): silently record the
  //    CURRENTLY displayed stage as the baseline, without announcing it —
  //    only a genuine transition that happens AFTER this point should speak.
  //  - otherwise: announce once per real stage transition, throttled to
  //    ARIA_LIVE_ANNOUNCE_MIN_INTERVAL_MS via the pure, i18n-agnostic
  //    shouldAnnounceStage helper (utils/chartA11y.js — also unit-tested
  //    there).
  useEffect(() => {
    if (!ariaLiveEnabled) {
      ariaLiveWasEnabledRef.current = false;
      lastAnnouncedStageKeyRef.current = null;
      lastAnnounceTsRef.current = null;
      setStageAnnounceText('');
      return;
    }
    if (!ariaLiveWasEnabledRef.current) {
      ariaLiveWasEnabledRef.current = true;
      lastAnnouncedStageKeyRef.current = stageAnnounceMessage;
      lastAnnounceTsRef.current = null;
      return;
    }
    const now = Date.now();
    if (shouldAnnounceStage(
      lastAnnouncedStageKeyRef.current,
      stageAnnounceMessage,
      lastAnnounceTsRef.current,
      now,
      ARIA_LIVE_ANNOUNCE_MIN_INTERVAL_MS
    )) {
      lastAnnouncedStageKeyRef.current = stageAnnounceMessage;
      lastAnnounceTsRef.current = now;
      setStageAnnounceText(stageAnnounceMessage);
    }
  }, [ariaLiveEnabled, stageAnnounceMessage]);

  const resolvedLabelColor = useMemo(
    () => (highlightOptions.labels ? highlightAccentColor : (colorOverrides.label || DEFAULT_LABEL_COLOR)),
    [colorOverrides.label, highlightOptions.labels, highlightAccentColor]
  );
  const resolvedLegendTextColor = useMemo(
    () => (highlightOptions.legend ? highlightAccentColor : (colorOverrides.legendText || DEFAULT_LEGEND_TEXT_COLOR)),
    [colorOverrides.legendText, highlightOptions.legend, highlightAccentColor]
  );
  const legendBackgroundColor = useMemo(
    () => colorOverrides.legendBackground || DEFAULT_LEGEND_BACKGROUND,
    [colorOverrides.legendBackground]
  );
  const explanationStyles = useMemo(() => ({
    color: colorOverrides.explanation || DEFAULT_EXPLANATION_COLOR,
    backgroundColor: highlightOptions.explanation && explanationHighlights.length > 0
      ? toRgba(highlightAccentColor, 0.16)
      : 'transparent',
    boxShadow: highlightOptions.explanation && explanationHighlights.length > 0
      ? `0 0 0 2px ${toRgba(highlightAccentColor, 0.35)}`
      : 'none'
  }), [colorOverrides.explanation, highlightOptions.explanation, highlightAccentColor, explanationHighlights.length]);

  const getElementColor = useCallback((elementId, fallbackColor) => {
    if (!elementId) return fallbackColor;

    const manualOverride = colorOverrides.elements?.[elementId];
    if (manualOverride) {
      return manualOverride;
    }

    if (presetColorMappings.presetName !== 'auto') {
      const mappedElementColor = presetColorMappings.elementMap.get(elementId);
      if (mappedElementColor) {
        return mappedElementColor;
      }

      const fallbackColorKey = normalizeColorKey(fallbackColor);
      if (fallbackColorKey && presetColorMappings.sourceColorMap.has(fallbackColorKey)) {
        return presetColorMappings.sourceColorMap.get(fallbackColorKey);
      }

      return presetColorMappings.palette[
        getConsistentIndexFromId(elementId) % presetColorMappings.palette.length
      ];
    }

    return fallbackColor;
  }, [colorOverrides.elements, presetColorMappings]);

  // --- Chart magnification handlers (Tier-1 a11y) ----------------------------
  // Live container box read from the DOM so pan clamping matches the actual
  // rendered chart size at action time (not a stale layout guess).
  const readZoomDims = useCallback(() => {
    const el = graphContainerRef.current;
    if (el && el.clientWidth > 0) return { width: el.clientWidth, height: el.clientHeight };
    return { width: containerWidth, height: containerWidth };
  }, [containerWidth]);
  const handleZoomIn = useCallback(() => setZoomState((s) => chartZoom.zoomIn(s, readZoomDims())), [readZoomDims]);
  const handleZoomOut = useCallback(() => setZoomState((s) => chartZoom.zoomOut(s, readZoomDims())), [readZoomDims]);
  const handleZoomReset = useCallback(() => setZoomState(chartZoom.reset()), []);
  const handleZoomPan = useCallback(
    (dx, dy) => setZoomState((s) => chartZoom.pan(s, dx, dy, readZoomDims())),
    [readZoomDims]
  );
  // Keyboard: +/- zoom and 0 reset work whenever the chart region has focus;
  // arrows pan but ONLY when the wrapper itself is focused (target === wrapper),
  // so focused chart elements keep their roving-arrow navigation.
  const handleZoomKeyDown = useCallback(
    (e) => {
      const action = chartZoom.resolveZoomKey(e.key);
      if (!action) return;
      const isPan = action.indexOf('pan') === 0;
      // Arrows pan ONLY while zoomed and only when the wrapper itself is focused.
      // At fit, do NOT claim/preventDefault Arrow keys, so stage navigation still
      // works even if browser focus lingers on the viewport after reset (Codex P2).
      if (isPan && (e.target !== e.currentTarget || !chartZoom.isZoomed(zoomState))) return;
      e.preventDefault();
      switch (action) {
        case 'in': handleZoomIn(); break;
        case 'out': handleZoomOut(); break;
        case 'reset': handleZoomReset(); break;
        // Arrow = direction the view moves (reveal that edge) → shift content the opposite way.
        case 'panLeft': handleZoomPan(chartZoom.PAN_STEP, 0); break;
        case 'panRight': handleZoomPan(-chartZoom.PAN_STEP, 0); break;
        case 'panUp': handleZoomPan(0, chartZoom.PAN_STEP); break;
        case 'panDown': handleZoomPan(0, -chartZoom.PAN_STEP); break;
        default: break;
      }
    },
    [handleZoomIn, handleZoomOut, handleZoomReset, handleZoomPan, zoomState]
  );
  // Pointer-drag pan — active ONLY once magnified, so at fit (normal use) there
  // is zero new pointer behavior and existing element interactions are intact.
  // A small movement threshold lets taps/clicks still reach chart elements.
  const handleZoomPointerDown = useCallback((e) => {
    if (!chartZoom.isZoomed(zoomState)) return;
    // Ignore drags that begin on an existing draggable chart element (legend
    // move/resize, pie/Venn labels — all marked touch-action:none) so grabbing
    // one of those does not ALSO pan the whole chart (Codex P2).
    if (chartZoom.pointerStartsOnDraggable(e.target, e.currentTarget)) return;
    zoomPointerRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
  }, [zoomState]);
  const handleZoomPointerMove = useCallback(
    (e) => {
      const p = zoomPointerRef.current;
      if (!p || p.id !== e.pointerId) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!p.moved && Math.hypot(dx, dy) < 6) return; // below threshold: let taps/clicks through
      if (!p.moved) {
        p.moved = true;
        setIsZoomDragging(true);
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* capture is best-effort */ }
      }
      p.x = e.clientX;
      p.y = e.clientY;
      handleZoomPan(dx, dy); // grab semantics: content follows the pointer
    },
    [handleZoomPan]
  );
  const handleZoomPointerUp = useCallback((e) => {
    const p = zoomPointerRef.current;
    if (p && p.id === e.pointerId) {
      zoomPointerRef.current = null;
      setIsZoomDragging(false);
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
  }, []);

  // --- High-contrast chart toggle (Tier-1 a11y) ------------------------------

  const legendVisualContext = useMemo(() => ({
    textColor: resolvedLegendTextColor,
    backgroundColor: legendBackgroundColor,
    highlightShadow: highlightOptions.legend ? `0 0 6px ${toRgba(highlightAccentColor, 0.35)}` : 'none'
  }), [resolvedLegendTextColor, legendBackgroundColor, highlightOptions.legend, highlightAccentColor]);

  const renderExplanationWithHighlight = useCallback((text) => {
    if (!text) return null;
    // The stage explanation is AI-generated. Annotate the WHOLE string ONCE and project its segments onto the
    // bold (**…**) + highlight ranges via the shared projectDecoratedText — never re-tokenize a fragment, or a
    // boundary isolating a context-sensitive kanji (箱の**中**) would mis-read (Codex R16 #2). Furigana ruby
    // follows the global toggle (furiganaEnabled); highlights are default-OFF curated hits.
    return projectDecoratedText(text, explanationHighlights.filter(Boolean), furiganaEnabled).map((g, i) => {
      let node = furiganaEnabled ? renderFuriganaSegments(g.segs, `exp${i}`) : g.segs.map((s) => s.text).join('');
      if (g.meta && g.meta.hl) {
        node = (
          <span
            key={`exp-hl-${i}`}
            style={{
              backgroundColor: toRgba(highlightAccentColor, 0.2),
              boxShadow: `0 0 0 2px ${toRgba(highlightAccentColor, 0.35)}`,
              borderRadius: '4px',
              padding: '1px 2px'
            }}
          >{node}</span>
        );
      }
      if (g.meta && g.meta.bold) node = <strong key={`exp-b-${i}`} className="font-bold">{node}</strong>;
      return <React.Fragment key={`exp-${i}`}>{node}</React.Fragment>;
    });
  }, [explanationHighlights, highlightAccentColor, furiganaEnabled]);

  const clearTtsSlowTimer = useCallback(() => {
    if (ttsSlowTimerRef.current) {
      clearTimeout(ttsSlowTimerRef.current);
      ttsSlowTimerRef.current = null;
    }
  }, []);

  const AUDIO_CACHE_PREFIX = 'EXPLANATION_AUDIO_CACHE:';

  const storeGeneratedExplanationAudio = useCallback((cacheKey, audioUrl, audioBase64 = null) => {
    if (!cacheKey || !audioUrl) return;
    explanationAudioCacheRef.current.set(cacheKey, audioUrl);
    generatedAudioUrlsRef.current.add(audioUrl);
    if (typeof window !== 'undefined' && audioBase64) {
      try {
        window.localStorage.setItem(`${AUDIO_CACHE_PREFIX}${cacheKey}`, audioBase64);
      } catch (error) {
        logClientWarn('音声キャッシュの保存に失敗しました:', error);
      }
    }
    while (explanationAudioCacheRef.current.size > MAX_EXPLANATION_AUDIO_CACHE_ENTRIES) {
      const oldestKey = explanationAudioCacheRef.current.keys().next().value;
      if (!oldestKey) break;
      const oldestUrl = explanationAudioCacheRef.current.get(oldestKey);
      explanationAudioCacheRef.current.delete(oldestKey);
      if (oldestUrl) {
        generatedAudioUrlsRef.current.delete(oldestUrl);
        try {
          URL.revokeObjectURL(oldestUrl);
        } catch (error) {
          logClientWarn('音声キャッシュの解放に失敗しました:', error);
        }
      }
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(`${AUDIO_CACHE_PREFIX}${oldestKey}`);
      }
    }
  }, []);

  const axisFontWeight = useMemo(
    () => (highlightOptions.labels ? '800' : 'bold'),
    [highlightOptions.labels]
  );

  const colorPreferenceScopeLabel = useMemo(() => {
    if (colorPreferenceScope === 'user') return 'ユーザー全体';
    if (colorPreferenceScope === 'stage') return 'ステージ別';
    return 'グラフ別';
  }, [colorPreferenceScope]);

  const networkStatusLabel = useMemo(() => {
    if (!networkInfo) return t('ig.network.unknown');
    const segments = [];
    if (networkInfo.effectiveType) segments.push(networkInfo.effectiveType.toUpperCase());
    if (typeof networkInfo.downlink === 'number') segments.push(`${networkInfo.downlink.toFixed(1)}Mbps`);
    if (networkInfo.saveData) segments.push(t('ig.network.dataSaver'));
    return segments.length ? t('ig.network.connected', { segments: segments.join(' / ') }) : t('ig.network.unknown');
  }, [networkInfo, t]);

  const isNetworkLikelySlow = useMemo(() => {
    if (!networkInfo) return false;
    if (networkInfo.effectiveType && ['slow-2g', '2g'].includes(networkInfo.effectiveType)) {
      return true;
    }
    if (typeof networkInfo.downlink === 'number' && networkInfo.downlink < 1) {
      return true;
    }
    return false;
  }, [networkInfo]);

  const hasCustomColorPreferences = useMemo(() => {
    const { label, legendText, legendBackground, explanation, elements } = colorOverrides || {};
    const hasElementOverrides = elements
      ? Object.values(elements).some((value) => Boolean(value))
      : false;
    return Boolean(
      label ||
      legendText ||
      legendBackground ||
      explanation ||
      hasElementOverrides ||
      highlightOptions.labels ||
      highlightOptions.legend ||
      highlightOptions.explanation
    );
  }, [colorOverrides, highlightOptions]);

  const stageTransitionHint = useMemo(() => {
    const totalStages = Array.isArray(stageCatalog) ? stageCatalog.length : 0;
    if (totalStages <= 1) {
      const singleStageMessage = difficultyInfo?.supportText || '';
      return singleStageMessage ? `${singleStageMessage}` : 'この図は単一ステージで構成されています。';
    }
    if (currentStageNum >= totalStages - 1) {
      return difficultyInfo?.completionHint || '全ステージを確認しました。';
    }
    const nextStage = stageCatalog[currentStageNum + 1];
    const nextLabel =
      nextStage?.stage_summary ||
      nextStage?.description ||
      nextStage?.title ||
      `Stage ${currentStageNum + 2}`;
    const transitionGuidance = difficultyInfo?.transitionHint ? ` ${difficultyInfo.transitionHint}` : '';
    return `次は Stage ${currentStageNum + 2}/${totalStages}「${nextLabel}」。${transitionGuidance}`.trim();
  }, [currentStageNum, difficultyInfo, stageCatalog]);

  useEffect(() => {
    setColorPrefsLoaded(false);
    const applyDefaults = () => {
      setColorOverrides((prev) => { const next = createDefaultColorOverrides(); return sameJson(prev, next) ? prev : next; });
      setHighlightOptions((prev) => { const next = createDefaultHighlightOptions(); return sameJson(prev, next) ? prev : next; });
      setHighlightAccentColor(settings?.highlightAccentColor || DEFAULT_HIGHLIGHT_ACCENT_COLOR);
      setColorPrefsLoaded(true);
    };
    if (!colorPreferenceStorageKey || typeof window === 'undefined') {
      applyDefaults();
      return;
    }
    try {
      const stored = window.localStorage.getItem(colorPreferenceStorageKey);
      if (!stored) {
        applyDefaults();
        return;
      }
      const parsed = JSON.parse(stored);
      const nextOverrides = {
        ...createDefaultColorOverrides(),
        ...(parsed?.colorOverrides || {}),
        elements: { ...(parsed?.colorOverrides?.elements || {}) }
      };
      setColorOverrides((prev) => (sameJson(prev, nextOverrides) ? prev : nextOverrides));
      const nextHighlight = {
        ...createDefaultHighlightOptions(),
        ...(parsed?.highlightOptions || {})
      };
      setHighlightOptions((prev) => (sameJson(prev, nextHighlight) ? prev : nextHighlight));
      setHighlightAccentColor(parsed?.highlightAccentColor || DEFAULT_HIGHLIGHT_ACCENT_COLOR);
      setColorPrefsLoaded(true);
    } catch (error) {
      logClientWarn('色設定の読み込みに失敗しました:', error);
      applyDefaults();
    }
  }, [colorPreferenceStorageKey]);

  useEffect(() => {
    if (!colorPreferenceStorageKey || !colorPrefsLoaded || typeof window === 'undefined') {
      return;
    }
    try {
      const payload = {
        colorOverrides,
        highlightOptions,
        highlightAccentColor,
        updatedAt: Date.now()
      };
      window.localStorage.setItem(colorPreferenceStorageKey, JSON.stringify(payload));
    } catch (error) {
      logClientWarn('色設定の保存に失敗しました:', error);
    }
  }, [colorPreferenceStorageKey, colorOverrides, highlightOptions, highlightAccentColor, colorPrefsLoaded]);

  // 軸・凡例・値ラベルの文字サイズの永続化（グローバル設定）。
  // 色設定（colorPreferenceStorageKey 単位、グラフ/ステージ毎）とは別キー・別エフェクトで完全隔離。
  // load: グローバルキーをマウント時に 1 回だけ読み込む。グラフ/ステージを跨いでも reset しない
  //       ので、ユーザーが一度調整したサイズは全グラフ・全セッションで維持される。
  useEffect(() => {
    let xTick = null;
    let yTick = null;
    let xLabel = null;
    let yLabel = null;
    let legend = null;
    let spacing = null;
    let vlabel = null;
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(GLOBAL_TEXT_SIZE_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) || {};
          // 新キー（4 次元）優先。無ければ旧単一 tickFontSize を 4 次元へ fan-out（後方互換）。
          const legacyTick = Number(parsed.tickFontSize);
          const xt = Number(parsed.xTickFontSize);
          const yt = Number(parsed.yTickFontSize);
          const xl = Number(parsed.xLabelFontSize);
          const yl = Number(parsed.yLabelFontSize);
          if (Number.isFinite(xt)) xTick = xt;
          else if (Number.isFinite(legacyTick)) xTick = legacyTick;
          if (Number.isFinite(yt)) yTick = yt;
          else if (Number.isFinite(legacyTick)) yTick = legacyTick;
          if (Number.isFinite(xl)) xLabel = xl;
          else if (Number.isFinite(legacyTick)) xLabel = legacyTick;
          if (Number.isFinite(yl)) yLabel = yl;
          else if (Number.isFinite(legacyTick)) yLabel = legacyTick;
          const l = Number(parsed.legendFontScale);
          const s = Number(parsed.legendLineSpacing);
          const v = Number(parsed.valueLabelScale);
          if (Number.isFinite(l)) legend = l;
          if (Number.isFinite(s)) spacing = s;
          if (Number.isFinite(v)) vlabel = v;
        }
      } catch (error) {
        logClientWarn('軸・凡例の文字サイズ設定の読み込みに失敗しました:', error);
      }
    }
    setLayoutOverrides((prev) => {
      const next = {
        ...prev,
        axis: { ...prev.axis, xTickFontSize: xTick, yTickFontSize: yTick, xLabelFontSize: xLabel, yLabelFontSize: yLabel },
        legendFontScale: legend,
        legendLineSpacing: spacing,
        valueLabelScale: vlabel
      };
      return sameJson(prev, next) ? prev : next;
    });
    setAxisPrefsLoaded(true);
    // グローバルキーは固定（モジュール定数）。マウント時 1 回のみ読み込むため依存は空。
  }, []);

  useEffect(() => {
    if (!axisPrefsLoaded || typeof window === 'undefined') {
      return;
    }
    try {
      const xTick = layoutOverrides.axis?.xTickFontSize;
      const yTick = layoutOverrides.axis?.yTickFontSize;
      const xLabel = layoutOverrides.axis?.xLabelFontSize;
      const yLabel = layoutOverrides.axis?.yLabelFontSize;
      const legend = layoutOverrides.legendFontScale;
      const spacing = layoutOverrides.legendLineSpacing;
      const vlabel = layoutOverrides.valueLabelScale;
      const payload = {};
      // 4 次元を個別保存。
      if (Number.isFinite(xTick)) payload.xTickFontSize = xTick;
      if (Number.isFinite(yTick)) payload.yTickFontSize = yTick;
      if (Number.isFinite(xLabel)) payload.xLabelFontSize = xLabel;
      if (Number.isFinite(yLabel)) payload.yLabelFontSize = yLabel;
      // 旧単一 tickFontSize も mirror（= xTickFontSize、Q3 downgrade/rollback 保護）。
      if (Number.isFinite(xTick)) payload.tickFontSize = xTick;
      if (Number.isFinite(legend)) payload.legendFontScale = legend;
      if (Number.isFinite(spacing)) payload.legendLineSpacing = spacing;
      if (Number.isFinite(vlabel)) payload.valueLabelScale = vlabel;
      if (Object.keys(payload).length) {
        payload.updatedAt = Date.now();
        window.localStorage.setItem(GLOBAL_TEXT_SIZE_STORAGE_KEY, JSON.stringify(payload));
      } else {
        window.localStorage.removeItem(GLOBAL_TEXT_SIZE_STORAGE_KEY);
      }
    } catch (error) {
      logClientWarn('軸・凡例の文字サイズ設定の保存に失敗しました:', error);
    }
  }, [layoutOverrides.axis, layoutOverrides.legendFontScale, layoutOverrides.legendLineSpacing, layoutOverrides.valueLabelScale, axisPrefsLoaded]);

  // A2 (Tier-1 a11y / Gate B): stage-transition aria-live announcer opt-in
  // toggle persistence. SAME load-once/save-on-change idiom as the
  // axis/legend font-size prefs directly above, but its own dedicated key
  // (ARIA_LIVE_ENABLED_STORAGE_KEY = 'graphTutor::ariaLiveEnabled::v1') and
  // its own boolean payload — completely separate from
  // GLOBAL_TEXT_SIZE_STORAGE_KEY. Global (not per-graph/per-stage), so once
  // a student turns this on it stays on across graphs and sessions.
  // load: read once on mount only (empty dep array).
  useEffect(() => {
    let enabled = false;
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(ARIA_LIVE_ENABLED_STORAGE_KEY);
        if (stored !== null) {
          enabled = JSON.parse(stored) === true;
        }
      } catch (error) {
        logClientWarn('読み上げ通知設定の読み込みに失敗しました:', error);
      }
    }
    setAriaLiveEnabled(enabled);
    setAriaLiveEnabledLoaded(true);
  }, []);

  // save: only after the initial load completes, so we never clobber a
  // stored value with the pre-load default before it's been read.
  useEffect(() => {
    if (!ariaLiveEnabledLoaded || typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(ARIA_LIVE_ENABLED_STORAGE_KEY, JSON.stringify(ariaLiveEnabled));
    } catch (error) {
      logClientWarn('読み上げ通知設定の保存に失敗しました:', error);
    }
  }, [ariaLiveEnabled, ariaLiveEnabledLoaded]);

  // show_value_tags overlay が参照する値ラベル倍率を ref に同期（dep 経由の再生成を避ける）。
  useEffect(() => {
    valueLabelScaleRef.current = layoutOverrides.valueLabelScale ?? 1;
  }, [layoutOverrides.valueLabelScale]);

  // グローバル設定変更に追従
  useEffect(() => {
    if (settings?.highlightAccentColor && settings.highlightAccentColor !== highlightAccentColor) {
      setHighlightAccentColor(settings.highlightAccentColor);
    }
  }, [settings?.highlightAccentColor, highlightAccentColor]);

  // onMouseUp never arrives for a touch selection on iPadOS, so the same commit
  // is also driven by `selectionchange` (see useSelectionCapture). Kept as a
  // callback so both entry points run identical logic.
  const explanationRef = useRef(null);
  const handleExplanationSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    const selectedText = selection.toString().trim();
    if (!selectedText) return;
    setExplanationHighlights((prev) => {
      if (prev.includes(selectedText)) return prev;
      return [...prev, selectedText].slice(-6);
    });
  }, []);

  const removeExplanationHighlight = useCallback((text) => {
    setExplanationHighlights((prev) => prev.filter((item) => item !== text));
  }, []);

  const clearExplanationHighlights = useCallback(() => {
    setExplanationHighlights([]);
  }, []);

  useSelectionCapture(explanationRef, handleExplanationSelection);

  useEffect(() => {
    const audioUrlSet = generatedAudioUrlsRef.current;
    return () => {
      audioUrlSet.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      audioUrlSet.clear();
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    setIsMobileDevice(/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ''));
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection || typeof connection.addEventListener !== 'function') {
      setNetworkInfo(null);
      return;
    }
    const updateConnectionInfo = () => {
      setNetworkInfo({
        effectiveType: connection.effectiveType || null,
        downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
        rtt: typeof connection.rtt === 'number' ? connection.rtt : null,
        saveData: Boolean(connection.saveData)
      });
    };
    updateConnectionInfo();
    connection.addEventListener('change', updateConnectionInfo);
    return () => {
      connection.removeEventListener('change', updateConnectionInfo);
    };
  }, []);

  useEffect(() => {
    if (!graphData) return;
    // A-3: keep the shown stage DATA aligned with the restored stage INDEX (both read
    // from graphData.current_stage) so a reloaded session resumes on its real stage.
    setCurrentStageData(resolveInitialStageData(graphData));
    setCurrentStageNum(graphData.current_stage ?? 0);
    setStageDataGraphId(graphData?.graph_id ?? null);
  }, [graphData]);

  // NOTE: elementVisibilityOverrides reset moved into stage handlers directly
  // (handleNextStage, handlePrevStage, handleJumpToStage, handleReset)
  // to batch state updates and prevent cascading re-renders that cause double animation.

  useEffect(() => {
    if (!activeStageData) {
      setStageExplanation('');
      setExplanationAudioUrl('');
      return;
    }
    const explanation = pickFirstNonEmptyText([
      activeStageData.stage_explanation,
      activeStageData.stage_text,
      activeStageData.step_text,
      activeStageData.rich_explanation,
      activeStageData.explanation,
      activeStageData.summary,
      activeStageData.description,
      activeStageData.notes,
      graphData?.current_stage_explanation,
      graphData?.stage_explanation
    ]);
    const explanationAudio = pickFirstValidUrl([
      activeStageData.stage_audio_url,
      activeStageData.explanation_audio_url,
      activeStageData.audio_url,
      activeStageData.voice_url,
      graphData?.current_stage_audio_url,
      graphData?.stage_audio_url,
      graphData?.explanation_audio_url
    ]);
    setStageExplanation(explanation);
    setExplanationAudioUrl(explanationAudio);
    setExplanationGraphId(stageDataGraphId);
  }, [activeStageData, graphData, stageDataGraphId]);

  const explanationLinesWithEmoji = useMemo(
    () => enrichExplanationText(stageExplanation),
    [stageExplanation]
  );

  const explanationSpeechText = useMemo(
    () => cleanTextForSpeech(stageExplanation),
    [stageExplanation]
  );

  const finalExplanationAudioUrl = explanationAudioUrl || generatedExplanationAudioUrl;

  useEffect(() => {
    let isActive = true;

    const finalizePendingState = () => {
      clearTtsSlowTimer();
      setIsTtsSlowWarning(false);
    };

    if (explanationAudioUrl) {
      setGeneratedExplanationAudioUrl('');
      setIsGeneratingExplanationAudio(false);
      setExplanationAudioError(null);
      finalizePendingState();
      return () => {
        isActive = false;
      };
    }

    // The explanation is set by another effect one render after a chart switch: until it
    // belongs to this chart, do not speak (or log) the previous chart's text (Codex B-R9).
    if (explanationGraphId !== (graphData?.graph_id ?? null)) {
      return () => {
        isActive = false;
      };
    }

    if (!voiceOutputEnabled || !explanationSpeechText || explanationSpeechText.length < 2) {
      setGeneratedExplanationAudioUrl('');
      setIsGeneratingExplanationAudio(false);
      finalizePendingState();
      return () => {
        isActive = false;
      };
    }

    const cacheKey = `${graphData?.graph_id || 'graph'}:${currentStageNum}:${explanationSpeechText}`;
    if (explanationAudioCacheRef.current.has(cacheKey)) {
      setGeneratedExplanationAudioUrl(explanationAudioCacheRef.current.get(cacheKey));
      setIsGeneratingExplanationAudio(false);
      setExplanationAudioError(null);
      finalizePendingState();
      return () => {
        isActive = false;
      };
    }
    if (typeof window !== 'undefined') {
      const persisted = window.localStorage.getItem(`${AUDIO_CACHE_PREFIX}${cacheKey}`);
      if (persisted) {
        const restoredUrl = createAudioUrlFromBase64(persisted);
        explanationAudioCacheRef.current.set(cacheKey, restoredUrl);
        setGeneratedExplanationAudioUrl(restoredUrl);
        setIsGeneratingExplanationAudio(false);
        setExplanationAudioError(null);
        finalizePendingState();
        return () => {
          isActive = false;
        };
      }
    }

    setGeneratedExplanationAudioUrl('');
    setIsGeneratingExplanationAudio(true);
    setExplanationAudioError(null);
    setIsTtsSlowWarning(false);
    clearTtsSlowTimer();
    ttsSlowTimerRef.current = setTimeout(() => {
      if (isActive) {
        setIsTtsSlowWarning(true);
      }
    }, TTS_SLOW_WARNING_DELAY_MS);

    // The reporter is captured at the request start: success / error are reported through the
    // same one, so they stay with the task and question the request belonged to (Codex B-R7).
    const reportTts = ttsEventRef.current;
    if (reportTts) reportTts('tts_start', 'graph');
    api.post(
      '/api/tts/synthesize',
      {
        text: explanationSpeechText,
        voice_type: preferredVoiceType,
        speaking_rate: preferredSpeechRate,
        pitch: 0.0,
        volume_gain_db: 0
      },
      { timeout: 30000 }
    ).then((response) => {
      if (!isActive) return;
      const audioContent = response.data?.audio_content;
      if (!audioContent) {
        throw new Error('No audio content returned');
      }
      const audioUrl = createAudioUrlFromBase64(audioContent);
      storeGeneratedExplanationAudio(cacheKey, audioUrl, audioContent);
      setGeneratedExplanationAudioUrl(audioUrl);
      if (reportTts) reportTts('tts_success', 'graph');
    }).catch((error) => {
      if (reportTts) reportTts('tts_error', 'graph');
      if (!isActive) return;
      logClientError('❌ 説明音声生成エラー:', error);
      setGeneratedExplanationAudioUrl('');
      setExplanationAudioError(error.response?.data?.detail || error.message);
    }).finally(() => {
      if (!isActive) return;
      setIsGeneratingExplanationAudio(false);
      finalizePendingState();
    });

    return () => {
      isActive = false;
      clearTtsSlowTimer();
    };
  }, [
    explanationAudioUrl,
    explanationSpeechText,
    voiceOutputEnabled,
    preferredVoiceType,
    preferredSpeechRate,
    preferredSpeechVolume,
    graphData?.graph_id,
    explanationGraphId,
    currentStageNum,
    clearTtsSlowTimer,
    storeGeneratedExplanationAudio,
    ttsRetryToken
  ]);

  useLayoutEffect(() => {
    if (!graphContainerRef.current) return;
    const element = graphContainerRef.current;

    const commitWidth = (raw) => {
      // Floor, not round: the chart must never be drawn wider than its box.
      const next = Math.floor(raw || DEFAULT_LAYOUT.width);
      setContainerWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next));
    };
    const updateWidth = () => {
      commitWidth(element.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;
      commitWidth(entries[0].contentRect.width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const adaptiveLayout = useMemo(() => {
    const baseLayout = cloneLayout(DEFAULT_LAYOUT);
    const actualGraphData = graphData?.graph_data || graphData || {};
    const graphType = actualGraphData?.graph_type || graphData?.graph_type || 'bar_chart';

    const width = clampValue((containerWidth || DEFAULT_LAYOUT.width) - 16, MIN_CHART_WIDTH, MAX_CHART_WIDTH);
    const margin = { ...baseLayout.margin };

    const xLabels = collectXAxisLabels(graphType, actualGraphData);
    const xAxisStrategy = determineXAxisLabelStrategy(
      xLabels,
      width,
      layoutOverrides.axis?.xTickFontSize ?? 20  // Pass current font size for better Japanese label handling
    );

    if (xAxisStrategy.extraMargin > 0) {
      margin.bottom = Math.min(margin.bottom + xAxisStrategy.extraMargin, 360);
    }

    baseLayout.axis.xTickRotation = xAxisStrategy.rotation;
    baseLayout.axis.xTickOffsetX = xAxisStrategy.offsetX;
    baseLayout.axis.xTickOffsetY = xAxisStrategy.offsetY;

    const legendItems = countLegendItems(graphType, actualGraphData);
    const legendPosition = 'right';

    return {
      ...baseLayout,
      width,
      legend: {
        ...baseLayout.legend,
        position: legendPosition
      },
      margin
    };
  }, [graphData, containerWidth, layoutOverrides.axis?.xTickFontSize]);

  // AI default for the # of extra function coordinate labels, from the student's
  // cognitive profile + effective support level. The manual slider (below) can
  // override it; the keypoint labels are never affected by either.
  const aiFunctionLabelBudget = useMemo(
    () => computeFunctionLabelBudget(studentProfile?.cognitive_difficulty, supportLevel),
    [studentProfile, supportLevel]
  );

  const finalLayout = useMemo(() => {
    const mergedMargin = {
      top: layoutOverrides.margin?.top ?? adaptiveLayout.margin.top,
      right: layoutOverrides.margin?.right ?? adaptiveLayout.margin.right,
      bottom: layoutOverrides.margin?.bottom ?? adaptiveLayout.margin.bottom,
      left: layoutOverrides.margin?.left ?? adaptiveLayout.margin.left
    };

    const legendManual = layoutOverrides.legendManual || {};

    return {
      ...adaptiveLayout,
      width: layoutOverrides.width ?? adaptiveLayout.width,
      height: layoutOverrides.height ?? adaptiveLayout.height,
      margin: mergedMargin,
      valueLabelScale: layoutOverrides.valueLabelScale ?? 1,
      // Effective function-label controls: manual override wins, else AI default.
      functionLabelBudget: layoutOverrides.functionLabelDensity ?? aiFunctionLabelBudget,
      functionLabelsVisible: layoutOverrides.functionLabelsVisible ?? true,
      axis: {
        ...adaptiveLayout.axis,
        xTickFontSize: layoutOverrides.axis?.xTickFontSize ?? adaptiveLayout.axis?.xTickFontSize,
        yTickFontSize: layoutOverrides.axis?.yTickFontSize ?? adaptiveLayout.axis?.yTickFontSize,
        xLabelFontSize: layoutOverrides.axis?.xLabelFontSize ?? adaptiveLayout.axis?.xLabelFontSize,
        yLabelFontSize: layoutOverrides.axis?.yLabelFontSize ?? adaptiveLayout.axis?.yLabelFontSize
      },
      legend: {
        ...adaptiveLayout.legend,
        position: layoutOverrides.legendPosition ?? adaptiveLayout.legend.position,
        manual: legendManual,
        fontScale: layoutOverrides.legendFontScale ?? 1,
        lineSpacing: layoutOverrides.legendLineSpacing ?? 1
      }
    };
  }, [adaptiveLayout, layoutOverrides, aiFunctionLabelBudget]);

  const getResolvedLegendManual = useCallback((manual = {}) => {
    const metrics = legendMetricsRef.current;
    const fallbackWidth = metrics?.width ?? finalLayout.legend?.width ?? DEFAULT_LAYOUT.legend.width;
    const fallbackHeight = metrics?.height ?? finalLayout.legend?.height ?? 140;
    const fallbackX = metrics?.x ?? (finalLayout.width - fallbackWidth - (finalLayout.margin?.right ?? 24));
    const fallbackY = metrics?.y ?? (finalLayout.margin?.top ?? 40);
    return {
      x: manual.x ?? fallbackX,
      y: manual.y ?? fallbackY,
      width: manual.width ?? fallbackWidth,
      height: manual.height ?? fallbackHeight
    };
  }, [finalLayout]);

  const clampLegendManual = useCallback((manual) => ({
    x: clampValue(manual.x, 0, Math.max(finalLayout.width - MIN_LEGEND_WIDTH, 0)),
    y: clampValue(manual.y, 0, Math.max(finalLayout.height - 80, 0)),
    width: clampValue(manual.width, MIN_LEGEND_WIDTH, finalLayout.width - MIN_MARGIN),
    height: clampValue(manual.height, 120, finalLayout.height - MIN_MARGIN)
  }), [finalLayout.height, finalLayout.width]);

  const snapLegendManual = useCallback((manual) => {
    const clamped = clampLegendManual(manual);
    const margin = finalLayout.margin || {};
    const rightEdge = Math.max(finalLayout.width - clamped.width - (margin.right ?? 24), 0);
    const leftEdge = margin.left ?? 24;
    const topEdge = margin.top ?? 24;
    const bottomEdge = Math.max(finalLayout.height - clamped.height - (margin.bottom ?? 24), 0);
    const centerX = Math.max((finalLayout.width - clamped.width) / 2, 0);
    const centerY = Math.max((finalLayout.height - clamped.height) / 2, 0);

    const snapped = { ...clamped };
    if (Math.abs(snapped.x - rightEdge) <= LEGEND_SNAP_THRESHOLD) {
      snapped.x = rightEdge;
    } else if (Math.abs(snapped.x - leftEdge) <= LEGEND_SNAP_THRESHOLD) {
      snapped.x = leftEdge;
    } else if (Math.abs(snapped.x - centerX) <= LEGEND_SNAP_THRESHOLD) {
      snapped.x = centerX;
    }

    if (Math.abs(snapped.y - topEdge) <= LEGEND_SNAP_THRESHOLD) {
      snapped.y = topEdge;
    } else if (Math.abs(snapped.y - bottomEdge) <= LEGEND_SNAP_THRESHOLD) {
      snapped.y = bottomEdge;
    } else if (Math.abs(snapped.y - centerY) <= LEGEND_SNAP_THRESHOLD) {
      snapped.y = centerY;
    }

    let legendPosition = null;
    if (snapped.y >= bottomEdge - LEGEND_SNAP_THRESHOLD) {
      legendPosition = 'bottom';
    } else if (snapped.x >= rightEdge - LEGEND_SNAP_THRESHOLD) {
      legendPosition = 'right';
    }

    return { manual: snapped, legendPosition };
  }, [clampLegendManual, finalLayout]);

  const commitLegendManual = useCallback((mutator) => {
    setLayoutOverrides((prev) => {
      const resolved = getResolvedLegendManual(prev.legendManual || {});
      const result = mutator({ ...resolved });
      if (!result) return prev;

      const manualCandidate = result.manual || result;
      const clamped = clampLegendManual(manualCandidate);
      const next = {
        ...prev,
        legendManual: clamped
      };
      if (result.legendPosition) {
        next.legendPosition = result.legendPosition;
      }
      return next;
    });
  }, [clampLegendManual, getResolvedLegendManual]);

  const moveLegendBy = useCallback((dx, dy) => {
    commitLegendManual((draft) => {
      const moved = { ...draft, x: draft.x + dx, y: draft.y + dy };
      return snapLegendManual(moved);
    });
  }, [commitLegendManual, snapLegendManual]);

  const resizeLegendBy = useCallback((dw, dh) => {
    commitLegendManual((draft) => {
      const resized = { ...draft, width: draft.width + dw, height: draft.height + dh };
      return snapLegendManual(resized);
    });
  }, [commitLegendManual, snapLegendManual]);

  const handleLegendKeyboard = useCallback((event, mode = 'move') => {
    const step = event.shiftKey ? LEGEND_KEYBOARD_FAST_STEP : LEGEND_KEYBOARD_STEP;
    if (mode === 'move') {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveLegendBy(-step, 0);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveLegendBy(step, 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveLegendBy(0, -step);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveLegendBy(0, step);
      }
      return;
    }

  if (mode === 'resize') {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        resizeLegendBy(step, 0);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        resizeLegendBy(-step, 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        resizeLegendBy(0, -step);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        resizeLegendBy(0, step);
      }
    }
  }, [moveLegendBy, resizeLegendBy]);

  const showTooltip = useCallback((event, payload) => {
    if (!graphContainerRef.current) return;
    const rect = graphContainerRef.current.getBoundingClientRect();
    const x = clampValue(event.clientX - rect.left + 12, 0, rect.width - 8);
    const y = clampValue(event.clientY - rect.top - 48, 0, rect.height - 8);
    setTooltipInfo({
      title: payload.title,
      subtitle: payload.subtitle,
      detailLines: payload.detailLines || [],
      color: payload.color || resolvedLabelColor,
      icon: payload.icon || '🔍',
      x,
      y
    });
  }, [resolvedLabelColor]);

  const hideTooltip = useCallback(() => {
    setTooltipInfo(null);
  }, []);

  const handleElementDrilldown = useCallback((payload) => {
    setDrilldownInfo({
      title: payload.title,
      description: payload.description || '',
      detailLines: payload.detailLines || [],
      variant: payload.variant || 'data',
      icon: payload.icon || '🔎',
      timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    });
  }, []);

  const attachInteractiveHandlers = useCallback((selection, config) => {
    if (!selection || typeof selection.on !== 'function') {
      return;
    }
    const {
      getPayload,
      isInteractive = () => true,
      onDrilldown,
      getElementId
    } = config || {};

    // Set data-graph-element-id for keyword highlight cross-referencing
    if (getElementId) {
      selection.attr('data-graph-element-id', (d) => getElementId(d) || null);
    }

    // ─── Keyboard reachability (Tier-1 a11y / Gate B, A4) ──────────────────
    // Tab-stop management: SVG-WIDE ROVING TABINDEX. Renderers reach this
    // helper in two shapes: real multi-node joins (bar, histogram) AND
    // per-point size-1 selections inside forEach/.each() loops (line,
    // scatter, area, dot-plot, box-plot outliers, stacked-bar segments,
    // decision-tree nodes). A selection-scoped cap would silently degrade to
    // one tab stop PER POINT for the second shape, so the roving scope is the
    // whole chart instead: every wired node is marked .ig-kbd-item with
    // tabindex=-1, only the chart's FIRST wired node is the Tab entry point
    // (tabindex=0), and Arrow keys move focus across all .ig-kbd-item nodes
    // of the chart in DOM order. Net: exactly ONE raw Tab stop per chart
    // regardless of call-site shape (population-safety: ADHD/ASD/WM overload
    // risk — same rationale as the rate-limited aria-live announcer).
    const resolveElementLabel = (d) => {
      const payloadTitle = getPayload ? getPayload(d)?.title : undefined;
      if (payloadTitle) return payloadTitle;
      const elementId = getElementId ? getElementId(d) : undefined;
      return elementId != null ? String(elementId) : '';
    };

    // Ghost guard: elements rendered for stage continuity but NOT yet
    // revealed at the current stage (isInteractive(d) === false — every
    // renderer passes stage visibility here) are excluded from ALL keyboard
    // wiring: no tabindex, no role, no aria-label, no .ig-kbd-item. Wiring
    // them would make an unrevealed ghost the Tab entry point and leak
    // unrevealed values through Arrow navigation + aria-label — the exact
    // opposite of the staged-presentation pedagogy the ghost-dim system
    // enforces visually.
    selection
      .classed('ig-kbd-item', (d) => !!isInteractive(d))
      .attr('tabindex', function assignRovingTabindex(d) {
        if (!isInteractive(d)) return null;
        const svgRoot = this.ownerSVGElement;
        const existingEntry = svgRoot && svgRoot.querySelector('.ig-kbd-item[tabindex="0"]');
        return existingEntry && existingEntry !== this ? '-1' : '0';
      })
      .attr('role', (d) => (isInteractive(d) ? 'button' : null))
      .attr('aria-label', (d) => (
        isInteractive(d)
          ? t('ig.a11y.graphElementLabel', { label: resolveElementLabel(d) })
          : null
      ));

    const getChartKbdNodes = (node) => {
      const svgRoot = node && node.ownerSVGElement;
      return svgRoot ? Array.from(svgRoot.querySelectorAll('.ig-kbd-item')) : [];
    };
    const moveRovingFocus = (fromNode, delta) => {
      const nodes = getChartKbdNodes(fromNode);
      const fromIndex = nodes.indexOf(fromNode);
      if (fromIndex === -1) return;
      const toIndex = clampValue(fromIndex + delta, 0, nodes.length - 1);
      if (toIndex === fromIndex) return;
      d3.select(fromNode).attr('tabindex', '-1');
      const target = nodes[toIndex];
      d3.select(target).attr('tabindex', '0');
      target.focus();
    };

    // Shared linkage used by BOTH the pointer click handler and the keyboard
    // activation handler below, so keyboard activation is guaranteed to stay
    // in lockstep with click semantics (no separate/duplicated branch).
    const triggerDrilldown = (datum) => {
      // Always notify parent for reverse keyword highlight (graph → text),
      // even if element is not fully interactive (e.g., hidden stage)
      if (onElementClick && getElementId) {
        onElementClick(getElementId(datum));
      }
      if (!isInteractive(datum)) return;
      if (onDrilldown) {
        handleElementDrilldown(onDrilldown(datum));
      } else if (getPayload) {
        const payload = getPayload(datum);
        handleElementDrilldown({
          title: payload.title,
          description: payload.subtitle,
          detailLines: payload.detailLines,
          icon: payload.icon,
          variant: 'data'
        });
      }
    };

    selection
      .style('pointer-events', 'auto')
      .on('pointerenter.tooltip', (event, datum) => {
        if (!isInteractive(datum)) return;
        if (getPayload) {
          showTooltip(event, getPayload(datum));
        }
      })
      .on('pointermove.tooltip', (event, datum) => {
        if (!isInteractive(datum)) return;
        if (getPayload) {
          showTooltip(event, getPayload(datum));
        }
      })
      .on('pointerleave.tooltip pointercancel.tooltip', () => {
        hideTooltip();
      })
      // Phase A: dwell observation for the research log (pointer devices only;
      // touch has no hover). Only the element id and the dwell length leave
      // here; the parent downsamples and buckets them. No render state changes.
      .on('pointerenter.research', function researchHoverStart(event, datum) {
        if (!hoverDwellRef.current || event?.pointerType === 'touch') return;
        const elementId = getElementId ? getElementId(datum) : null;
        if (!elementId) return;
        // The reporter of the task this hover began in (Codex B-R9): an end event that fires
        // after a task switch, before D3 removes the old nodes, goes to the old task's
        // callback, which drops it.
        this.__researchHoverStart = { elementId, at: Date.now(), report: hoverDwellRef.current };
      })
      .on('pointerleave.research pointercancel.research', function researchHoverEnd() {
        const start = this.__researchHoverStart;
        this.__researchHoverStart = null;
        if (!start || !start.report) return;
        start.report(start.elementId, Date.now() - start.at);
      })
      .on('click.drilldown', (event, datum) => {
        triggerDrilldown(datum);
      })
      .on('keydown.drilldown', function keydownDrilldown(event, datum) {
        if (isActivationKey(event.key)) {
          if (event.key !== 'Enter') {
            // Space must not scroll the page — covers both ' ' and the
            // legacy 'Spacebar' key value that isActivationKey accepts.
            event.preventDefault();
          }
          // SAME linkage as the click.drilldown branch above.
          triggerDrilldown(datum);
          return;
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          moveRovingFocus(this, 1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          moveRovingFocus(this, -1);
        }
      });
  }, [handleElementDrilldown, hideTooltip, onElementClick, showTooltip, t]);

  // ─── 双方向ハイライト（ProblemIntegrationView連携） ─────────────────────
  const isIntegrationHighlighted = useCallback((elementId) => {
    return Array.isArray(highlightedElements) && elementId && highlightedElements.includes(elementId);
  }, [highlightedElements]);

  const attachIntegrationClick = useCallback((selection, elementId) => {
    if (!selection || !elementId) return;
    const highlighted = isIntegrationHighlighted(elementId);
    selection.classed('graph-integration-highlight', highlighted);
    if (selection.node()?.tagName === 'text') {
      selection.classed('graph-integration-highlight-text', highlighted);
    }
  }, [isIntegrationHighlighted]);

  const startDrag = useCallback((mode, pointerEvent, context = {}) => {
    if (!pointerEvent) return;
    pointerEvent.preventDefault();

    const metrics = context.legendMetrics || {};
    legendMetricsRef.current = metrics;
    const start = {
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      x: metrics.x ?? 0,
      y: metrics.y ?? 0,
      width: metrics.width ?? (metrics.defaultWidth || MIN_LEGEND_WIDTH),
      height: metrics.height ?? 140
    };

    const handleMove = (event) => {
      // F4: convert the client-space drag delta to the chart's local coordinates,
      // so a magnified legend tracks the pointer 1:1. At fit (scale 1) this is an
      // exact no-op → no change to mouse/touch behaviour in normal, unzoomed use.
      const local = chartZoom.toLocalDelta(
        event.clientX - start.pointerX,
        event.clientY - start.pointerY,
        zoomStateRef.current
      );
      const dx = local.dx;
      const dy = local.dy;

      if (mode === 'legend-move') {
        const provisional = {
          x: clampValue(start.x + dx, 0, finalLayout.width - MIN_LEGEND_WIDTH),
          y: clampValue(start.y + dy, 0, finalLayout.height - 80),
          width: start.width,
          height: start.height
        };
        const snapped = snapLegendManual(provisional);
        setLayoutOverrides((prev) => ({
          ...prev,
          legendManual: {
            ...(prev.legendManual || {}),
            width: provisional.width,
            height: provisional.height,
            x: snapped.manual.x,
            y: snapped.manual.y
          }
        }));
      }

      if (mode === 'legend-resize') {
        const provisional = {
          width: clampValue(start.width + dx, MIN_LEGEND_WIDTH, finalLayout.width - MIN_MARGIN),
          height: clampValue(start.height + dy, 120, finalLayout.height - MIN_MARGIN),
          x: start.x,
          y: start.y
        };
        const snapped = snapLegendManual(provisional);
        setLayoutOverrides((prev) => ({
          ...prev,
          legendManual: {
            ...(prev.legendManual || {}),
            width: snapped.manual.width,
            height: snapped.manual.height
          }
        }));
      }
    };

    const stop = () => {
      setLayoutOverrides((prev) => {
        if (!prev.legendManual) return prev;
        const snapped = snapLegendManual(prev.legendManual);
        const next = {
          ...prev,
          legendManual: snapped.manual
        };
        if (snapped.legendPosition) {
          next.legendPosition = snapped.legendPosition;
        }
        return next;
      });
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  }, [finalLayout.height, finalLayout.width, snapLegendManual]);

  const decorateLegendGroup = useCallback((legendGroup) => {
    if (!legendGroup) {
      return;
    }

    legendGroup
      .attr('tabindex', 0)
      .attr('role', 'group')
      .attr('aria-label', t('ig.chart.legend'))
      .on('keydown.legend-move', (event) => handleLegendKeyboard(event, 'move'));
  }, [handleLegendKeyboard]);

const renderBarChart = useCallback((svg, stageData, layout) => {
const { width, height, margin, axis, legend } = layout;
const shouldAnimate = layout.shouldAnimate !== false;
const labelColor = resolvedLabelColor;
const axisWeight = axisFontWeight;
const legendTextColor = legendVisualContext.textColor;
const legendBgColor = legendVisualContext.backgroundColor;
const legendHighlightShadow = legendVisualContext.highlightShadow;
  const actualGraphData = graphData.graph_data || graphData;
  const bars = actualGraphData.elements?.filter((e) => e.type === 'bar') || [];
  const visibleElements = stageData.visible_elements || [];
  const stageFlags = getStageVisibilityFlags(stageData);
  const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
  const isShowAllStage = stageData.show_all_with_original_style === true;
  const axesVisible = stageFlags.showAxisLines || isShowAllStage;
  const showAxisLabels = stageFlags.showAxisLabels || isShowAllStage;
  const showTickLabels = stageFlags.showTickLabels || isShowAllStage;
  const showLegend = stageFlags.showLegend || isShowAllStage;
  const showData = stageFlags.showData || isShowAllStage;

  const basePalette = getCUDPalette(settings?.colorAccessibilityPreset);

  const data = bars.map((bar, index) => {
    const dp0 = bar.data_points?.[0];
    const rawValue = dp0?.y ?? dp0?.value ?? bar.value ?? (index + 1) * 2;
    const value = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue) || (index + 1) * 2;
    const rawLabel = dp0?.x_label ?? dp0?.category ?? null;
    const parsedLabel = (!rawLabel && bar.label?.includes(' - '))
        ? bar.label.split(' - ')[0]
        : null;
    const label = rawLabel || parsedLabel || bar.label || t('ig.chart.itemN', { n: index + 1 });
    const barLabel = bar.label || label;
    const visible = visibleElements.includes(bar.id);
    const elementEmoji = bar.emoji ||
      bar.icon ||
      bar.symbol ||
      bar.data_points?.[0]?.emoji ||
      bar.data_points?.[0]?.icon ||
      null;
    const assignedColor = getElementColor(
      bar.id,
      bar.color || stageData.highlight_color || basePalette[getConsistentIndexFromId(bar.id) % basePalette.length]
    );
    return {
      id: bar.id,
      label,
      barLabel,
      value,
      color: assignedColor,
      visible,
      isHighlighted: visible && !visibleElements.includes('axis_x'),
      emoji: elementEmoji,
      baseColor: assignedColor
    };
  });

  const categoryMap = {};
  data.forEach((bar) => {
    if (!categoryMap[bar.label]) {
      categoryMap[bar.label] = [];
    }
    categoryMap[bar.label].push(bar);
  });

  const categories = Object.keys(categoryMap);
  const isGroupedBarChart = categories.length < data.length;

  const axisXData = actualGraphData.elements?.find((e) => e.id === 'axis_x');
  const axisYData = actualGraphData.elements?.find((e) => e.id === 'axis_y');
  const xAxisLabel = axisXData?.label || t('ig.chart.category');
  const yAxisLabel = axisYData?.label || t('ig.chart.quantity');

  let xScale;
  let xSubScale = null;
  if (isGroupedBarChart) {
    xScale = d3.scaleBand()
      .domain(categories)
      .range([margin.left, width - margin.right])
      .padding(0.2);
    const maxGroupSize = Math.max(...Object.values(categoryMap).map((group) => group.length));
    xSubScale = d3.scaleBand()
      .domain(d3.range(maxGroupSize))
      .range([0, xScale.bandwidth()])
      .padding(0.1);
  } else {
    xScale = d3.scaleBand()
      .domain(data.map((d) => d.label))
      .range([margin.left, width - margin.right])
      .padding(0.4);
  }

  // The domain must reach BELOW zero when the data does. Pinning the floor at 0
  // mapped a negative value to a point under the baseline, which made the bar's
  // computed height negative — and an SVG rect with a negative height draws
  // nothing at all. A student looking at a sub-zero January temperature saw an
  // empty slot where their answer was, with no indication anything was missing.
  const values = data.map((d) => d.value).filter((v) => Number.isFinite(v));
  const maxValue = values.length ? Math.max(...values) : 10;
  const minValue = values.length ? Math.min(...values) : 0;
  // Zero always stays in view: a bar chart is read against its baseline, and
  // dropping it for all-negative data would leave nothing to read the bars
  // against.
  const yScale = d3.scaleLinear()
    .domain([Math.min(0, minValue), Math.max(0, maxValue)])
    .nice(5)
    .range([height - margin.bottom, margin.top]);
  // Where value 0 sits. Bars grow from HERE, not from the bottom of the plot.
  const baselineY = yScale(0);
  const barTop = (value) => yScale(Math.max(0, value));
  const barHeight = (value) => Math.abs(yScale(value) - baselineY);

  const xTickFontSize = axis?.xTickFontSize || 20;
  const yTickFontSize = axis?.yTickFontSize || 20;
  const xTickRotation = axis?.xTickRotation || 0;
  const xTickOffsetX = axis?.xTickOffsetX ?? 0;
  const xTickOffsetY = axis?.xTickOffsetY ?? 10;

  if (axesVisible) {
    const yAxisGenerator = d3.axisLeft(yScale).ticks(5);
    if (!showTickLabels) {
      yAxisGenerator.tickFormat(() => '');
    }
    const yAxis = svg.append('g')
      .attr('class', 'axis axis-y')
      .attr('data-element-id', 'axis_y')
      .attr('transform', `translate(${margin.left},0)`)
      .call(yAxisGenerator)
      .style('font-size', `${yTickFontSize}px`);

    yAxis.selectAll('line').attr('stroke', '#64748b');
    yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

    if (showAxisLabels) {
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -(height / 2))
        .attr('y', margin.left - 50)
        .attr('text-anchor', 'middle')
        .style('font-size', `${axis?.yLabelFontSize || 20}px`)
        .style('font-weight', axisWeight)
        .style('fill', labelColor)
        .text(yAxisLabel);
    }
  }

  if (axesVisible) {
    // With negative values the bars straddle zero, so the line they are read
    // against is the ZERO line, not the bottom of the plot. Category labels stay
    // at the bottom (below every bar, including the downward ones) — only the
    // baseline itself moves.
    const hasNegative = values.some((v) => v < 0);
    if (hasNegative) {
      svg.append('line')
        .attr('class', 'axis axis-zero')
        .attr('data-element-id', 'axis_zero')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', baselineY)
        .attr('y2', baselineY)
        .attr('stroke', '#64748b')
        .attr('stroke-width', 2);
    }
    const xAxisGenerator = d3.axisBottom(xScale).tickFormat(showTickLabels ? '' : '');
    const xAxis = svg.append('g')
      .attr('class', 'axis axis-x')
      .attr('data-element-id', 'axis_x')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(xAxisGenerator)
      .style('font-size', `${xTickFontSize}px`);

    xAxis.selectAll('line').attr('stroke', '#64748b');
    xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

    if (showTickLabels) {
      xAxis.selectAll('.tick').each(function(d) {
        const tick = d3.select(this);
        const icon = getSmartEmojiForLabel(d);
        const labelText = tick.append('text')
          .attr('text-anchor', xTickRotation !== 0 ? 'end' : 'middle')
          .style('font-size', `${xTickFontSize}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor);

        if (xTickRotation !== 0) {
          labelText
            .attr('transform', `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`)
            .text(d);
          // Emoji below rotated label (not rotated, centered on tick)
          tick.append('text')
            .attr('y', xTickOffsetY + 55)
            .attr('text-anchor', 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('fill', labelColor)
            .text(icon);
        } else {
          labelText
            .attr('y', xTickOffsetY + 10)
            .text(d);
          tick.append('text')
            .attr('y', xTickOffsetY + 45)
            .attr('text-anchor', 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('fill', labelColor)
            .text(icon);
        }
      });
    }
  }

  if (axesVisible && showAxisLabels && xAxisLabel) {
    // The caption has to clear whatever the tick labels actually occupy, so it
    // is measured, not guessed. A fixed offset cannot work: a rotated label of
    // rendered width L extends ~L*sin|angle| below the axis (well past 60px for
    // long CJK labels), and the emoji row sits at xTickOffsetY + 55.
    // NOTE xAxisStrategy.extraMargin does NOT solve this — it grows
    // margin.bottom, which moves the axis line and this caption down together,
    // so the gap between them never widens.
    const xTitleFontSize = axis?.xLabelFontSize || 20;
    const axisBaseY = height - margin.bottom;
    let xTitleY = axisBaseY + 60; // legacy offset kept as the floor
    if (showTickLabels) {
      const xAxisNode = svg.select('.axis-x').node();
      if (xAxisNode && typeof xAxisNode.getBBox === 'function') {
        try {
          // getBBox is local to the axis <g>, which is already translated to
          // axisBaseY — so add it back to get svg-space coordinates.
          const box = xAxisNode.getBBox();
          xTitleY = Math.max(xTitleY, axisBaseY + box.y + box.height + xTitleFontSize);
        } catch (e) {
          // getBBox throws on detached / zero-size trees; keep the floor.
        }
      }
    }
    // Never push the caption off the bottom of the canvas.
    xTitleY = Math.min(xTitleY, height - 4);
    svg.append('text')
      .attr('data-axis-title', 'x')
      .attr('x', (width - margin.left - margin.right) / 2 + margin.left)
      .attr('y', xTitleY)
      .attr('text-anchor', 'middle')
      .style('font-size', `${xTitleFontSize}px`)
      .style('font-weight', axisWeight)
      .style('fill', labelColor)
      .text(xAxisLabel);
  }

  if (isGroupedBarChart) {
    Object.values(categoryMap).forEach((groupBars) => {
      groupBars.forEach((bar, idx) => {
        bar.groupIndex = idx;
      });
    });
  } else {
    data.forEach((bar, idx) => { bar.groupIndex = idx; });
  }

  data.forEach((bar) => {
    const assignedColor = bar.color || '#94a3b8';
    bar.baseColor = assignedColor;
    bar.focusStyle = getElementFocusStyle({
      baseColor: assignedColor,
      fallbackColor: '#94a3b8',
      isVisible: isShowAllStage || bar.visible,
      isHighlighted: bar.isHighlighted,
      isShowAllStage,
      difficultyType,
      elementId: bar.id
    });
  });

  const getBarWidth = () => (isGroupedBarChart && xSubScale ? xSubScale.bandwidth() : xScale.bandwidth());
  const getBarX = (bar) => {
    if (isGroupedBarChart && xSubScale) {
      const offset = xSubScale(bar.groupIndex ?? 0) ?? 0;
      return xScale(bar.label) + offset;
    }
    return xScale(bar.label);
  };

  const barsToRender = showData ? data : [];
  const barGroups = svg.selectAll('.bar-group')
    .data(barsToRender)
    .enter()
    .append('g')
    .attr('class', 'bar-group')
    .attr('data-element-id', (d) => d.id);

  const barRects = barGroups.append('rect')
    .attr('class', 'bar')
    .attr('data-element-id', (d) => d.id)
    .attr('x', (d) => getBarX(d))
    .attr('y', (d) => shouldAnimate ? baselineY : barTop(d.value))
    .attr('width', (d) => getBarWidth(d))
    .attr('height', (d) => shouldAnimate ? 0 : barHeight(d.value))
    .attr('fill', (d) => d.focusStyle.color)
    .attr('opacity', (d) => d.focusStyle.opacity)
    .attr('stroke', (d) => {
      if (d.focusStyle.emphasis === 'keyword_highlight') return '#FFD700';
      if (d.focusStyle.emphasis === 'focus') return DEFAULT_STAT_OUTLINE_COLOR;
      if (d.focusStyle.emphasis === 'hidden') return 'none';
      return DEFAULT_STAT_OUTLINE_COLOR;
    })
    .attr('stroke-width', (d) => {
      if (d.focusStyle.emphasis === 'keyword_highlight') return 7;
      if (d.focusStyle.emphasis === 'focus') return 3;
      if (d.focusStyle.emphasis === 'hidden') return 0;
      return 1.5;
    })
    .attr('rx', 6)
    .classed('keyword-highlight-blink', (d) => d.focusStyle.emphasis === 'keyword_highlight');

  if (shouldAnimate) {
    barRects
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attr('y', (d) => barTop(d.value))
      .attr('height', (d) => barHeight(d.value));
  }

  attachInteractiveHandlers(barRects, {
    getElementId: (d) => d.id,
    getPayload: (bar) => ({
      title: bar.barLabel,
      subtitle: t('ig.chart.valueColon', { value: bar.value }),
      detailLines: [
        t('ig.chart.categoryColon', { value: bar.label })
      ].filter(Boolean),
      color: bar.focusStyle?.strokeColor || bar.focusStyle?.color,
      icon: getSmartEmojiForLabel(bar)
    }),
    isInteractive: (bar) => isShowAllStage || bar.visible,
    onDrilldown: (bar) => ({
      title: t('ig.chart.detail', { label: bar.barLabel }),
      description: t('ig.chart.barValueDesc', { label: bar.barLabel, value: bar.value }),
      detailLines: [
        t('ig.chart.categoryColon', { value: bar.label }),
        stageData?.stage_summary ? stageData.stage_summary : null
      ].filter(Boolean),
      icon: getSmartEmojiForLabel(bar),
      variant: 'bar'
    })
  });

  barGroups.append('text')
    .attr('class', 'value-label')
    // show_value_tags overlay target: tag the value-label text (SEPARATE data-value-for
    // attribute, NOT data-element-id, to avoid polluting the element-id namespace that
    // dim/focus keep-walks enumerate) so the handler's text[data-value-for] selector bolds it.
    .attr('data-value-for', (d) => d.id)
    .attr('x', (d) => getBarX(d) + getBarWidth(d) / 2)
    // Above the bar for positives; clear of the bar's lower edge for negatives.
    //
    // `y` on <text> is the BASELINE, so the glyphs are drawn ABOVE it. The old
    // code offset a negative label by a flat +20 — less than the 20-24px font —
    // so the digits landed back on top of the bar, and the clamp then pulled
    // them further INTO it. A student reading Sapporo's January temperature saw
    // "-3.6" cut in half by its own bar.
    .attr('y', (d) => {
      const fontSize = (d.focusStyle.emphasis === 'focus' ? 24 : 20) * vlScale(layout);
      if (d.value >= 0) return yScale(d.value) - 10;
      // Offset by the full font size so the glyph box clears the bar end.
      const below = yScale(d.value) + fontSize + 6;
      const plotFloor = height - margin.bottom - 4;
      // A deep bar can leave no room underneath. Put the label above the zero
      // line rather than clamping it back over the bar it belongs to.
      return below <= plotFloor ? below : baselineY - 8;
    })
    .attr('text-anchor', 'middle')
    .style('font-size', (d) => `${(d.focusStyle.emphasis === 'focus' ? 24 : 20) * vlScale(layout)}px`)
    .style('font-weight', (d) => d.focusStyle.emphasis === 'focus' ? 'bold' : (highlightOptions.labels ? '700' : '600'))
    .style('fill', labelColor)
    .style('opacity', (d) => d.focusStyle.opacity)
    .text((d) => d.value);

  const legendBars = showLegend
    ? data
    : (isShowAllStage ? data : data.filter((bar) => bar.visible));
  if (showLegend && legendBars.length > 0 && (isGroupedBarChart || legendBars.length > 1)) {
    const visibleCount = Math.min(legendBars.length, 10);
    const cappedVScale = legendVScaleCapped(layout, visibleCount, 20, 18);
    const legendMetrics = getLegendMetrics(layout, visibleCount, {
      defaultWidth: legend?.width || 140,
      rowHeight: 20 * cappedVScale,
      extraHeight: 36,
      bottomOffset: 40,
      maxItems: 10
    });
    legendMetricsRef.current = legendMetrics;

    const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);
    const legendGroup = svg.append('g')
      .attr('class', 'bar-legend')
      .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

    legendGroup.append('rect')
      .attr('width', legendMetrics.width)
      .attr('height', legendMetrics.height)
      .attr('fill', legendBgColor)
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 2)
      .attr('rx', 8)
      .style('cursor', 'move')
      .style('touch-action', 'none')
      .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

    legendGroup.append('text')
      .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
      .attr('y', 0)
      .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
      .style('font-size', `${10 * legendFontScale}px`)
      .style('font-weight', highlightOptions.legend ? '800' : 'bold')
      .style('fill', legendTextColor)
      .style('text-shadow', legendHighlightShadow)
      .text(`📊 ${t('ig.chart.legend')}`);
    legendGroup.append('text')
      .attr('x', legendMetrics.width - 8)
      .attr('y', 0)
      .attr('text-anchor', 'end')
      .style('font-size', `${9 * legendFontScale}px`)
      .style('font-weight', '700')
      .style('fill', legendTextColor)
      .style('cursor', 'pointer')
      .text(`🎨 ${t('ig.chart.editColor')}`)
      .on('click', () => setIsLayoutPanelOpen(true))
      .on('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsLayoutPanelOpen(true);
        }
      });
    const focusText = stageData?.stage_summary || stageData?.stage_explanation || graphData?.stage_explanation;
    if (focusText) {
      const focusButton = legendGroup.append('text')
        .attr('x', legendMetrics.width - 8)
        .attr('y', 16)
        .attr('text-anchor', 'end')
        .style('font-size', `${9 * legendFontScale}px`)
        .style('font-weight', '700')
        .style('fill', legendTextColor)
        .style('cursor', 'pointer')
        .text(`👀 ${t('ig.chart.focus')}`);
      const triggerFocus = () => handleElementDrilldown({
        title: t('ig.chart.focus'),
        description: focusText,
        detailLines: [],
        icon: '👀',
        variant: 'note'
      });
      focusButton.on('click', triggerFocus)
        .on('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFocus();
          }
        });
    }

      legendBars.slice(0, 10).forEach((bar, index) => {
        const itemY = (18 + index * 20) * cappedVScale;
        const emoji = getSmartEmojiForLabel(bar);

      legendGroup.append('rect')
        .attr('x', 6)
        .attr('y', itemY - 12)
        .attr('width', 12)
        .attr('height', 12)
        .attr('rx', 3)
        .attr('fill', bar.baseColor || '#94a3b8')
        .attr('stroke', '#e2e8f0')
        .attr('stroke-width', 1.5);

      legendGroup.append('text')
        .attr('x', 28)
        .attr('y', itemY)
        .style('font-size', `${12 * legendFontScale}px`)
        .style('fill', legendTextColor)
        .text(emoji);

      legendGroup.append('text')
        .attr('x', 46)
        .attr('y', itemY - 2)
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', 'bold')
        .style('fill', legendTextColor)
        .text((bar.barLabel || bar.label || t('ig.chart.itemN', { n: index + 1 })).substring(0, 18) +
          ((bar.barLabel || bar.label || '').length > 18 ? '...' : ''));

      legendGroup.append('text')
        .attr('x', 46)
        .attr('y', itemY + 10)
        .style('font-size', `${9 * legendFontScale}px`)
        .style('fill', legendTextColor)
        .text(t('ig.chart.valueColon', { value: bar.value }));
    });

    legendGroup.append('rect')
      .attr('x', legendMetrics.width - 16)
      .attr('y', legendMetrics.height - 16)
      .attr('width', 16)
      .attr('height', 16)
      .attr('fill', 'rgba(37, 99, 235, 0.6)')
      .attr('rx', 3)
      .style('cursor', 'nwse-resize')
      .style('touch-action', 'none')
      .attr('tabindex', 0)
      .attr('role', 'slider')
      .attr('aria-label', t('ig.chart.legendResize'))
      .attr('aria-valuemin', MIN_LEGEND_WIDTH)
      .attr('aria-valuemax', width - MIN_MARGIN)
      .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
      .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

    decorateLegendGroup(legendGroup, legendMetrics);
  }

  // ── Tier 2 overlay rendering (bar chart) ──
  const _t2grid = tier2ActionsRef.current.find(a => a.type === 'show_grid');
  if (_t2grid) {
    yScale.ticks(5).forEach(tick => {
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yScale(tick)).attr('y2', yScale(tick))
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
    const xTicks = xScale.domain();
    xTicks.forEach(tick => {
      const xPos = xScale(tick) + xScale.bandwidth() / 2;
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', xPos).attr('x2', xPos)
        .attr('y1', margin.top).attr('y2', height - margin.bottom)
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
  }
  const _t2proj = tier2ActionsRef.current.find(a => a.type === 'show_projection_lines');
  if (_t2proj) {
    const targetIds = new Set(_t2proj.element_ids || []);
    // Ghost-surface guard (same factory + context as applyOverlay): never draw
    // full-opacity projection lines onto stage-invisible 0.15 ghosts. Axis
    // nodes pass via predicate rule 5, so the pre-existing "empty ids → lines
    // on axes" behavior is intentionally preserved. Read-only: neither the
    // action object nor element_ids is cloned or mutated.
    const effectiveVisible = createEffectiveVisibilityPredicate({
      ...overlayVisibilityContextRef.current,
      elements: actualGraphData?.elements || [],
    });
    svg.selectAll('[data-element-id]').each(function() {
      const el = d3.select(this);
      const id = el.attr('data-element-id');
      if ((targetIds.size === 0 || targetIds.has(id)) && effectiveVisible(id)) {
        const bbox = this.getBBox();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y;
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', margin.left).attr('x2', cx).attr('y1', cy).attr('y2', cy)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', cx).attr('x2', cx).attr('y1', cy).attr('y2', height - margin.bottom)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
      }
    });
  }
  const _t2pin = tier2ActionsRef.current.find(a => a.type === 'pin_reference_value');
  if (_t2pin && _t2pin.params) {
    const refValue = _t2pin.params.reference_value;
    if (refValue !== undefined && yScale) {
      const yPos = yScale(refValue);
      svg.append('line').attr('class', 'overlay-pin-reference')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yPos).attr('y2', yPos)
        .attr('stroke', '#843F1F').attr('stroke-dasharray', '6,3').attr('stroke-width', 1.5);
      svg.append('text').attr('class', 'overlay-pin-label')
        .attr('x', width - margin.right + 4).attr('y', yPos + 4)
        .style('font-size', `${20 * vlScale(layout)}px`).style('fill', 'var(--accent)').style('font-weight', 'bold')
        .text(refValue);
    }
  }
  const _t2simp = tier2ActionsRef.current.find(a => a.type === 'simplify_labels');
  if (_t2simp && _t2simp.params && _t2simp.params.label_map) {
    const labelMap = _t2simp.params.label_map;
    svg.selectAll('.tick text, .axis text').each(function() {
      const el = d3.select(this);
      const text = el.text();
      if (labelMap[text]) { el.text(labelMap[text]); }
      else if (text.length > 8) { el.text(text.substring(0, 7) + '\u2026'); }
    });
  }

  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    highlightOptions.labels,
    highlightOptions.legend,
    legendVisualContext,
    resolvedLabelColor,
    studentProfile,
    startDrag
  ]);

  const renderLineChart = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
    const stageFlags = getStageVisibilityFlags(stageData);
    const stageVisibility = createStageVisibilityChecker(stageData.visible_elements || []);
    const hasVisibilityConstraints = (stageData.visible_elements || []).length > 0;
    const isShowAllStage = stageData.show_all_with_original_style === true || !hasVisibilityConstraints;
    // 折线图完整渲染 - 使用Vision AI真实数据
    const actualGraphData = graphData.graph_data || graphData; // ✨ Handle both nested and direct structure
    const lines = actualGraphData.elements?.filter(e => e.type === 'line') || [];
    const visibleElements = stageData.visible_elements || [];
    const axesVisible = stageFlags.showAxisLines || isShowAllStage;
    const showAxisLabels = stageFlags.showAxisLabels || isShowAllStage;
    const showTickLabels = stageFlags.showTickLabels || isShowAllStage;

    console.log('🔍 Line chart rendering:', {
      totalLines: lines.length,
      visibleElements: visibleElements,
      detectionMethod: actualGraphData.detection_method,
      graphDataStructure: {
        hasGraphData: !!graphData.graph_data,
        hasElements: !!actualGraphData.elements,
        elementCount: actualGraphData.elements?.length
      }
    });

    // 使用Vision AI返回的真实折线数据
    const lineData = lines.map((line, i) => {
      const isVisible = visibleElements.includes(line.id);
      const lineCount = visibleElements.filter(id => id.includes('line')).length;
      const isHighlighted = isVisible && lineCount === 1;

      // 使用Vision AI返回的颜色，或后备颜色
      const lineColor = getElementColor(
        line.id,
        line.color || getCUDPalette(settings?.colorAccessibilityPreset)[getConsistentIndexFromId(line.id) % getCUDPalette(settings?.colorAccessibilityPreset).length]
      );

      console.log(`📊 Line ${i}:`, {
        id: line.id,
        label: line.label,
        color: lineColor,
        isVisible,
        isHighlighted
      });

      // 使用Vision AI返回的真实数据点，或生成模拟数据
      const chartPoints = line.data_points && line.data_points.length > 0
        ? line.data_points.map(p => ({x: p.x, y: p.y, label: p.x_label}))
        : [
            {x: 1, y: 2 + i},
            {x: 2, y: 4 + i * 0.5},
            {x: 3, y: 3 + i * 0.8},
            {x: 4, y: 5 + i * 0.3},
            {x: 5, y: 4 + i * 0.6}
          ];

      const focusStyle = getElementFocusStyle({
        baseColor: lineColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || isVisible,
        isHighlighted,
        isShowAllStage,
        difficultyType,
        elementId: line.id
      });

      return {
        id: line.id,
        label: line.label || t('ig.chart.seriesN', { n: i+1 }),
        color: lineColor,  // ✨ 使用Vision AI识别的真实颜色
        points: chartPoints,
        visible: isVisible,
        isHighlighted: isHighlighted,
        focusStyle,
        emoji: line.emoji || line.icon || line.symbol || null
      };
    });

    console.log('✅ Final lineData:', lineData);

    // 🔧 动态计算坐标轴范围（基于真实数据）
    const allPoints = lineData.flatMap(line => line.points);
    const xValues = allPoints.map(p => p.x);
    const yValues = allPoints.map(p => p.y);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    console.log('📊 Data range:', { xMin, xMax, yMin, yMax });

    // 使用真实数据范围，添加10%的边距
    const xPadding = (xMax - xMin) * 0.1 || 1;
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const xScale = d3.scaleLinear()
      .domain([xMin - xPadding, xMax + xPadding])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice(5)
      .range([height - margin.bottom, margin.top]);

    // 绘制坐标轴
    if (axesVisible || !hasVisibilityConstraints) {
      const yTickFontSize = axis?.yTickFontSize || 20;
      const xTickFontSize = axis?.xTickFontSize || 20;
      // determineXAxisLabelStrategy covers line_chart / area_chart too, but
      // until now only bar/scatter/histogram/box APPLIED the rotation it
      // returns — so these two widened their bottom margin and still drew the
      // labels flat, straight through each other.
      const xTickRotation = axis?.xTickRotation || 0;
      const xTickOffsetX = axis?.xTickOffsetX || 0;
      const xTickOffsetY = axis?.xTickOffsetY || 0;
      const yAxisGenerator = d3.axisLeft(yScale).ticks(5);
      if (!showTickLabels) {
        yAxisGenerator.tickFormat(() => '');
      }

    const yAxis = svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(yAxisGenerator)
      .style('font-size', `${yTickFontSize}px`);
    yAxis.selectAll('line').attr('stroke', '#64748b');
    yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
    yAxis.selectAll('text')
      .style('fill', labelColor)
      .style('font-weight', axisWeight);

      // 🔧 使用真实数据点的x_label作为横轴刻度
      const uniqueXValues = [...new Set(allPoints.map(p => p.x))].sort((a, b) => a - b);
      const xAxisLabels = uniqueXValues.map(x => {
        const point = allPoints.find(p => p.x === x);
        return point?.label || x.toString();
      });

      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(xScale)
          .tickValues(uniqueXValues)
          .tickFormat(''))  // 清空默认标签，手动添加
        .style('font-size', `${xTickFontSize}px`);
      xAxis.selectAll('line').attr('stroke', '#64748b');
      xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

      // 为每个tick添加Emoji和文字标签
      console.log('✅ 开始为折线图X轴添加emoji...');
      const tickCount = xAxis.selectAll('.tick').size();
      console.log(`🔍 折线图X轴tick数量: ${tickCount}`);

      if (showTickLabels) {
        xAxis.selectAll('.tick').each(function(d, i) {
          const tick = d3.select(this);
          const label = xAxisLabels[i] || d.toString();

          console.log(`🔍 [折线图横軸] ラベル: "${label}" (type: ${typeof label})`);

          const icon = getSmartEmojiForLabel(label);  // 使用智能识别

          console.log(`🎨 [折线图横軸] マッチしたEmoji: "${icon}"`);

          // 添加文字标签（在横轴下方20px）
          const labelText = tick.append('text')
            .attr('text-anchor', xTickRotation !== 0 ? 'end' : 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('font-weight', axisWeight)
            .style('fill', labelColor)
            .text(label);
          if (xTickRotation !== 0) {
            labelText.attr(
              'transform',
              `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`,
            );
          } else {
            labelText.attr('y', 20);
          }

          // 添加Emoji图标（在文字下方）
          tick.append('text')
            // Rotated labels sweep downward, so the icon has to drop with them.
            .attr('y', xTickRotation !== 0 ? xTickOffsetY + 55 : 55)
            .attr('text-anchor', 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('fill', labelColor)
            .text(icon);

          console.log(`✅ [折线图横軸] Emoji "${icon}" + テキスト "${label}" を追加完了`);
        });
      }

      console.log('✅ 折线图X轴和emoji图标绘制完成');

      // ✨ 轴标签 - 从Vision AI获取真实标签 + 添加图标
      const actualGraphData = graphData.graph_data || graphData; // ✨ Handle both nested and direct structure
      const yAxisData = actualGraphData.elements?.find(e => e.id === 'axis_y');
      const xAxisData = actualGraphData.elements?.find(e => e.id === 'axis_x');

      const yAxisLabel = yAxisData?.label || t('ig.chart.yAxis');
      const xAxisLabel = xAxisData?.label || t('ig.chart.xAxis');

      // Y轴标签 + 图标
      if (showAxisLabels) {
        const yAxisGroup = svg.append('g')
          .attr('transform', `rotate(-90) translate(${-(height / 2)}, ${margin.left - 60})`);

        yAxisGroup.append('text')
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('fill', labelColor)
          .text('📊');

        yAxisGroup.append('text')
          .attr('x', 30)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);

        if (xAxisLabel) {
          const xLabelEmoji = getSmartEmojiForLabel(xAxisLabel);
          // Measured, not fixed at +80. Rotating the tick labels (above) makes
          // the band reach well past that constant, so a hardcoded offset now
          // lands inside the labels — the regression this rotation would
          // otherwise introduce, and the same defect already fixed on the bar
          // chart. The old value stays as the floor, so charts with short
          // unrotated labels render exactly as before.
          const xAxisBase = height - margin.bottom;
          let xCaptionY = xAxisBase + 80;
          const xAxisNodeForCaption = xAxis.node();
          if (xAxisNodeForCaption && typeof xAxisNodeForCaption.getBBox === 'function') {
            try {
              const box = xAxisNodeForCaption.getBBox();
              if (box && (box.width || box.height)) {
                xCaptionY = Math.max(
                  xCaptionY,
                  xAxisBase + box.y + box.height + (axis?.xLabelFontSize || 20),
                );
              }
            } catch (e) {
              // getBBox throws on detached / zero-size trees; keep the floor.
            }
          }
          // The caption group draws the emoji at y=0 and the label 28px BELOW
          // it, so the clamp has to reserve that much or the label itself is
          // cut off at the canvas edge — trading an overlap for an unreadable
          // caption, which is no better.
          const xCaptionFont = axis?.xLabelFontSize || 20;
          xCaptionY = Math.min(xCaptionY, height - 4 - 28 - xCaptionFont);
          const xAxisGroup = svg.append('g')
            .attr('transform', `translate(${margin.left + (width - margin.left - margin.right) / 2}, ${xCaptionY})`);

          xAxisGroup.append('text')
            .attr('text-anchor', 'middle')
            .style('font-size', `${axis?.xLabelFontSize || 20}px`)
            .style('fill', labelColor)
            .text(xLabelEmoji);

          xAxisGroup.append('text')
            .attr('y', 28)
            .attr('text-anchor', 'middle')
            .style('font-size', `${axis?.xLabelFontSize || 20}px`)
            .style('font-weight', axisWeight)
            .style('fill', labelColor)
            .text(xAxisLabel);
        }
      }
    }

    // 绘制折线
    const line = d3.line()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y))
      .curve(d3.curveMonotoneX);

    lineData.forEach((lineItem) => {
      if (!lineItem.visible && !isShowAllStage) {
        return;
      }
      const style = lineItem.focusStyle;

      const path = svg.append('path')
        .datum(lineItem.points)
        .attr('data-element-id', lineItem.id || 'line-' + lineData.indexOf(lineItem))
        .attr('fill', 'none')
        .attr('stroke', style.color)
        .attr('stroke-width', style.emphasis === 'focus' ? 6 : 3)
        .attr('opacity', style.opacity)
        .attr('d', line);

      const totalLength = path.node().getTotalLength();
      if (shouldAnimate) {
        path
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(1000)
          .ease(d3.easeLinear)
          .attr('stroke-dashoffset', 0);
      }

      lineItem.points.forEach(point => {
        const circle = svg.append('circle')
          .datum({ point, lineItem })
          .attr('data-element-id', lineItem.id || 'line-' + lineData.indexOf(lineItem))
          .attr('cx', xScale(point.x))
          .attr('cy', yScale(point.y))
          .attr('r', style.emphasis === 'focus' ? 8 : 4)
          .attr('fill', style.color)
          .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', style.emphasis === 'keyword_highlight' ? 6 : (style.emphasis === 'focus' ? 2 : 1))
          .attr('opacity', shouldAnimate ? 0 : style.opacity)
          .classed('keyword-highlight-blink', style.emphasis === 'keyword_highlight');

        if (shouldAnimate) {
          circle
            .transition()
            .delay(1000)
            .duration(300)
            .attr('opacity', style.opacity);
        }

        attachInteractiveHandlers(circle, {
          getElementId: (d) => d.lineItem?.id || lineItem.id,
          getPayload: ({ point }) => ({
            title: lineItem.label || t('ig.chart.dataPoint'),
            subtitle: `(${point.x}, ${point.y})`,
            detailLines: [],
            icon: lineItem.emoji || getSmartEmojiForLabel(lineItem.label)
          }),
          isInteractive: () => isShowAllStage || lineItem.visible,
          onDrilldown: ({ point }) => ({
            title: t('ig.chart.detail', { label: lineItem.label || t('ig.chart.data') }),
            description: `X=${point.x}, Y=${point.y}`,
            detailLines: [
              t('ig.chart.seriesColon', { value: lineItem.label || 'N/A' }),
              stageData?.stage_summary ? stageData.stage_summary : null
            ].filter(Boolean),
            icon: lineItem.emoji || getSmartEmojiForLabel(lineItem.label),
            variant: 'line'
          })
        });
      });

      // A' 2026-06-15: render value labels ALWAYS (so a grounded show_value_tags overlay
      // can reveal+bold them at upload) but keep them hidden unless this element is focused.
      // The show_value_tags handler sets display:null to reveal. Students without the
      // overlay (and non-focus) see no value labels — no global clutter change.
      lineItem.points.forEach(point => {
        const _vlt = svg.append('text')
          .attr('data-value-for', lineItem.id)
          .attr('x', xScale(point.x))
          .attr('y', yScale(point.y) - 12)
          .attr('text-anchor', 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('font-weight', 'bold')
          .style('fill', labelColor)
          .text(`${point.y}`);
        if (style.emphasis !== 'focus') _vlt.style('display', 'none');
      });
    });

  // ── Tier 2 overlay rendering (line chart) ──
  const _t2gridL = tier2ActionsRef.current.find(a => a.type === 'show_grid');
  if (_t2gridL) {
    yScale.ticks(5).forEach(tick => {
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yScale(tick)).attr('y2', yScale(tick))
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
    const xTicksL = xScale.ticks ? xScale.ticks(5) : [];
    xTicksL.forEach(tick => {
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', xScale(tick)).attr('x2', xScale(tick))
        .attr('y1', margin.top).attr('y2', height - margin.bottom)
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
  }
  const _t2projL = tier2ActionsRef.current.find(a => a.type === 'show_projection_lines');
  if (_t2projL) {
    const targetIds = new Set(_t2projL.element_ids || []);
    // Ghost-surface guard — see renderBarChart's _t2proj block (same factory
    // + context as applyOverlay; axis nodes pass via rule 5; read-only).
    const effectiveVisible = createEffectiveVisibilityPredicate({
      ...overlayVisibilityContextRef.current,
      elements: actualGraphData?.elements || [],
    });
    svg.selectAll('[data-element-id]').each(function() {
      const el = d3.select(this);
      const id = el.attr('data-element-id');
      if ((targetIds.size === 0 || targetIds.has(id)) && effectiveVisible(id)) {
        const bbox = this.getBBox();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y;
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', margin.left).attr('x2', cx).attr('y1', cy).attr('y2', cy)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', cx).attr('x2', cx).attr('y1', cy).attr('y2', height - margin.bottom)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
      }
    });
  }
  const _t2pinL = tier2ActionsRef.current.find(a => a.type === 'pin_reference_value');
  if (_t2pinL && _t2pinL.params) {
    const refValue = _t2pinL.params.reference_value;
    if (refValue !== undefined && yScale) {
      const yPos = yScale(refValue);
      svg.append('line').attr('class', 'overlay-pin-reference')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yPos).attr('y2', yPos)
        .attr('stroke', '#843F1F').attr('stroke-dasharray', '6,3').attr('stroke-width', 1.5);
      svg.append('text').attr('class', 'overlay-pin-label')
        .attr('x', width - margin.right + 4).attr('y', yPos + 4)
        .style('font-size', `${20 * vlScale(layout)}px`).style('fill', 'var(--accent)').style('font-weight', 'bold')
        .text(refValue);
    }
  }
  const _t2simpL = tier2ActionsRef.current.find(a => a.type === 'simplify_labels');
  if (_t2simpL && _t2simpL.params && _t2simpL.params.label_map) {
    const labelMap = _t2simpL.params.label_map;
    svg.selectAll('.tick text, .axis text').each(function() {
      const el = d3.select(this);
      const text = el.text();
      if (labelMap[text]) { el.text(labelMap[text]); }
      else if (text.length > 8) { el.text(text.substring(0, 7) + '\u2026'); }
    });
  }

  }, [attachInteractiveHandlers, axisFontWeight, getElementColor, getElementFocusStyle, graphData, resolvedLabelColor, studentProfile]);

  const renderPieChart = useCallback((svg, stageData, layout) => {
    const { width, height, margin, legend, axis } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const legendTextColor = resolvedLegendTextColor;
    const legendBgColor = legendBackgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
    const isShowAllStage = stageData.show_all_with_original_style === true;
    const defaultColors = getCUDPalette(settings?.colorAccessibilityPreset);

    console.log('🥧🥧🥧 ===== renderPieChart 被调用 ===== 🥧🥧🥧');
    console.log('📏 SVG尺寸:', { width, height, margin });

    const actualGraphData = graphData.graph_data || graphData;
    const segments = actualGraphData.elements?.filter((e) => e.type === 'pie_segment' || e.type === 'segment') || [];
    const visibleElements = stageData.visible_elements || [];
    const visibleSegmentIds = visibleElements.filter((id) => id.includes('segment') || id.includes('pie'));

    console.log('🔍 Pie chart analysis:', {
      totalSegments: segments.length,
      visibleElements
    });

    const data = segments.map((segment, index) => {
      const value = segment.value || segment.percentage || (index + 1) * 10;
      const label = segment.label || t('ig.chart.itemN', { n: index + 1 });
      const isVisible = visibleElements.includes(segment.id);
      const baseColor = getElementColor(segment.id, segment.color || defaultColors[getConsistentIndexFromId(segment.id) % defaultColors.length]);
      const focusStyle = getElementFocusStyle({
        baseColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || isVisible,
        isHighlighted: isVisible && visibleSegmentIds.length <= 1,
        isShowAllStage,
        difficultyType,
        elementId: segment.id
      });

      return {
        id: segment.id,
        label,
        value,
        percentage: segment.percentage || null,
        baseColor,
        focusStyle,
        visible: isVisible,
        emoji: segment.emoji || segment.icon || segment.symbol || null
      };
    });

    console.log('✅ Pie data:', data);

    const centerX = width / 2 - 100;
    const centerY = height / 2 + 20;
    const radius = Math.min(width, height) / 3.5;
    console.log('🔍 Pie chart layout:', { centerX, centerY, radius });

    const arc = d3.arc().innerRadius(0).outerRadius(radius);
    const arcHighlight = d3.arc().innerRadius(0).outerRadius(radius * 1.1);
    const pieGroup = svg.append('g').attr('transform', `translate(${centerX}, ${centerY})`);

    const totalValue = d3.sum(data, (item) => item.percentage || item.value || 0);
    let currentAngle = 0;
    const pieDataWithAngles = data.map((segment) => {
      const weight = segment.percentage || segment.value || 0;
      const angleSize = totalValue ? (weight / totalValue) * 2 * Math.PI : 0;
      const startAngle = currentAngle;
      currentAngle += angleSize;
      return { data: segment, startAngle, endAngle: currentAngle, value: segment.value };
    });
    const placedPieLabels = [];
    const registerPieLabelBox = (x, y, width, height) => {
      placedPieLabels.push({ x, y, width, height });
    };
    const computePieLabelY = (x, y, textAnchor) => {
      const labelWidth = textAnchor === 'middle' ? 80 : 140;
      const labelHeight = 18;
      const overlaps = (candidateY) => placedPieLabels.some((label) => {
        const horizontalGap = Math.abs(label.x - x);
        const verticalGap = Math.abs(label.y - candidateY);
        return horizontalGap < (label.width + labelWidth) / 2 && verticalGap < (label.height + labelHeight) / 2;
      });
      let candidateY = y;
      let attempts = 0;
      while (attempts < 10 && overlaps(candidateY)) {
        candidateY += (attempts % 2 === 0 ? 1 : -1) * labelHeight;
        attempts += 1;
      }
      registerPieLabelBox(x, candidateY, labelWidth, labelHeight);
      return candidateY;
    };

    pieDataWithAngles.forEach((slice) => {
      const originalData = slice.data;
      const shouldRender = originalData.visible || isShowAllStage;
      if (!shouldRender) {
        return;
      }
      const style = originalData.focusStyle || { color: originalData.baseColor, strokeColor: originalData.baseColor, opacity: 1, emphasis: 'full' };
      const strokeColor = style.strokeColor || originalData.baseColor || '#94a3b8';
      const strokeWidth = style.emphasis === 'keyword_highlight' ? 6 : (style.emphasis === 'focus' ? 3 : 2);
      const percentageValue = originalData.percentage || originalData.value || 0;
      const outlineColor = style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR;
      const segmentPath = style.emphasis === 'focus' ? arcHighlight(slice) : arc(slice);

      const segment = pieGroup
        .append('path')
        .datum(slice)
        .attr('data-element-id', originalData.id || 'pie-segment-' + pieDataWithAngles.indexOf(slice))
        .attr('fill', style.color)
        .attr('opacity', style.opacity)
        .attr('stroke', outlineColor)
        .attr('stroke-width', strokeWidth)
        .style('cursor', 'pointer')
        .classed('keyword-highlight-blink', style.emphasis === 'keyword_highlight');

      if (shouldAnimate) {
        segment
          .transition()
          .duration(800)
          .ease(d3.easeCubicOut)
          .attrTween('d', (d) => {
            const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
            return (t) => (style.emphasis === 'focus' ? arcHighlight(interpolate(t)) : arc(interpolate(t)));
          });
      } else {
        segment.attr('d', segmentPath);
      }

      attachInteractiveHandlers(segment, {
        getElementId: (d) => d.data?.id || originalData.id,
        getPayload: () => ({
          title: originalData.label,
          subtitle: `${percentageValue}% / ${originalData.value}`,
          detailLines: [],
          color: strokeColor,
          icon: originalData.emoji || getSmartEmojiForLabel(originalData)
        }),
        isInteractive: () => isShowAllStage || originalData.visible,
        onDrilldown: () => ({
          title: t('ig.chart.detail', { label: originalData.label }),
          description: `${percentageValue}% (${originalData.value})`,
          detailLines: [stageData?.stage_summary ? stageData.stage_summary : null].filter(Boolean),
          icon: originalData.emoji || getSmartEmojiForLabel(originalData),
          variant: 'pie'
        })
      });

      const [labelX, labelY] = arc.centroid(slice);
      if ((percentageValue > 5) && (style.opacity ?? 1) > 0) {
        pieGroup
          .append('text')
          .attr('data-value-for', originalData.id)
          .attr('transform', `translate(${labelX}, ${labelY})`)
          .attr('text-anchor', 'middle')
          .style('font-size', `${(style.emphasis === 'focus' ? 24 : 20) * vlScale(layout)}px`)
          .style('font-weight', style.emphasis === 'focus' ? '800' : axisWeight)
          .style('fill', labelColor)
          .style('opacity', shouldAnimate ? 0 : 1)
          .text(`${percentageValue}%`)
          .call((selection) => {
            if (shouldAnimate) {
              selection
                .transition()
                .delay(800)
                .duration(300)
                .style('opacity', 1);
            }
          });
      }

      const outerArc = d3.arc().innerRadius(radius * 1.2).outerRadius(radius * 1.2);
      const [outerX, outerY] = outerArc.centroid(slice);
      const midAngle = (slice.startAngle + slice.endAngle) / 2;
      const textAnchor = midAngle < Math.PI ? 'start' : 'end';
      const xOffset = midAngle < Math.PI ? 10 : -10;
      const emoji = getSmartEmojiForLabel(originalData);

      pieGroup
        .append('polyline')
        .attr('points', () => {
          const pos = outerArc.centroid(slice);
          pos[0] = radius * 1.15 * (midAngle < Math.PI ? 1 : -1);
          return [arc.centroid(slice), outerArc.centroid(slice), pos];
        })
        .attr('stroke', style.emphasis === 'focus' ? DEFAULT_STAT_OUTLINE_COLOR : '#cbd5e1')
        .attr('stroke-width', style.emphasis === 'focus' ? 2 : 1)
        .attr('fill', 'none')
        .style('opacity', shouldAnimate ? 0 : 0.6)
        .call((selection) => {
          if (shouldAnimate) {
            selection
              .transition()
              .delay(800)
              .duration(300)
              .style('opacity', 0.6);
          }
        });

      const maxLabelRadius = radius * 0.9;
      const clampToRadius = (x, y, limit) => {
        const dist = Math.hypot(x, y);
        if (dist === 0 || dist <= limit) return { x, y };
        const scale = limit / dist;
        return { x: x * scale, y: y * scale };
      };
      const initialInside = clampToRadius(outerX * 0.85, outerY * 0.85, maxLabelRadius);
      const finalLabelX = initialInside.x + xOffset * 0.6;
      const primaryLabelY = computePieLabelY(finalLabelX, initialInside.y, textAnchor);
      const labelGroup = pieGroup
        .append('text')
        .attr('transform', `translate(${finalLabelX}, ${primaryLabelY})`)
        .attr('text-anchor', textAnchor)
        .style('font-size', style.emphasis === 'focus' ? `${(axis?.xLabelFontSize || 20) + 2}px` : `${axis?.xLabelFontSize || 20}px`)
        .style('font-weight', style.emphasis === 'focus' ? '800' : axisWeight)
        .style('fill', labelColor)
        .style('opacity', shouldAnimate ? 0 : 1)
        .style('cursor', 'grab');

      labelGroup
        .text(`${emoji} ${originalData.label}`)
        .call((selection) => {
          if (shouldAnimate) {
            selection
              .transition()
              .delay(800)
              .duration(300)
              .style('opacity', 1);
          }
        });

      const secondaryLabelY = primaryLabelY + 15;
      registerPieLabelBox(finalLabelX, secondaryLabelY, textAnchor === 'middle' ? 80 : 140, 14);
      const secondaryText = pieGroup
        .append('text')
        .attr('transform', `translate(${finalLabelX}, ${secondaryLabelY})`)
        .attr('text-anchor', textAnchor)
        .style('font-size', `${20 * vlScale(layout)}px`)
        .style('fill', mixColors(labelColor, '#475569', 0.5))
        .style('opacity', shouldAnimate ? 0 : 1)
        .text(`${originalData.value} (${percentageValue}%)`);
      secondaryText.call((selection) => {
        if (shouldAnimate) {
          selection
            .transition()
            .delay(800)
            .duration(300)
            .style('opacity', 1);
        }
      });

      const dragState = { x: finalLabelX, y: primaryLabelY };
      const applyLabelTransform = () => {
        labelGroup.attr('transform', `translate(${dragState.x}, ${dragState.y})`);
        secondaryText.attr('transform', `translate(${dragState.x}, ${dragState.y + 15})`);
      };
      const dragBehavior = d3.drag()
        .on('start', () => {
          labelGroup.style('cursor', 'grabbing');
        })
        .on('drag', (event) => {
          const clamped = clampToRadius(event.x, event.y, maxLabelRadius);
          dragState.x = clamped.x;
          dragState.y = clamped.y;
          applyLabelTransform();
        })
        .on('end', () => {
          labelGroup.style('cursor', 'grab');
        });
      labelGroup.classed('ig-draggable', true).call(dragBehavior);
    });

    if (data.length > 0) {
      const visibleCount = Math.min(data.length, 8);
      const cappedVScale = legendVScaleCapped(layout, visibleCount, 26, 22);
      const legendMetrics = getLegendMetrics(layout, visibleCount, {
        defaultWidth: legend?.width || 140,
        rowHeight: 26 * cappedVScale,
        extraHeight: 36,
        bottomOffset: 34
      });
      legendMetricsRef.current = legendMetrics;

      const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);
      const legendGroup = svg.append('g').attr('class', 'pie-legend').attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

      legendGroup
        .append('rect')
        .attr('width', legendMetrics.width)
        .attr('height', legendMetrics.height)
        .attr('fill', legendBgColor)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 2)
        .attr('rx', 8)
        .style('cursor', 'move')
        .style('touch-action', 'none')
        .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

      legendGroup
        .append('text')
        .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
        .attr('y', 0)
        .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', highlightOptions.legend ? '800' : 'bold')
        .style('fill', legendTextColor)
        .style('text-shadow', legendHighlightShadow)
        .text(`🥧 ${t('ig.chart.legend')}`);

      data.slice(0, 8).forEach((item, idx) => {
        const entryStyle = item.focusStyle || {};
        const entryOpacity = (item.visible || isShowAllStage) ? (entryStyle.opacity ?? 1) : 0.35;
        const swatchColor = entryStyle.strokeColor || item.baseColor || defaultColors[idx % defaultColors.length];
        const emoji = getSmartEmojiForLabel(item);
        const itemY = (22 + idx * 26) * cappedVScale;

        legendGroup
          .append('circle')
          .attr('cx', 10)
          .attr('cy', itemY)
          .attr('r', 7)
          .attr('fill', swatchColor)
          .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', 1.5)
          .attr('opacity', entryOpacity);

        legendGroup
          .append('text')
          .attr('x', 24)
          .attr('y', itemY + 4)
          .style('font-size', `${12 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(emoji);

        const displayLabel = item.label.substring(0, 16) + (item.label.length > 16 ? '...' : '');
        legendGroup
          .append('text')
          .attr('x', 42)
          .attr('y', itemY + 2)
          .style('font-size', `${10 * legendFontScale}px`)
          .style('font-weight', item.visible ? '700' : '500')
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(displayLabel);

        legendGroup
          .append('text')
          .attr('x', 42)
          .attr('y', itemY + 14)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(`${item.percentage || item.value || 0}%`);
      });

      legendGroup
        .append('rect')
        .attr('x', legendMetrics.width - 16)
        .attr('y', legendMetrics.height - 16)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'rgba(37, 99, 235, 0.6)')
        .attr('rx', 3)
        .style('cursor', 'nwse-resize')
        .style('touch-action', 'none')
        .attr('tabindex', 0)
        .attr('role', 'slider')
        .attr('aria-label', t('ig.chart.legendResize'))
        .attr('aria-valuemin', MIN_LEGEND_WIDTH)
        .attr('aria-valuemax', width - MIN_MARGIN)
        .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
        .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

      decorateLegendGroup(legendGroup, legendMetrics);
    }

    console.log('✅ 饼图渲染完成');
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    highlightOptions.legend,
    legendBackgroundColor,
    resolvedLegendTextColor,
    resolvedLabelColor,
    settings?.colorAccessibilityPreset,
    startDrag,
    studentProfile
  ]);

  const renderScatterPlot = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis, legend } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    console.log('🔵🔵🔵 ===== renderScatterPlot 被调用 ===== 🔵🔵🔵');
    console.log('📏 SVG尺寸:', { width, height, margin });

    // 从后端数据中提取散点信息
    const actualGraphData = graphData.graph_data || graphData;
    const points = actualGraphData.elements?.filter(e => e.type === 'point' || e.type === 'scatter_point') || [];
    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);

    console.log('🔍 Scatter plot analysis:', {
      totalPoints: points.length,
      visibleElements: visibleElements,
      axesVisible: axesVisible
    });

    const xTickFontSize = axis?.xTickFontSize || 20;
    const yTickFontSize = axis?.yTickFontSize || 20;
    const xTickRotation = axis?.xTickRotation || 0;
    const xTickOffsetX = axis?.xTickOffsetX ?? 0;
    const xTickOffsetY = axis?.xTickOffsetY ?? 10;

    // 检查是否是最终阶段（显示所有数据且保持原色）
    const isShowAllStage = stageData.show_all_with_original_style === true;
    const visibleScatterIds = visibleElements.filter(id => id.startsWith('scatter_'));

    // 准备散点数据 - 后端返回的每个element包含一组points
    const defaultColors = getCUDPalette(settings?.colorAccessibilityPreset);

    const scatterData = points.map((pointGroup, index) => {
      const isVisible = visibleElements.includes(pointGroup.id);
      const isHighlighted = isVisible && (isShowAllStage || visibleScatterIds.length <= 1); // 只有一个散点系列可见时高亮

      // 从data_points数组中提取所有点
      const dataPoints = pointGroup.data_points || [];
      const normalizedPoints = dataPoints.reduce((acc, rawPoint) => {
        const xValue = Number(rawPoint.x);
        const yValue = Number(rawPoint.y);

        if (!Number.isFinite(xValue) || !Number.isFinite(yValue)) {
          return acc;
        }

        acc.push({
          x: xValue,
          y: yValue,
          visible: rawPoint.visible !== false
        });
        return acc;
      }, []);

      const baseColor = getElementColor(pointGroup.id, pointGroup.color || defaultColors[getConsistentIndexFromId(pointGroup.id) % defaultColors.length]);
      const focusStyle = getElementFocusStyle({
        baseColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || isVisible,
        isHighlighted,
        isShowAllStage,
        difficultyType,
        elementId: pointGroup.id
      });

      return {
        id: pointGroup.id,
        seriesName: pointGroup.label || t('ig.chart.groupN', { n: index + 1 }),
        color: baseColor,
        points: normalizedPoints,
        visible: isVisible,
        isHighlighted: isHighlighted,
        focusStyle,
        emoji: pointGroup.emoji || pointGroup.icon || pointGroup.symbol || null
      };
    });

    console.log('✅ Scatter data:', scatterData);

    // 计算数据范围
    const seriesForRange = (showData || isShowAllStage)
      ? scatterData.filter(series => (series.visible && series.points.length > 0) || isShowAllStage)
      : scatterData.filter(series => series.points.length > 0);
    const allVisiblePoints = seriesForRange.flatMap(series =>
      series.points.filter(point => point.visible !== false)
    );

    if (allVisiblePoints.length === 0) {
      if (seriesForRange.length === 0) {
        console.warn('⚠️ 当前阶段没有散点需要显示');
        return;
      }

      console.warn('⚠️ 可见散点数据为空');
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', '#ef4444')
        .text(t('ig.chart.noData'));
      return;
    }

    const xValues = allVisiblePoints.map(p => p.x);
    const yValues = allVisiblePoints.map(p => p.y);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    console.log('📊 Data range:', { xMin, xMax, yMin, yMax });

    // 添加10%的边距
    const xPadding = (xMax - xMin) * 0.1 || 1;
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const xScale = d3.scaleLinear()
      .domain([xMin - xPadding, xMax + xPadding])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice(5)
      .range([height - margin.bottom, margin.top]);

    // 绘制坐标轴
    if (axesVisible) {
      const yAxisGenerator = d3.axisLeft(yScale).ticks(5);
      if (!showTickLabels) {
        yAxisGenerator.tickFormat(() => '');
      }
      // Y轴
      const yAxis = svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxisGenerator)
        .style('font-size', `${yTickFontSize}px`);
      yAxis.selectAll('line').attr('stroke', '#64748b');
      yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      applyAxisTextStyles(yAxis.selectAll('text'), labelColor, axisWeight);
      applyAxisTextStyles(yAxis.selectAll('text'), labelColor, axisWeight);

      // X轴
      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(xScale).ticks(5))
        .style('font-size', `${xTickFontSize}px`);
      xAxis.selectAll('line').attr('stroke', '#64748b');
      xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      const xAxisTicks = xAxis.selectAll('text')
        .style('font-size', `${xTickFontSize}px`);
      applyAxisTextStyles(xAxisTicks, labelColor, axisWeight);
      if (!showTickLabels) {
        xAxisTicks.text('');
      } else if (xTickRotation !== 0) {
        xAxisTicks
          .style('text-anchor', 'end')
          .attr('transform', `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`);
      } else {
        xAxisTicks.attr('transform', null).style('text-anchor', 'middle');
      }

      // 获取轴标签
      const xAxisData = actualGraphData.elements?.find(e => e.id === 'axis_x');
      const yAxisData = actualGraphData.elements?.find(e => e.id === 'axis_y');

      const xAxisLabel = xAxisData?.label || t('ig.chart.xAxis');
      const yAxisLabel = yAxisData?.label || t('ig.chart.yAxis');

      console.log('🔍 Axis labels:', { xAxisLabel, yAxisLabel });

      if (showAxisLabels) {
        // X轴标签 + emoji
        const xLabelEmoji = getSmartEmojiForLabel(xAxisLabel);
        const xLabelGroup = svg.append('g')
          .attr('transform', `translate(${width / 2}, ${height - margin.bottom + 80})`);

        xLabelGroup.append('text')
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('fill', labelColor)
          .text(xLabelEmoji);

        xLabelGroup.append('text')
          .attr('y', 28)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(xAxisLabel);

        console.log(`✅ [散布図X軸] Emoji "${xLabelEmoji}" + ラベル "${xAxisLabel}" を追加完了`);

        // Y轴标签 + emoji
        const yLabelEmoji = getSmartEmojiForLabel(yAxisLabel);
        const yLabelGroup = svg.append('g')
          .attr('transform', `rotate(-90) translate(${-(height / 2)}, ${margin.left - 60})`);

        yLabelGroup.append('text')
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('fill', labelColor)
          .text(yLabelEmoji);

        yLabelGroup.append('text')
          .attr('x', 35)
          .attr('y', 5)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);

        console.log(`✅ [散布図Y軸] Emoji "${yLabelEmoji}" + ラベル "${yAxisLabel}" を追加完了`);
      }
    }

    // 颜色方案
    // 绘制散点
    if (showData) {
      scatterData.forEach((series, seriesIndex) => {
      if (!series.visible && !isShowAllStage) return;
      if (series.points.length === 0) return;

      const style = series.focusStyle;

      console.log(`🎨 Drawing series "${series.seriesName}" with color ${style.color}`);

      series.points.forEach(point => {
        if (point.visible === false) return;

        const finalX = xScale(point.x);
        const finalY = yScale(point.y);

        // 绘制散点
        const pointCircle = svg.append('circle')
          .datum({ point, series })
          .attr('data-element-id', series.id || 'scatter-' + seriesIndex)
          .attr('cx', shouldAnimate ? width / 2 : finalX)
          .attr('cy', shouldAnimate ? height / 2 : finalY)
          .attr('r', shouldAnimate ? 0 : (style.emphasis === 'focus' ? 8 : 5))
          .attr('fill', style.color)
          .attr('opacity', shouldAnimate ? 0 : style.opacity)
          .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', style.emphasis === 'keyword_highlight' ? 6 : (style.emphasis === 'focus' ? 2 : 1.5))
          .classed('keyword-highlight-blink', style.emphasis === 'keyword_highlight');

        if (shouldAnimate) {
          pointCircle
            .transition()
            .duration(800)
            .delay(Math.random() * 300)
            .ease(d3.easeCubicOut)
            .attr('cx', finalX)
            .attr('cy', finalY)
            .attr('r', style.emphasis === 'focus' ? 8 : 5)
            .attr('opacity', style.opacity);
        }

        attachInteractiveHandlers(pointCircle, {
          getElementId: (d) => d.series?.id || d.point?.id,
          getPayload: ({ point, series }) => ({
            title: series.seriesName,
            subtitle: `(${point.x}, ${point.y})`,
            detailLines: [],
            icon: series.emoji || getSmartEmojiForLabel(series.seriesName)
          }),
          isInteractive: () => isShowAllStage || series.visible,
          onDrilldown: ({ point, series }) => ({
            title: t('ig.chart.detail', { label: series.seriesName }),
            description: `X=${point.x}, Y=${point.y}`,
            detailLines: [
              stageData?.stage_summary ? stageData.stage_summary : null
            ].filter(Boolean),
            icon: series.emoji || getSmartEmojiForLabel(series.seriesName),
            variant: 'scatter'
          })
        });

        // 添加数值标签（在点上方显示坐标）。A' 2026-06-15: render ALWAYS (hidden unless
        // focused) so a grounded show_value_tags overlay can reveal+bold them at upload;
        // the handler sets display:null to reveal. No global clutter for non-overlay students.
        const pointLabel = svg.append('text')
          .attr('data-value-for', series.id)
          .attr('x', finalX)
          .attr('y', finalY - 10)
          .attr('text-anchor', 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('font-weight', 'bold')
          .style('fill', labelColor)
          .text(`(${point.x.toFixed(1)}, ${point.y.toFixed(1)})`);
        if (style.emphasis !== 'focus') {
          // hidden; inline opacity not set (SVG default is 1) so reveal via display:null
          // shows it at full opacity without animation.
          pointLabel.style('display', 'none');
        } else {
          pointLabel.style('opacity', shouldAnimate ? 0 : 1);
          if (shouldAnimate) {
            pointLabel
              .transition()
              .delay(800 + Math.random() * 300)
              .duration(300)
              .style('opacity', 1);
          }
        }
      });
      });
    }

    const visibleSeries = showLegend
      ? scatterData.filter((series) => series.points.length > 0)
      : scatterData.filter(s => ((s.visible && s.points.length > 0) || isShowAllStage));
    if (showLegend && visibleSeries.length > 0) {
      const visibleCount = Math.min(visibleSeries.length, 8);
      const cappedVScale = legendVScaleCapped(layout, visibleCount, 26, 22);
      const legendMetrics = getLegendMetrics(layout, visibleCount, {
        defaultWidth: legend?.width || 140,
        rowHeight: 26 * cappedVScale,
        extraHeight: 36,
        bottomOffset: 34,
        maxItems: 10
      });
      legendMetricsRef.current = legendMetrics;

      console.log('✅ 开始绘制散点图图例');

      const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);
      const legendGroup = svg.append('g')
        .attr('class', 'scatter-legend')
        .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

      legendGroup.append('rect')
        .attr('width', legendMetrics.width)
        .attr('height', legendMetrics.height)
        .attr('fill', legendBgColor)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 2)
        .attr('rx', 8)
        .style('cursor', 'move')
        .style('touch-action', 'none')
        .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

      legendGroup.append('text')
        .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
        .attr('y', 0)
        .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', highlightOptions.legend ? '800' : 'bold')
        .style('fill', legendTextColor)
        .style('text-shadow', legendHighlightShadow)
        .text(`🔵 ${t('ig.chart.legend')}`);

      visibleSeries.slice(0, 8).forEach((series, i) => {
        const itemY = (22 + i * 26) * cappedVScale;
        const seriesColor = series.color || defaultColors[i % defaultColors.length];

        legendGroup.append('circle')
          .attr('cx', 10)
          .attr('cy', itemY)
          .attr('r', 6)
          .attr('fill', seriesColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5);

        const emoji = getSmartEmojiForLabel({
          label: series.seriesName,
          emoji: series.emoji
        });

        legendGroup.append('text')
          .attr('x', 24)
          .attr('y', itemY + 4)
          .style('font-size', `${12 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .text(emoji);

        const displayLabel = series.seriesName.substring(0, 16) + (series.seriesName.length > 16 ? '...' : '');
        legendGroup.append('text')
          .attr('x', 42)
          .attr('y', itemY + 2)
          .style('font-size', `${10 * legendFontScale}px`)
          .style('font-weight', 'bold')
          .style('fill', legendTextColor)
          .text(displayLabel);

        const pointCount = series.points.filter(p => p.visible !== false).length;
        legendGroup.append('text')
          .attr('x', 42)
          .attr('y', itemY + 14)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .text(t('ig.chart.pointCount', { count: pointCount }));
      });

      legendGroup.append('rect')
        .attr('x', legendMetrics.width - 16)
        .attr('y', legendMetrics.height - 16)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'rgba(37, 99, 235, 0.6)')
        .attr('rx', 3)
        .style('cursor', 'nwse-resize')
        .style('touch-action', 'none')
        .attr('tabindex', 0)
        .attr('role', 'slider')
        .attr('aria-label', t('ig.chart.legendResize'))
        .attr('aria-valuemin', MIN_LEGEND_WIDTH)
        .attr('aria-valuemax', width - MIN_MARGIN)
        .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
        .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

      decorateLegendGroup(legendGroup, legendMetrics);
    }

    // ── トレンドバンド描画（散布図） ──────────────────────────────────────────
    if (stageData.show_trend_band && allVisiblePoints.length > 2) {
      const n = allVisiblePoints.length;
      const sumX = allVisiblePoints.reduce((s, p) => s + p.x, 0);
      const sumY = allVisiblePoints.reduce((s, p) => s + p.y, 0);
      const sumXY = allVisiblePoints.reduce((s, p) => s + p.x * p.y, 0);
      const sumX2 = allVisiblePoints.reduce((s, p) => s + p.x * p.x, 0);
      const denom = n * sumX2 - sumX * sumX;
      if (Math.abs(denom) > 1e-10) {
        const slope = (n * sumXY - sumX * sumY) / denom;
        const intercept = (sumY - slope * sumX) / n;
        // Standard error for band width
        const residuals = allVisiblePoints.map(p => p.y - (slope * p.x + intercept));
        const stdErr = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / Math.max(n - 2, 1));
        const bandWidth = stdErr * 1.5; // 1.5× standard error band
        // Generate band path points
        const xDomain = xScale.domain();
        const bandSteps = 40;
        const stepSize = (xDomain[1] - xDomain[0]) / bandSteps;
        const upperPoints = [];
        const lowerPoints = [];
        for (let i = 0; i <= bandSteps; i++) {
          const xVal = xDomain[0] + i * stepSize;
          const yVal = slope * xVal + intercept;
          upperPoints.push({ x: xScale(xVal), y: yScale(yVal + bandWidth) });
          lowerPoints.push({ x: xScale(xVal), y: yScale(yVal - bandWidth) });
        }
        // Draw band (filled area)
        const areaPoints = [...upperPoints, ...lowerPoints.reverse()];
        const bandOpacity = stageData.trend_band_opacity || 0.3;
        svg.append('path')
          .datum(areaPoints)
          .attr('d', d3.line().x(d => d.x).y(d => d.y).curve(d3.curveMonotoneX))
          .attr('fill', '#ef4444')
          .attr('fill-opacity', bandOpacity * 0.4)
          .attr('stroke', '#ef4444')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', bandOpacity)
          .attr('pointer-events', 'none');
        // Draw center regression line
        const centerLine = d3.line().x(d => d.x).y(d => d.y).curve(d3.curveMonotoneX);
        const centerPoints = [];
        for (let i = 0; i <= bandSteps; i++) {
          const xVal = xDomain[0] + i * stepSize;
          const yVal = slope * xVal + intercept;
          centerPoints.push({ x: xScale(xVal), y: yScale(yVal) });
        }
        svg.append('path')
          .datum(centerPoints)
          .attr('d', centerLine)
          .attr('fill', 'none')
          .attr('stroke', '#ef4444')
          .attr('stroke-width', 2)
          .attr('opacity', 0.8)
          .attr('pointer-events', 'none');
      }
    }

    // ── Tier 2 overlay rendering (scatter) ──
    const _t2gridS = tier2ActionsRef.current.find(a => a.type === 'show_grid');
    if (_t2gridS) {
      yScale.ticks(5).forEach(tick => {
        svg.append('line').attr('class', 'overlay-grid-line')
          .attr('x1', margin.left).attr('x2', width - margin.right)
          .attr('y1', yScale(tick)).attr('y2', yScale(tick))
          .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
      });
      const xTicksS = xScale.ticks ? xScale.ticks(5) : [];
      xTicksS.forEach(tick => {
        svg.append('line').attr('class', 'overlay-grid-line')
          .attr('x1', xScale(tick)).attr('x2', xScale(tick))
          .attr('y1', margin.top).attr('y2', height - margin.bottom)
          .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
      });
    }
    const _t2projS = tier2ActionsRef.current.find(a => a.type === 'show_projection_lines');
    if (_t2projS) {
      const targetIds = new Set(_t2projS.element_ids || []);
      // Ghost-surface guard — see renderBarChart's _t2proj block (same factory
      // + context as applyOverlay; axis nodes pass via rule 5; read-only).
      const effectiveVisible = createEffectiveVisibilityPredicate({
        ...overlayVisibilityContextRef.current,
        elements: actualGraphData?.elements || [],
      });
      svg.selectAll('[data-element-id]').each(function() {
        const el = d3.select(this);
        const id = el.attr('data-element-id');
        if ((targetIds.size === 0 || targetIds.has(id)) && effectiveVisible(id)) {
          const bbox = this.getBBox();
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y;
          svg.append('line').attr('class', 'overlay-projection')
            .attr('x1', margin.left).attr('x2', cx).attr('y1', cy).attr('y2', cy)
            .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
          svg.append('line').attr('class', 'overlay-projection')
            .attr('x1', cx).attr('x2', cx).attr('y1', cy).attr('y2', height - margin.bottom)
            .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
        }
      });
    }
    const _t2pinS = tier2ActionsRef.current.find(a => a.type === 'pin_reference_value');
    if (_t2pinS && _t2pinS.params) {
      const refValue = _t2pinS.params.reference_value;
      if (refValue !== undefined && yScale) {
        const yPos = yScale(refValue);
        svg.append('line').attr('class', 'overlay-pin-reference')
          .attr('x1', margin.left).attr('x2', width - margin.right)
          .attr('y1', yPos).attr('y2', yPos)
          .attr('stroke', '#843F1F').attr('stroke-dasharray', '6,3').attr('stroke-width', 1.5);
        svg.append('text').attr('class', 'overlay-pin-label')
          .attr('x', width - margin.right + 4).attr('y', yPos + 4)
          .style('font-size', `${20 * vlScale(layout)}px`).style('fill', 'var(--accent)').style('font-weight', 'bold')
          .text(refValue);
      }
    }

    console.log('✅ 散点图渲染完成');
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    legendVisualContext,
    highlightOptions.legend,
    resolvedLabelColor,
    startDrag,
    studentProfile
  ]);

  const renderHistogram = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis, legend } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    console.log('📊📊📊 ===== renderHistogram 被调用 ===== 📊📊📊');
    console.log('📏 SVG尺寸:', { width, height, margin });

    const chartMargin = {
      top: typeof margin.top === 'number' ? margin.top : 60,
      right: typeof margin.right === 'number' ? margin.right : 60,
      bottom: typeof margin.bottom === 'number' ? margin.bottom : 180,
      left: typeof margin.left === 'number' ? margin.left : 100
    };
    const xTickFontSize = axis?.xTickFontSize || 20;
    const yTickFontSize = axis?.yTickFontSize || 20;
    const xTickRotation = axis?.xTickRotation || 0;
    const xTickOffsetX = axis?.xTickOffsetX ?? 0;
    const xTickOffsetY = axis?.xTickOffsetY ?? 10;

    // 从后端数据中提取直方图区间信息
    const actualGraphData = graphData.graph_data || graphData;
    const bins = actualGraphData.elements?.filter(e => e.type === 'histogram_bar') || [];
    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
    const isShowAllStage = stageData.show_all_with_original_style === true;

    const axisXData = actualGraphData.elements?.find(e => e.id === 'axis_x');
    const axisYData = actualGraphData.elements?.find(e => e.id === 'axis_y');
    const xAxisLabel = axisXData?.label || t('ig.chart.bin');
    const yAxisLabel = axisYData?.label || t('ig.chart.frequency');

    console.log('🔍 Histogram analysis:', {
      totalBins: bins.length,
      visibleElements: visibleElements,
      axesVisible: axesVisible,
      adjustedMargin: chartMargin,
      legendPosition: legend?.position || 'right'
    });

    // 准备数据
    const data = bins.map((bin, i) => {
      const binRange = bin.bin_range || [i * 10, (i + 1) * 10];
      const frequency = bin.frequency || bin.data_points?.[0]?.y || 0;
      const label = bin.label || `${binRange[0]}-${binRange[1]}`;

      return {
        id: bin.id,
        label,
        binRange,
        frequency,
        color: bin.color || null,
        visible: visibleElements.includes(bin.id),
        isHighlighted: visibleElements.includes(bin.id) && !visibleElements.includes('axis_x')
      };
    });

    console.log('✅ Histogram data:', data);

    // 检查是否有数据
    if (data.length === 0) {
      console.warn('⚠️ 没有直方图数据');
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', '#ef4444')
        .text(t('ig.chart.noData'));
      return;
    }

    // X轴比例（区间）
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.label))
      .range([chartMargin.left, width - chartMargin.right])
      .padding(0); // 直方图无间隙

    // Y轴比例（频度）
    const maxFrequency = d3.max(data, d => d.frequency) || 10;
    const yScale = d3.scaleLinear()
      .domain([0, maxFrequency])
      .nice(5)
      .range([height - chartMargin.bottom, chartMargin.top]);

    // 绘制Y轴
    if (axesVisible) {
      const yAxisGenerator = d3.axisLeft(yScale).ticks(5);
      if (!showTickLabels) {
        yAxisGenerator.tickFormat(() => '');
      }
      const yAxis = svg.append('g')
        .attr('transform', `translate(${chartMargin.left},0)`)
        .call(yAxisGenerator)
        .style('font-size', `${yTickFontSize}px`);

      yAxis.selectAll('line').attr('stroke', '#64748b');
      yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      applyAxisTextStyles(yAxis.selectAll('text'), labelColor, axisWeight);

      if (showAxisLabels) {
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(height / 2))
          .attr('y', chartMargin.left - 60)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);
      }
    }

    // 绘制X轴
    if (axesVisible) {
      const xAxis = svg.append('g')
        .attr('transform', `translate(0,${height - chartMargin.bottom})`)
        .call(d3.axisBottom(xScale))
        .style('font-size', `${xTickFontSize}px`);

      xAxis.selectAll('line').attr('stroke', '#64748b');
      xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

      const xAxisText = xAxis.selectAll('text')
        .style('font-size', `${xTickFontSize}px`);
      applyAxisTextStyles(xAxisText, labelColor, axisWeight);

      if (!showTickLabels) {
        xAxisText.text('');
      } else if (xTickRotation !== 0) {
        xAxisText
          .style('text-anchor', 'end')
          .attr('transform', `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`);
      } else {
        xAxisText
          .style('text-anchor', 'middle')
          .attr('transform', `translate(0, ${xTickOffsetY})`);
      }

      if (showAxisLabels) {
        const axisLabelY = height - chartMargin.bottom + 120;
        svg.append('text')
          .attr('x', width / 2)
          .attr('y', axisLabelY)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(xAxisLabel);
      }
    }

    const defaultColor = '#4A90E2';
    data.forEach((bin, index) => {
      const assignedColor = getElementColor(bin.id, bin.color || stageData.highlight_color || defaultColor);
      bin.baseColor = assignedColor;
      bin.focusStyle = getElementFocusStyle({
        baseColor: assignedColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || bin.visible,
        isHighlighted: bin.isHighlighted,
        isShowAllStage,
        difficultyType,
        elementId: bin.id
      });
    });

    const histogramData = showData ? data : [];
    const barGroups = svg.selectAll('.histogram-bar-group')
      .data(histogramData)
      .enter()
      .append('g')
      .attr('class', 'histogram-bar-group');

    // 柱子矩形
    const histogramRects = barGroups.append('rect')
      .attr('class', 'histogram-bar')
      .attr('data-element-id', (d, i) => d.id || 'histogram-' + i)
      .attr('x', d => xScale(d.label))
      .attr('y', d => shouldAnimate ? height - chartMargin.bottom : yScale(d.frequency))
      .attr('width', xScale.bandwidth())
      .attr('height', d => shouldAnimate ? 0 : (height - chartMargin.bottom - yScale(d.frequency)))
      .attr('fill', d => d.focusStyle.color)
      .attr('opacity', d => d.focusStyle.opacity)
      .attr('stroke', d => {
        if (d.focusStyle.emphasis === 'keyword_highlight') return '#FFD700';
        if (d.focusStyle.emphasis === 'focus') return DEFAULT_STAT_OUTLINE_COLOR;
        if (d.focusStyle.emphasis === 'hidden') return '#e2e8f0';
        return DEFAULT_STAT_OUTLINE_COLOR;
      })
      .attr('stroke-width', d => {
        if (d.focusStyle.emphasis === 'keyword_highlight') return 7;
        if (d.focusStyle.emphasis === 'focus') return 2;
        if (d.focusStyle.emphasis === 'hidden') return 0.5;
        return 1;
      })
      .classed('keyword-highlight-blink', d => d.focusStyle.emphasis === 'keyword_highlight');

    if (shouldAnimate) {
      histogramRects
        .transition()
        .duration(800)
        .ease(d3.easeCubicOut)
        .attr('y', d => yScale(d.frequency))
        .attr('height', d => height - chartMargin.bottom - yScale(d.frequency));
    }

    attachInteractiveHandlers(histogramRects, {
      getElementId: (d) => d.id,
      getPayload: (bin) => ({
        title: bin.label,
        subtitle: t('ig.chart.frequencyColon', { value: bin.frequency }),
        detailLines: [
          t('ig.chart.rangeColon', { value: bin.binRange.join(' - ') })
        ].filter(Boolean),
        icon: '📊'
      }),
      isInteractive: (bin) => isShowAllStage || bin.visible,
      onDrilldown: (bin) => ({
        title: t('ig.chart.detail', { label: bin.label }),
        description: t('ig.chart.binDataDesc', { label: bin.label, frequency: bin.frequency }),
        detailLines: [
          t('ig.chart.rangeColon', { value: bin.binRange.join(' - ') }),
          stageData?.stage_summary ? stageData.stage_summary : null
        ].filter(Boolean),
        icon: '📊',
        variant: 'histogram'
      })
    });

    // 频度标签
    barGroups.append('text')
      .attr('class', 'frequency-label')
      .attr('data-value-for', (d) => d.id)
      .attr('x', d => xScale(d.label) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.frequency) - 12)
      .attr('text-anchor', 'middle')
      .style('font-size', d => `${(d.focusStyle.emphasis === 'focus' ? 24 : 20) * vlScale(layout)}px`)
      .style('font-weight', d => d.focusStyle.emphasis === 'focus' ? 'bold' : (highlightOptions.labels ? '700' : '600'))
      .style('fill', labelColor)
      .style('opacity', d => shouldAnimate ? 0 : d.focusStyle.opacity)
      .text(d => d.frequency)
      .call((selection) => {
        if (shouldAnimate) {
          selection
            .transition()
            .duration(800)
            .style('opacity', d => d.focusStyle.opacity);
        }
      });

    const legendBins = showLegend
      ? data
      : (isShowAllStage ? data : data.filter((bin) => bin.visible));
    if (showLegend && legendBins.length > 1) {
      const visibleCount = Math.min(legendBins.length, 10);
      const cappedVScale = legendVScaleCapped(layout, visibleCount, 18, 16);
      const legendMetrics = getLegendMetrics(layout, visibleCount, {
        defaultWidth: legend?.width || 140,
        rowHeight: 18 * cappedVScale,
        extraHeight: 34,
        bottomOffset: 32,
        maxItems: 10
      });
      legendMetricsRef.current = legendMetrics;
      const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);

      const legendGroup = svg.append('g')
        .attr('class', 'histogram-legend')
        .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

      legendGroup.append('rect')
        .attr('width', legendMetrics.width)
        .attr('height', legendMetrics.height)
        .attr('fill', legendBgColor)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 2)
        .attr('rx', 8)
        .style('cursor', 'move')
        .style('touch-action', 'none')
        .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

      legendGroup.append('text')
        .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
        .attr('y', 0)
        .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', highlightOptions.legend ? '800' : 'bold')
        .style('fill', legendTextColor)
        .style('text-shadow', legendHighlightShadow)
        .text(`📊 ${t('ig.chart.bin')}`);

      legendBins.slice(0, 10).forEach((bin, index) => {
        const entryStyle = bin.focusStyle || {};
        const entryOpacity = entryStyle.opacity ?? 1;
        const swatchColor = entryStyle.strokeColor || bin.baseColor || '#94a3b8';
        const emoji = getSmartEmojiForLabel(bin);
        const itemY = (16 + index * 18) * cappedVScale;

        legendGroup.append('rect')
          .attr('x', 6)
          .attr('y', itemY - 10)
          .attr('width', 12)
          .attr('height', 12)
          .attr('rx', 3)
          .attr('fill', swatchColor)
          .attr('opacity', entryOpacity);

        legendGroup.append('text')
          .attr('x', 28)
          .attr('y', itemY + 3)
          .style('font-size', `${12 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(emoji);

        const displayLabel = bin.label.substring(0, 18) + (bin.label.length > 18 ? '...' : '');
        legendGroup.append('text')
          .attr('x', 46)
          .attr('y', itemY - 1)
          .style('font-size', `${10 * legendFontScale}px`)
          .style('font-weight', '600')
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(displayLabel);

        legendGroup.append('text')
          .attr('x', 46)
          .attr('y', itemY + 11)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(t('ig.chart.frequencyColon', { value: bin.frequency }));
      });

      legendGroup.append('rect')
        .attr('x', legendMetrics.width - 16)
        .attr('y', legendMetrics.height - 16)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'rgba(37, 99, 235, 0.6)')
        .attr('rx', 3)
        .style('cursor', 'nwse-resize')
        .style('touch-action', 'none')
        .attr('tabindex', 0)
        .attr('role', 'slider')
        .attr('aria-label', t('ig.chart.legendResize'))
        .attr('aria-valuemin', MIN_LEGEND_WIDTH)
        .attr('aria-valuemax', width - MIN_MARGIN)
        .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
        .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

      decorateLegendGroup(legendGroup, legendMetrics);
    }

    // トレンド線描画（実線）
    if (stageData.show_trend_line && data.length > 1) {
      const trendPoints = data.map(d => ({
        x: xScale(d.label) + xScale.bandwidth() / 2,
        y: yScale(d.frequency)
      }));
      const lineGen = d3.line()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveMonotoneX);
      svg.append('path')
        .datum(trendPoints)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 2)
        .attr('opacity', 0.8);
    }

    // ── Tier 2 overlay rendering (histogram) ──
    const _t2gridH = tier2ActionsRef.current.find(a => a.type === 'show_grid');
    if (_t2gridH) {
      yScale.ticks(5).forEach(tick => {
        svg.append('line').attr('class', 'overlay-grid-line')
          .attr('x1', chartMargin.left).attr('x2', width - chartMargin.right)
          .attr('y1', yScale(tick)).attr('y2', yScale(tick))
          .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
      });
      const xTicksH = xScale.domain();
      xTicksH.forEach(tick => {
        const xPos = xScale(tick) + xScale.bandwidth() / 2;
        svg.append('line').attr('class', 'overlay-grid-line')
          .attr('x1', xPos).attr('x2', xPos)
          .attr('y1', chartMargin.top).attr('y2', height - chartMargin.bottom)
          .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
      });
    }
    const _t2pinH = tier2ActionsRef.current.find(a => a.type === 'pin_reference_value');
    if (_t2pinH && _t2pinH.params) {
      const refValue = _t2pinH.params.reference_value;
      if (refValue !== undefined && yScale) {
        const yPos = yScale(refValue);
        svg.append('line').attr('class', 'overlay-pin-reference')
          .attr('x1', chartMargin.left).attr('x2', width - chartMargin.right)
          .attr('y1', yPos).attr('y2', yPos)
          .attr('stroke', '#843F1F').attr('stroke-dasharray', '6,3').attr('stroke-width', 1.5);
        svg.append('text').attr('class', 'overlay-pin-label')
          .attr('x', width - chartMargin.right + 4).attr('y', yPos + 4)
          .style('font-size', `${20 * vlScale(layout)}px`).style('fill', 'var(--accent)').style('font-weight', 'bold')
          .text(refValue);
      }
    }
    const _t2simpH = tier2ActionsRef.current.find(a => a.type === 'simplify_labels');
    if (_t2simpH && _t2simpH.params && _t2simpH.params.label_map) {
      const labelMap = _t2simpH.params.label_map;
      svg.selectAll('.tick text, .axis text').each(function() {
        const el = d3.select(this);
        const text = el.text();
        if (labelMap[text]) { el.text(labelMap[text]); }
        else if (text.length > 8) { el.text(text.substring(0, 7) + '\u2026'); }
      });
    }

    console.log('✅ 直方图渲染完成');
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    highlightOptions.labels,
    highlightOptions.legend,
    legendVisualContext,
    resolvedLabelColor,
    startDrag,
    studentProfile
  ]);

  const renderBoxPlot = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis, legend } = layout;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    console.log('📦📦📦 ===== renderBoxPlot 被调用 ===== 📦📦📦');
    console.log('📏 SVG尺寸:', { width, height, margin });

    // 从后端数据中提取箱线图信息
    const actualGraphData = graphData.graph_data || graphData;
    const boxes = actualGraphData.elements?.filter(e => e.type === 'box') || [];
    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const isShowAllStage = stageData.show_all_with_original_style === true;
    const visibleBoxIds = visibleElements.filter(id => id.startsWith('box_'));
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);

    console.log('🔍 Box plot analysis:', {
      totalBoxes: boxes.length,
      visibleElements: visibleElements,
      axesVisible: axesVisible
    });

    // 准备数据
    const data = boxes.map((box, i) => {
      const quartileKeys = ['min', 'q1', 'median', 'q3', 'max'];
      const rawQuartiles = box.quartiles || {};
      const normalizedQuartiles = {};
      let hasQuartiles = true;

      quartileKeys.forEach(key => {
        const rawValue = rawQuartiles[key];
        const numericValue = rawValue === undefined || rawValue === null || rawValue === ''
          ? NaN
          : Number(rawValue);
        if (Number.isFinite(numericValue)) {
          normalizedQuartiles[key] = numericValue;
        } else {
          hasQuartiles = false;
        }
      });

      const outliers = Array.isArray(box.outliers)
        ? box.outliers
            .map(value => {
              if (value === undefined || value === null || value === '') {
                return null;
              }
              const numericValue = Number(value);
              return Number.isFinite(numericValue) ? numericValue : null;
            })
            .filter(value => value !== null)
        : [];

      const baseColor = getElementColor(box.id, box.color || stageData.highlight_color || '#50C878');
      const focusStyle = getElementFocusStyle({
        baseColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || visibleElements.includes(box.id),
        isHighlighted: visibleElements.includes(box.id) && (isShowAllStage || visibleBoxIds.length <= 1),
        isShowAllStage,
        difficultyType,
        elementId: box.id
      });

      return {
        id: box.id,
        label: box.label || t('ig.chart.datasetN', { n: i + 1 }),
        quartiles: normalizedQuartiles,
        outliers: outliers,
        hasQuartiles,
        color: baseColor,
        visible: visibleElements.includes(box.id),
        isHighlighted: visibleElements.includes(box.id) && (isShowAllStage || visibleBoxIds.length <= 1),
        focusStyle
      };
    });

    console.log('✅ Box plot data:', data);

    const validBoxes = data.filter(d => d.hasQuartiles);
    if (validBoxes.length === 0) {
      console.warn('⚠️ 箱线图缺少 quartiles 数据，无法渲染');
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', '#ef4444')
        .text(t('ig.chart.noData'));
      return;
    }

    const invalidBoxes = data.filter(d => !d.hasQuartiles);
    if (invalidBoxes.length > 0) {
      console.warn('⚠️ 以下箱线图元素缺少 quartiles:', invalidBoxes.map(b => b.id));
    }

    // X轴比例（类别）
    const xScale = d3.scaleBand()
      .domain(validBoxes.map(d => d.label))
      .range([margin.left, width - margin.right])
      .padding(0.3);

    // Y轴比例（数值）
    const allValues = validBoxes.flatMap(d => [
      d.quartiles.min,
      d.quartiles.q1,
      d.quartiles.median,
      d.quartiles.q3,
      d.quartiles.max,
      ...d.outliers
    ]);
    const yMin = Math.min(...allValues);
    const yMax = Math.max(...allValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const yScale = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice(8)
      .range([height - margin.bottom, margin.top]);

    // 绘制Y轴
    const xTickFontSize = axis?.xTickFontSize || 20;
    const xTickRotation = axis?.xTickRotation || 0;
    const xTickOffsetX = axis?.xTickOffsetX ?? 0;
    const xTickOffsetY = axis?.xTickOffsetY ?? 10;
    const yTickFontSize = axis?.yTickFontSize || 20;

    if (axesVisible) {
      const yAxisGenerator = d3.axisLeft(yScale).ticks(8);
      if (!showTickLabels) {
        yAxisGenerator.tickFormat(() => '');
      }
      const yAxis = svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxisGenerator)
        .style('font-size', `${yTickFontSize}px`);

      yAxis.selectAll('line').attr('stroke', '#64748b');
      yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      applyAxisTextStyles(yAxis.selectAll('text'), labelColor, axisWeight);

      if (showAxisLabels) {
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(height / 2))
          .attr('y', margin.left - 50)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(t('ig.chart.value'));
      }
    }

    // 绘制X轴
    if (axesVisible) {
      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(xScale))
        .style('font-size', `${xTickFontSize}px`);

      xAxis.selectAll('line').attr('stroke', '#64748b');
      xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

      const tickTexts = xAxis.selectAll('text');
      applyAxisTextStyles(tickTexts, labelColor, axisWeight);
      if (!showTickLabels) {
        tickTexts.text('');
      } else if (xTickRotation !== 0) {
        tickTexts
          .attr('text-anchor', 'end')
          .attr('transform', `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`);
      } else {
        tickTexts
          .attr('text-anchor', 'middle')
          .attr('dy', `${xTickOffsetY}px`);
      }
    }

    // 绘制箱线图
    const boxWidth = Math.min(xScale.bandwidth() * 0.6, 80);

    if (showData) {
      data.forEach((d, i) => {
      if (!d.hasQuartiles) return;
      if (!d.visible && !isShowAllStage) return;

      const centerX = xScale(d.label) + xScale.bandwidth() / 2;
      const style = d.focusStyle;
      const fillColor = style.color;
      const opacity = style.opacity;

      // 绘制须（min到q1，q3到max）
      // 下须：min到q1
      svg.append('line')
        .attr('x1', centerX)
        .attr('x2', centerX)
        .attr('y1', yScale(d.quartiles.min))
        .attr('y2', yScale(d.quartiles.q1))
        .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
        .attr('stroke-width', style.emphasis === 'focus' ? 3 : 2)
        .attr('opacity', opacity);

      // 上须：q3到max
      svg.append('line')
        .attr('x1', centerX)
        .attr('x2', centerX)
        .attr('y1', yScale(d.quartiles.q3))
        .attr('y2', yScale(d.quartiles.max))
        .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
        .attr('stroke-width', style.emphasis === 'focus' ? 3 : 2)
        .attr('opacity', opacity);

      // 须的端点线
      const whiskerWidth = boxWidth * 0.5;
      [d.quartiles.min, d.quartiles.max].forEach(value => {
        svg.append('line')
          .attr('x1', centerX - whiskerWidth / 2)
          .attr('x2', centerX + whiskerWidth / 2)
          .attr('y1', yScale(value))
          .attr('y2', yScale(value))
          .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', style.emphasis === 'focus' ? 3 : 2)
          .attr('opacity', opacity);
      });

      // 绘制箱（q1到q3）
      const boxHeight = yScale(d.quartiles.q1) - yScale(d.quartiles.q3);
      const boxRect = svg.append('rect')
        .datum(d)
        .attr('data-element-id', d.id || 'box-' + i)
        .attr('x', centerX - boxWidth / 2)
        .attr('y', yScale(d.quartiles.q3))
        .attr('width', boxWidth)
        .attr('height', boxHeight)
        .attr('fill', fillColor)
        .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
        .attr('stroke-width', style.emphasis === 'focus' ? 3 : 2)
        .attr('opacity', opacity)
        .attr('rx', 4);

      attachInteractiveHandlers(boxRect, {
        getElementId: (d) => d.id,
        getPayload: (boxData) => ({
          title: boxData.label,
          subtitle: t('ig.chart.medianColon', { value: boxData.quartiles.median }),
          detailLines: [
            `Q1: ${boxData.quartiles.q1}`,
            `Q3: ${boxData.quartiles.q3}`
          ],
          icon: '🧰'
        }),
        isInteractive: () => isShowAllStage || d.visible,
        onDrilldown: () => ({
          title: t('ig.chart.detail', { label: d.label }),
          description: t('ig.chart.boxMinMaxDesc', { min: d.quartiles.min, max: d.quartiles.max }),
          detailLines: [
            stageData?.stage_summary ? stageData.stage_summary : null
          ].filter(Boolean),
          icon: '🧰',
          variant: 'box'
        })
      });

      // 绘制中位数线
      svg.append('line')
        .attr('x1', centerX - boxWidth / 2)
        .attr('x2', centerX + boxWidth / 2)
        .attr('y1', yScale(d.quartiles.median))
        .attr('y2', yScale(d.quartiles.median))
        .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
        .attr('stroke-width', style.emphasis === 'focus' ? 4 : 3);

      // 绘制离群值
      d.outliers.forEach(outlier => {
        const outlierCircle = svg.append('circle')
          .datum({ outlier, box: d })
          .attr('cx', centerX)
          .attr('cy', yScale(outlier))
          .attr('r', style.emphasis === 'focus' ? 6 : 4)
          .attr('fill', 'none')
          .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', style.emphasis === 'focus' ? 3 : 2)
          .attr('opacity', opacity);

        attachInteractiveHandlers(outlierCircle, {
          getElementId: (d) => d.box?.id,
          getPayload: ({ outlier, box }) => ({
            title: t('ig.chart.outlierTitle', { label: box.label }),
            subtitle: `${outlier}`,
            detailLines: [],
            icon: '📍'
          }),
          isInteractive: () => isShowAllStage || d.visible,
          onDrilldown: ({ outlier, box }) => ({
            title: t('ig.chart.outlierShort', { label: box.label }),
            description: t('ig.chart.outlierDesc', { value: outlier }),
            detailLines: [
              stageData?.stage_summary ? stageData.stage_summary : null
            ].filter(Boolean),
            icon: '📍',
            variant: 'box-outlier'
          })
        });
      });

      // 数値ラベル（五数）: show_value_tags overlay target. Tagged with a SEPARATE
      // data-value-for=<box id> attribute (NOT data-element-id, so the dim/focus
      // keep-walk and tier2-ghost TC-AX axis-walk stay clean). Always appended; visible
      // when focused, display:none otherwise so the show_value_tags handler can reveal them.
      const boxValueId = d.id || ('box-' + i);
      const labels = [
        { value: d.quartiles.max, text: t('ig.chart.maxColon', { value: d.quartiles.max }) },
        { value: d.quartiles.q3, text: `Q3: ${d.quartiles.q3}` },
        { value: d.quartiles.median, text: t('ig.chart.medianColon', { value: d.quartiles.median }) },
        { value: d.quartiles.q1, text: `Q1: ${d.quartiles.q1}` },
        { value: d.quartiles.min, text: t('ig.chart.minColon', { value: d.quartiles.min }) }
      ];
      labels.forEach(label => {
        const _bvlt = svg.append('text')
          .attr('data-value-for', boxValueId)
          .attr('x', centerX + boxWidth / 2 + 10)
          .attr('y', yScale(label.value) + 5)
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('font-weight', 'bold')
          .style('fill', labelColor)
          .text(label.text);
        if (style.emphasis !== 'focus') _bvlt.style('display', 'none');
      });
      });
    }

    const legendBoxes = showLegend
      ? data
      : (isShowAllStage ? data : data.filter((box) => box.visible));
    if (showLegend && legendBoxes.length > 0) {
      const visibleCount = Math.min(legendBoxes.length, 8);
      const cappedVScale = legendVScaleCapped(layout, visibleCount, 18, 16);
      const legendMetrics = getLegendMetrics(layout, visibleCount, {
        defaultWidth: legend?.width || 140,
        rowHeight: 18 * cappedVScale,
        extraHeight: 34,
        bottomOffset: 34,
        maxItems: 8
      });
      legendMetricsRef.current = legendMetrics;
      const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);

      const legendGroup = svg.append('g')
        .attr('class', 'box-legend')
        .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

      legendGroup.append('rect')
        .attr('width', legendMetrics.width)
        .attr('height', legendMetrics.height)
        .attr('fill', legendBgColor)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 2)
        .attr('rx', 8)
        .style('cursor', 'move')
        .style('touch-action', 'none')
        .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

      legendGroup.append('text')
        .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
        .attr('y', 0)
        .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', highlightOptions.legend ? '800' : 'bold')
        .style('fill', legendTextColor)
        .style('text-shadow', legendHighlightShadow)
        .text(`🧰 ${t('ig.chart.boxInfo')}`);

      legendBoxes.slice(0, 8).forEach((box, index) => {
        const entryStyle = box.focusStyle || {};
        const itemY = (16 + index * 18) * cappedVScale;
        const swatchColor = entryStyle.strokeColor || box.color || '#94a3b8';
        const emoji = getSmartEmojiForLabel(box);
        const quartiles = box.quartiles || {};
        const summary = Number.isFinite(quartiles.median) ? t('ig.chart.medianSpace', { value: quartiles.median }) : t('ig.chart.medianDash');

        legendGroup.append('rect')
          .attr('x', 6)
          .attr('y', itemY - 10)
          .attr('width', 12)
          .attr('height', 12)
          .attr('rx', 3)
          .attr('fill', swatchColor)
          .attr('opacity', entryStyle.opacity ?? 1);

        legendGroup.append('text')
          .attr('x', 28)
          .attr('y', itemY + 3)
          .style('font-size', `${12 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .text(emoji);

        const labelText = box.label.substring(0, 16) + (box.label.length > 16 ? '...' : '');
        legendGroup.append('text')
          .attr('x', 46)
          .attr('y', itemY - 1)
          .style('font-size', `${10 * legendFontScale}px`)
          .style('font-weight', '600')
          .style('fill', legendTextColor)
          .text(labelText);

        legendGroup.append('text')
          .attr('x', 46)
          .attr('y', itemY + 11)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .text(summary);
      });

      legendGroup.append('rect')
        .attr('x', legendMetrics.width - 16)
        .attr('y', legendMetrics.height - 16)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'rgba(37, 99, 235, 0.6)')
        .attr('rx', 3)
        .style('cursor', 'nwse-resize')
        .style('touch-action', 'none')
        .attr('tabindex', 0)
        .attr('role', 'slider')
        .attr('aria-label', t('ig.chart.legendResize'))
        .attr('aria-valuemin', MIN_LEGEND_WIDTH)
        .attr('aria-valuemax', width - MIN_MARGIN)
        .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
        .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

      decorateLegendGroup(legendGroup, legendMetrics);
    }

    console.log('✅ 箱线图渲染完成');
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    highlightOptions.legend,
    legendVisualContext,
    resolvedLabelColor,
    startDrag,
    studentProfile
  ]);

  const renderColorLegend = useCallback((svg, layout, items, options = {}) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    const cappedVScale = legendVScaleCapped(layout, items.length, options.rowHeight ?? 20, 22);
    const legendMetrics = getLegendMetrics(layout, items.length, {
      defaultWidth: layout.legend?.width || 140,
      rowHeight: (options.rowHeight ?? 20) * cappedVScale,
      extraHeight: options.extraHeight ?? 36,
      bottomOffset: options.bottomOffset ?? 32
    });
    legendMetricsRef.current = legendMetrics;

    const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    const title = options.title || `📊 ${t('ig.chart.legend')}`;

    const legendGroup = svg.append('g')
      .attr('class', options.className || 'color-legend')
      .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

    legendGroup.append('rect')
      .attr('width', legendMetrics.width)
      .attr('height', legendMetrics.height)
      .attr('fill', legendBgColor)
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 2)
      .attr('rx', 12)
      .style('cursor', 'move')
      .style('touch-action', 'none')
      .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

    legendGroup.append('text')
      .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
      .attr('y', 0)
      .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
      .style('font-size', `${10 * legendFontScale}px`)
      .style('font-weight', highlightOptions.legend ? '800' : 'bold')
      .style('fill', legendTextColor)
      .style('text-shadow', legendHighlightShadow)
      .text(title);

    const rowHeight = (options.rowHeight ?? 20) * cappedVScale;
    items.forEach((item, index) => {
      const itemY = index * rowHeight + 22 * cappedVScale;
      const entryOpacity = item.visible === false ? 0.35 : 1;
      const swatchColor = item.color || '#38bdf8';
      const emoji = getSmartEmojiForLabel({ label: item.label, emoji: item.emoji });

      const itemGroup = legendGroup.append('g')
        .attr('transform', `translate(0, ${itemY})`)
        .attr('opacity', entryOpacity);

      itemGroup.append('rect')
        .attr('x', 0)
        .attr('y', -10)
        .attr('width', 12)
        .attr('height', 12)
        .attr('rx', 3)
        .attr('fill', swatchColor)
        .attr('fill-opacity', 0.35)
        .attr('stroke', swatchColor)
        .attr('stroke-width', 1.5);

      itemGroup.append('text')
        .attr('x', 22)
        .attr('y', -1)
        .style('font-size', `${12 * legendFontScale}px`)
        .style('fill', legendTextColor)
        .text(emoji);

      itemGroup.append('text')
        .attr('x', 40)
        .attr('y', -1)
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', item.isHighlighted ? '800' : '600')
        .style('fill', legendTextColor)
        .text((item.label || '').substring(0, 24) + ((item.label || '').length > 24 ? '…' : ''));

      const detail = item.detail || item.description;
      if (detail) {
        itemGroup.append('text')
          .attr('x', 40)
          .attr('y', 11)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .text(detail);
      }
    });

    legendGroup.append('rect')
      .attr('x', legendMetrics.width - 16)
      .attr('y', legendMetrics.height - 16)
      .attr('width', 16)
      .attr('height', 16)
      .attr('fill', 'rgba(37, 99, 235, 0.6)')
      .attr('rx', 3)
      .style('cursor', 'nwse-resize')
      .style('touch-action', 'none')
      .attr('tabindex', 0)
      .attr('role', 'slider')
      .attr('aria-label', t('ig.chart.legendResize'))
      .attr('aria-valuemin', MIN_LEGEND_WIDTH)
      .attr('aria-valuemax', layout.width - MIN_MARGIN)
      .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
      .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

    decorateLegendGroup(legendGroup, legendMetrics);
  }, [
    decorateLegendGroup,
    handleLegendKeyboard,
    highlightOptions.legend,
    legendVisualContext,
    startDrag
  ]);

  const renderUpcomingChartPlaceholder = useCallback((svg, stageData, layout) => {
    const { width, height } = layout;
    const actualGraphData = graphData.graph_data || graphData;
    const graphType = graphData.graph_type;
    const typeLabel = UPCOMING_CHART_LABELS[graphType] || graphType;
    const totalElements = actualGraphData?.elements?.length || 0;
    const visibleElements = stageData?.visible_elements?.length || 0;
    const cardWidth = Math.min(width - 80, 760);
    const cardHeight = 260;
    const cardX = (width - cardWidth) / 2;
    const cardY = Math.max((height - cardHeight) / 2, 40);
    const highlightColor = stageData?.highlight_color || '#38bdf8';

    svg.append('rect')
      .attr('x', cardX)
      .attr('y', cardY)
      .attr('width', cardWidth)
      .attr('height', cardHeight)
      .attr('rx', 24)
      .attr('fill', 'rgba(224, 242, 254, 0.85)')
      .attr('stroke', 'rgba(14, 165, 233, 0.5)')
      .attr('stroke-width', 2);

    svg.append('text')
      .attr('x', width / 2)
      .attr('y', cardY + 48)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', 'bold')
      .style('fill', '#0f172a')
      .text(t('ig.chart.preparingViz', { type: typeLabel }));

    const infoLines = [
      t('ig.chart.designingRenderer', { visible: visibleElements, total: totalElements }),
      t('ig.chart.dataAggregationNote'),
      t('ig.chart.upcomingReleaseNote')
    ];

    infoLines.forEach((line, idx) => {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', cardY + 100 + idx * 48)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#1e293b')
        .text(line);
    });

    svg.append('text')
      .attr('x', width / 2)
      .attr('y', cardY + cardHeight - 24)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('fill', highlightColor)
      .style('font-weight', '600')
      .text(t('ig.chart.betaTuning'));
  }, [graphData]);

  const renderGenericChart = useCallback((svg, stageData, layout) => {
    const { width, height } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const visibleElements = stageData.visible_elements || [];
    const elementPool =
      graphData?.graph_data?.elements ||
      graphData?.elements ||
      stageData?.elements ||
      [];
    const elementsToShow = (visibleElements.length
      ? elementPool.filter((element) => visibleElements.includes(element.id))
      : elementPool
    ).slice(0, 10);

    svg.append('text')
      .attr('x', width / 2)
      .attr('y', height / 2 - 40)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('fill', '#475569')
      .text(t('ig.chart.visibleCount', { count: visibleElements.length }));

    elementsToShow.forEach((element, index) => {
      const y = height / 2 + index * 40;
      svg.append('rect')
        .attr('x', width / 2 - 150)
        .attr('y', y)
        .attr('width', 300)
        .attr('height', 30)
        .attr('fill', stageData.highlight_color || '#94a3b8')
        .attr('rx', 5)
        .attr('opacity', shouldAnimate ? 0 : 0.85)
        .call((selection) => {
          if (shouldAnimate) {
            selection
              .transition()
              .delay(index * 120)
              .duration(400)
              .attr('opacity', 0.85);
          }
        });

      svg.append('text')
        .attr('x', width / 2)
        .attr('y', y + 20)
        .attr('text-anchor', 'middle')
        .style('font-size', `${20 * vlScale(layout)}px`)
        .style('font-weight', '600')
        .style('fill', '#0f172a')
        .text(`${element.type}: ${element.label || element.id}`)
        .attr('opacity', shouldAnimate ? 0 : 1)
        .call((selection) => {
          if (shouldAnimate) {
            selection
              .transition()
              .delay(index * 120)
              .duration(400)
              .attr('opacity', 1);
          }
        });
    });
  }, [graphData]);

const renderAreaChart = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis, legend } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
    const isShowAllStage = stageData.show_all_with_original_style === true;
    const defaultColors = getCUDPalette(settings?.colorAccessibilityPreset);
    console.log('📈📈📈 ===== renderAreaChart 被调用 ===== 📈📈📈');
    console.log('📏 SVG尺寸:', { width, height, margin });

    // 从后端数据中提取面积图信息（可能是area或line类型）
    const actualGraphData = graphData.graph_data || graphData;
    const areas = actualGraphData.elements?.filter(e => e.type === 'area' || e.type === 'line') || [];
    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const areaElementIds = new Set(areas.map((a) => a.id));
    const visibleAreaIds = visibleElements.filter((id) => areaElementIds.has(id));

    console.log('🔍 Area chart analysis:', {
      totalAreas: areas.length,
      visibleElements: visibleElements,
      axesVisible: axesVisible
    });

    // 使用Vision AI返回的真实数据
    const areaData = areas.map((area, i) => {
      const isVisible = visibleElements.includes(area.id);
      const isHighlighted = isVisible && (isShowAllStage || visibleAreaIds.length <= 1);
      const areaColor = getElementColor(area.id, area.color || defaultColors[getConsistentIndexFromId(area.id) % defaultColors.length]);
      const focusStyle = getElementFocusStyle({
        baseColor: areaColor,
        fallbackColor: '#94a3b8',
        isVisible: isShowAllStage || isVisible,
        isHighlighted,
        isShowAllStage,
        difficultyType,
        elementId: area.id
      });

      console.log(`📊 Area ${i}:`, {
        id: area.id,
        label: area.label,
        color: areaColor,
        isVisible,
        isHighlighted
      });

      // 使用Vision AI返回的真实数据点
      const chartPoints = area.data_points && area.data_points.length > 0
        ? area.data_points.map(p => ({x: p.x, y: p.y, label: p.x_label}))
        : [
            {x: 1, y: 2 + i},
            {x: 2, y: 4 + i * 0.5},
            {x: 3, y: 3 + i * 0.8},
            {x: 4, y: 5 + i * 0.3},
            {x: 5, y: 4 + i * 0.6}
          ];

      return {
        id: area.id,
        label: area.label || t('ig.chart.seriesN', { n: i + 1 }),
        baseColor: areaColor,
        focusStyle,
        points: chartPoints,
        visible: isVisible,
        isHighlighted,
        emoji: area.emoji || area.icon || area.symbol || null
      };
    });

    console.log('✅ Final areaData:', areaData);

    // 动态计算坐标轴范围（基于真实数据）
    const allPoints = areaData.flatMap(area => area.points);
    const xValues = allPoints.map(p => p.x);
    const yValues = allPoints.map(p => p.y);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = 0;  // 面积图通常从0开始
    const yMax = Math.max(...yValues);

    console.log('📊 Data range:', { xMin, xMax, yMin, yMax });

    // 使用真实数据范围，添加10%的边距
    const xPadding = (xMax - xMin) * 0.1 || 1;
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const xScale = d3.scaleLinear()
      .domain([xMin - xPadding, xMax + xPadding])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax + yPadding])
      .nice(5)
      .range([height - margin.bottom, margin.top]);

    // 绘制坐标轴
    if (axesVisible) {
      const xTickFontSize = axis?.xTickFontSize || 20;
      // determineXAxisLabelStrategy covers line_chart / area_chart too, but
      // until now only bar/scatter/histogram/box APPLIED the rotation it
      // returns — so these two widened their bottom margin and still drew the
      // labels flat, straight through each other.
      const xTickRotation = axis?.xTickRotation || 0;
      const xTickOffsetX = axis?.xTickOffsetX || 0;
      const xTickOffsetY = axis?.xTickOffsetY || 0;
      const yTickFontSize = axis?.yTickFontSize || 20;
      const yAxisGenerator = d3.axisLeft(yScale).ticks(5);
      if (!showTickLabels) {
        yAxisGenerator.tickFormat(() => '');
      }
      // Y轴
      const yAxis = svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxisGenerator)
        .style('font-size', `${yTickFontSize}px`);
      yAxis.selectAll('line').attr('stroke', '#64748b');
      yAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

      // X轴 - 使用真实数据点的x_label作为横轴刻度
      const uniqueXValues = [...new Set(allPoints.map(p => p.x))].sort((a, b) => a - b);
      const xAxisLabels = uniqueXValues.map(x => {
        const point = allPoints.find(p => p.x === x);
        return point?.label || x.toString();
      });

      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(xScale)
          .tickValues(uniqueXValues)
          .tickFormat(''))  // 清空默认标签，手动添加
        .style('font-size', `${xTickFontSize}px`);
      xAxis.selectAll('line').attr('stroke', '#64748b');
      xAxis.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);

      // 为每个tick添加Emoji和文字标签
      console.log('✅ 开始为面积图X轴添加emoji...');
      const tickCount = xAxis.selectAll('.tick').size();
      console.log(`🔍 面积图X轴tick数量: ${tickCount}`);

      if (showTickLabels) {
        xAxis.selectAll('.tick').each(function(d, i) {
          const tick = d3.select(this);
          const label = xAxisLabels[i] || d.toString();

          console.log(`🔍 [面グラフ横軸] ラベル: "${label}" (type: ${typeof label})`);

          const icon = getSmartEmojiForLabel(label);  // 使用智能识别

          console.log(`🎨 [面グラフ横軸] マッチしたEmoji: "${icon}"`);

          // 添加文字标签（在横轴下方20px）
          const labelText = tick.append('text')
            .attr('text-anchor', xTickRotation !== 0 ? 'end' : 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('font-weight', axisWeight)
            .style('fill', labelColor)
            .text(label);
          if (xTickRotation !== 0) {
            labelText.attr(
              'transform',
              `translate(${xTickOffsetX}, ${xTickOffsetY}) rotate(${xTickRotation})`,
            );
          } else {
            labelText.attr('y', 20);
          }

          tick.append('text')
            // Rotated labels sweep downward, so the icon has to drop with them.
            .attr('y', xTickRotation !== 0 ? xTickOffsetY + 55 : 55)
            .attr('text-anchor', 'middle')
            .style('font-size', `${xTickFontSize}px`)
            .style('fill', labelColor)
            .text(icon);

          console.log(`✅ [面グラフ横軸] Emoji "${icon}" + テキスト "${label}" を追加完了`);
        });
      }

      console.log('✅ 面积图X轴和emoji图标绘制完成');

      if (showAxisLabels) {
        // Y轴标签 + emoji
        const yAxisData = actualGraphData.elements?.find(e => e.id === 'axis_y');
        const yAxisLabel = yAxisData?.label || t('ig.chart.yAxis');
        const yLabelEmoji = getSmartEmojiForLabel(yAxisLabel);

        const yLabelGroup = svg.append('g')
          .attr('transform', `rotate(-90) translate(${-(height / 2)}, ${margin.left - 60})`);

        yLabelGroup.append('text')
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('fill', labelColor)
          .text(yLabelEmoji);

        yLabelGroup.append('text')
          .attr('x', 35)
          .attr('y', 5)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);

        console.log(`✅ [面グラフY軸] Emoji "${yLabelEmoji}" + ラベル "${yAxisLabel}" を追加完了`);
      }
    }

    // 创建面积生成器
    const area = d3.area()
      .x(d => xScale(d.x))
      .y0(height - margin.bottom)  // 底部基线
      .y1(d => yScale(d.y))         // 顶部数据线
      .curve(d3.curveMonotoneX);    // 平滑曲线

    // 创建线条生成器（用于顶部边界）
    const line = d3.line()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y))
      .curve(d3.curveMonotoneX);

    // 绘制面积图
    if (showData) {
      areaData.forEach((areaItem) => {
      const shouldRender = areaItem.visible || isShowAllStage;
      if (!shouldRender) return;

      const style = areaItem.focusStyle || {
        color: areaItem.baseColor,
        strokeColor: areaItem.baseColor,
        opacity: areaItem.visible ? 1 : 0.25,
        emphasis: areaItem.visible ? 'focus' : 'hidden'
      };
      const strokeColor = style.strokeColor || areaItem.baseColor || '#94a3b8';
      const fillOpacity = style.emphasis === 'focus' ? 0.55 : Math.max(0.2, style.opacity * 0.7);
      const strokeWidth = style.emphasis === 'keyword_highlight' ? 6 : (style.emphasis === 'focus' ? 3 : 2);

      const areaPath = svg.append('path')
        .datum(areaItem.points)
        .attr('data-element-id', areaItem.id || 'area-' + areaData.indexOf(areaItem))
        .attr('fill', strokeColor)
        .attr('fill-opacity', shouldAnimate ? 0 : fillOpacity)
        .attr('d', area);

      if (shouldAnimate) {
        areaPath
          .transition()
          .duration(1000)
          .ease(d3.easeCubicOut)
          .attr('fill-opacity', fillOpacity);
      }

      attachInteractiveHandlers(areaPath, {
        getElementId: () => areaItem.id,
        getPayload: () => ({
          title: areaItem.label,
          subtitle: t('ig.chart.rangeColon', { value: t('ig.chart.pointCountSpace', { count: areaItem.points.length }) }),
          detailLines: [],
          color: strokeColor,
          icon: areaItem.emoji || getSmartEmojiForLabel(areaItem.label)
        }),
        isInteractive: () => isShowAllStage || areaItem.visible,
        onDrilldown: () => ({
          title: t('ig.chart.detail', { label: areaItem.label }),
          description: t('ig.chart.areaDesc', { count: areaItem.points.length }),
          detailLines: [stageData?.stage_summary ? stageData.stage_summary : null].filter(Boolean),
          icon: areaItem.emoji || getSmartEmojiForLabel(areaItem.label),
          variant: 'area'
        })
      });

      const linePath = svg.append('path')
        .datum(areaItem.points)
        .attr('fill', 'none')
        .attr('stroke', strokeColor)
        .attr('stroke-width', strokeWidth)
        .attr('opacity', style.opacity)
        .attr('d', line);

      const totalLength = linePath.node().getTotalLength();
      if (shouldAnimate) {
        linePath
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(1000)
          .ease(d3.easeLinear)
          .attr('stroke-dashoffset', 0);
      }

      const pointRadius = style.emphasis === 'focus' ? 6 : 4;
      const pointFill = strokeColor;

      areaItem.points.forEach((point) => {
        const areaPoint = svg.append('circle')
          .datum({ point, areaItem })
          .attr('cx', xScale(point.x))
          .attr('cy', yScale(point.y))
          .attr('r', pointRadius)
          .attr('fill', pointFill)
          .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', 2)
          .attr('opacity', shouldAnimate ? 0 : style.opacity);

        if (shouldAnimate) {
          areaPoint
            .transition()
            .delay(1000)
            .duration(300)
            .attr('opacity', style.opacity);
        }

        attachInteractiveHandlers(areaPoint, {
          getElementId: (d) => d.areaItem?.id || areaItem.id,
          getPayload: ({ point }) => ({
            title: areaItem.label,
            subtitle: `(${point.x}, ${point.y})`,
            detailLines: [],
            color: strokeColor,
            icon: areaItem.emoji || getSmartEmojiForLabel(areaItem.label)
          }),
          isInteractive: () => isShowAllStage || areaItem.visible,
          onDrilldown: ({ point }) => ({
            title: t('ig.chart.detail', { label: areaItem.label }),
            description: `X=${point.x}, Y=${point.y}`,
            detailLines: [],
            icon: areaItem.emoji || getSmartEmojiForLabel(areaItem.label),
            variant: 'area-point'
          })
        });

        // A' 2026-06-15: render value labels ALWAYS (hidden unless focused/show-all) so a
        // grounded show_value_tags overlay can reveal+bold them at upload; handler sets
        // display:null to reveal. No global clutter for non-overlay students.
        const pointLabel = svg.append('text')
          .attr('data-value-for', areaItem.id)
          .attr('x', xScale(point.x))
          .attr('y', yScale(point.y) - pointRadius - 5)
          .attr('text-anchor', 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('font-weight', 'bold')
          .style('fill', labelColor)
          .text(point.y.toFixed(1));
        if (!(style.emphasis === 'focus' || isShowAllStage)) {
          // hidden; inline opacity not set (SVG default is 1) so reveal via display:null
          // shows it at full opacity without animation.
          pointLabel.style('display', 'none');
        } else {
          pointLabel.style('opacity', shouldAnimate ? 0 : 1);
          if (shouldAnimate) {
            pointLabel
              .transition()
              .delay(1300)
              .duration(300)
              .style('opacity', 1);
          }
        }
      });
      });
    }

    const legendAreas = showLegend
      ? areaData
      : (isShowAllStage ? areaData : areaData.filter((areaItem) => areaItem.visible));
    if (showLegend && legendAreas.length > 0) {
      const visibleCount = Math.min(legendAreas.length, 8);
      const cappedVScale = legendVScaleCapped(layout, visibleCount, 20, 16);
      const legendMetrics = getLegendMetrics(layout, visibleCount, {
        defaultWidth: legend?.width || 140,
        rowHeight: 20 * cappedVScale,
        extraHeight: 36,
        bottomOffset: 34
      });
      legendMetricsRef.current = legendMetrics;
      const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);

      const legendGroup = svg.append('g')
        .attr('class', 'area-legend')
        .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

      legendGroup.append('rect')
        .attr('width', legendMetrics.width)
        .attr('height', legendMetrics.height)
        .attr('fill', legendBgColor)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 2)
        .attr('rx', 8)
        .style('cursor', 'move')
        .style('touch-action', 'none')
        .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

      legendGroup.append('text')
        .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
        .attr('y', 0)
        .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', highlightOptions.legend ? '800' : 'bold')
        .style('fill', legendTextColor)
        .style('text-shadow', legendHighlightShadow)
        .text(`📈 ${t('ig.chart.legend')}`);

      legendAreas.slice(0, 8).forEach((areaItem, index) => {
        const entryStyle = areaItem.focusStyle || {};
        const itemY = (16 + index * 20) * cappedVScale;
        const swatchColor = entryStyle.strokeColor || areaItem.baseColor || '#94a3b8';
        const entryOpacity = entryStyle.opacity ?? 1;
        const emoji = getSmartEmojiForLabel(areaItem);
        const labelText = areaItem.label.substring(0, 16) + (areaItem.label.length > 16 ? '...' : '');

        legendGroup.append('rect')
          .attr('x', 0)
          .attr('y', itemY - 10)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', swatchColor)
          .attr('fill-opacity', 0.5)
          .attr('stroke', swatchColor)
          .attr('stroke-width', 1.5)
          .attr('rx', 3)
          .attr('opacity', entryOpacity);

        legendGroup.append('text')
          .attr('x', 22)
          .attr('y', itemY + 4)
          .style('font-size', `${12 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(emoji);

        legendGroup.append('text')
          .attr('x', 40)
          .attr('y', itemY + 2)
          .style('font-size', `${10 * legendFontScale}px`)
          .style('font-weight', 'bold')
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(labelText);

        legendGroup.append('text')
          .attr('x', 40)
          .attr('y', itemY + 14)
          .style('font-size', `${9 * legendFontScale}px`)
          .style('fill', legendTextColor)
          .style('opacity', entryOpacity)
          .text(t('ig.chart.pointCount', { count: areaItem.points.length }));
      });

      legendGroup.append('rect')
        .attr('x', legendMetrics.width - 16)
        .attr('y', legendMetrics.height - 16)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'rgba(37, 99, 235, 0.6)')
        .attr('rx', 3)
        .style('cursor', 'nwse-resize')
        .style('touch-action', 'none')
        .attr('tabindex', 0)
        .attr('role', 'slider')
        .attr('aria-label', t('ig.chart.legendResize'))
        .attr('aria-valuemin', MIN_LEGEND_WIDTH)
        .attr('aria-valuemax', width - MIN_MARGIN)
        .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
        .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

      decorateLegendGroup(legendGroup, legendMetrics);
    }

    console.log('✅ 面积图渲染完成');
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    decorateLegendGroup,
    getElementColor,
    getElementFocusStyle,
    graphData,
    handleLegendKeyboard,
    highlightOptions.legend,
    legendVisualContext,
    resolvedLabelColor,
    startDrag,
    studentProfile
  ]);

  const renderBandGraph = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const actualGraphData = graphData.graph_data || graphData || {};
    let rawSegments = (actualGraphData.elements || []).filter((element) =>
      BAND_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );

    if (!rawSegments.length) {
      rawSegments = (actualGraphData.elements || []).filter((element) => Array.isArray(element?.data_points) && element.data_points.length > 0);
    }

    if (!rawSegments.length) {
      renderGenericChart(svg, stageData, layout);
      return;
    }

    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const stageVisibility = createStageVisibilityChecker(visibleElements);
    const hasVisibilityConstraints = visibleElements.length > 0;
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const isShowAllStage = stageData.show_all_with_original_style === true || !hasVisibilityConstraints;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);

    const fallbackPoints = (index) => (
      Array.from({ length: 4 }).map((_, pointIndex) => ({
        key: `placeholder_${pointIndex}`,
        label: t('ig.chart.binN', { n: pointIndex + 1 }),
        value: Math.max(5, 20 - index * 3 - pointIndex * 2),
        numericValue: pointIndex + 1
      }))
    );

    const segments = rawSegments.map((segment, index) => {
      const color = getElementColor(segment.id, segment.color || EXTENDED_COLOR_PALETTE[getConsistentIndexFromId(segment.id) % EXTENDED_COLOR_PALETTE.length]);
      const isVisible = stageVisibility(segment.id, segment.label, segment.type);
      const rawPoints = Array.isArray(segment.data_points)
        ? segment.data_points
        : Array.isArray(segment.points)
          ? segment.points
          : Array.isArray(segment.values)
            ? segment.values.map((value, pointIndex) => ({ value, x: pointIndex + 1 }))
            : [];

      const processedPoints = (rawPoints.length ? rawPoints : fallbackPoints(index)).map((point, pointIndex) => {
        const key = point.key || point.x_key || point.category || point.x_label || `${point.x ?? pointIndex}`;
        const label = point.x_label || point.category || point.label || key;
        const numericValue = Number.isFinite(point.numeric_value)
          ? point.numeric_value
          : Number.isFinite(point.x)
            ? Number(point.x)
            : Number(point.order ?? pointIndex + 1);
        const value = Number(point.value ?? point.percentage ?? point.y ?? point.y1 ?? 0);
        return {
          key,
          label,
          numericValue,
          value,
          datum: point
        };
      });

      const pointMap = new Map();
      processedPoints.forEach((point) => {
        pointMap.set(point.key, point);
      });

      return {
        id: segment.id,
        label: segment.label || t('ig.chart.bandN', { n: index + 1 }),
        color,
        focusStyle: null,
        visible: isVisible,
        pointMap,
        points: processedPoints,
        emoji: segment.emoji || segment.icon || segment.symbol || null
      };
    }).filter((segment) => segment.points.length > 0);

    const segmentsForScale = segments;
    let segmentsToRender = (showData || isShowAllStage)
      ? (isShowAllStage ? segments : segments.filter((segment) => segment.visible))
      : [];

    const effectiveShowAll = isShowAllStage || !hasVisibilityConstraints || segmentsToRender.length === segments.length;
    segments.forEach((segment) => {
      const isVisible = stageVisibility(segment.id, segment.label, segment.type);
      segment.focusStyle = getElementFocusStyle({
        baseColor: segment.color,
        fallbackColor: '#94a3b8',
        isVisible: effectiveShowAll || isVisible,
        isHighlighted: isVisible,
        isShowAllStage: effectiveShowAll,
        difficultyType,
        elementId: segment.id
      });
    });

    const categoriesMap = new Map();
    (segmentsToRender.length ? segmentsToRender : segmentsForScale).forEach((segment) => {
      segment.points.forEach((point) => {
        if (!categoriesMap.has(point.key)) {
          categoriesMap.set(point.key, {
            key: point.key,
            label: point.label,
            numericValue: point.numericValue,
            order: categoriesMap.size
          });
        }
      });
    });

    const categories = Array.from(categoriesMap.values());
    if (!categories.length) {
      renderUpcomingChartPlaceholder(svg, stageData, layout);
      return;
    }

    const numericCategories = categories.every((category) => Number.isFinite(category.numericValue));
    categories.forEach((category, index) => {
      if (!Number.isFinite(category.numericValue)) {
        category.numericValue = index + 1;
      }
    });
    const sortedCategories = categories.slice().sort((a, b) => {
      if (numericCategories) {
        return (a.numericValue ?? 0) - (b.numericValue ?? 0);
      }
      return a.order - b.order;
    });

    // For band graphs, we use horizontal stacked bars
    // Y-axis = categories (years/periods) using scaleBand
    // X-axis = percentages (0-100) using scaleLinear

    const stackRows = sortedCategories.map((category, index) => {
      const row = {
        __key: category.key,
        label: category.label,
        numericValue: category.numericValue ?? index + 1
      };
      segmentsToRender.forEach((segment) => {
        row[segment.id] = segment.pointMap.get(category.key)?.value ?? 0;
      });
      return row;
    });

    // Calculate the maximum total value for each category
    const maxTotal = d3.max(stackRows, (row) => {
      return segmentsToRender.reduce((sum, segment) => {
        return sum + (row[segment.id] || 0);
      }, 0);
    }) || 100;

    // Y-axis: categories (years) - horizontal bands
    const yScale = d3.scaleBand()
      .domain(sortedCategories.map((category) => category.key))
      .range([margin.top, height - margin.bottom])
      .padding(0.2);

    // X-axis: percentage scale
    const xScale = d3.scaleLinear()
      .domain([0, maxTotal * 1.05])
      .range([margin.left, width - margin.right]);

    if (axesVisible) {
      // Y-axis: category labels (years/periods)
      const yAxis = svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(yScale))
        .style('font-size', `${axis?.yTickFontSize || 20}px`);
      yAxis.selectAll('line').attr('stroke', '#94a3b8');
      yAxis.selectAll('path').attr('stroke', '#94a3b8').attr('stroke-width', 2);
      applyAxisTextStyles(yAxis.selectAll('text'), labelColor, axisWeight);
      if (!showTickLabels) {
        yAxis.selectAll('text').text('');
      }

      // X-axis: percentage scale
      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(xScale).ticks(5))
        .style('font-size', `${axis?.xTickFontSize || 20}px`);
      xAxis.selectAll('line').attr('stroke', '#94a3b8');
      xAxis.selectAll('path').attr('stroke', '#94a3b8').attr('stroke-width', 2);
      applyAxisTextStyles(xAxis.selectAll('text'), labelColor, axisWeight);
      if (!showTickLabels) {
        xAxis.selectAll('text').text('');
      }

      if (showAxisLabels) {
        const xAxisLabel = actualGraphData.elements?.find((e) => e.id === 'axis_x')?.label || stageData?.x_label || t('ig.chart.proportion');
        const yAxisLabel = actualGraphData.elements?.find((e) => e.id === 'axis_y')?.label || stageData?.y_label || t('ig.chart.bin');
        svg.append('text')
          .attr('x', (width - margin.left - margin.right) / 2 + margin.left)
          .attr('y', height - margin.bottom + 56)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(xAxisLabel);
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(height / 2))
          .attr('y', margin.left - 52)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);
      }
    }

    // Render horizontal stacked bars
    if (showData) {
      stackRows.forEach((row, rowIndex) => {
      let xOffset = 0; // Cumulative x position for stacking

      segmentsToRender.forEach((segment, segmentIndex) => {
        const value = row[segment.id] || 0;
        if (value === 0) return; // Skip zero-width segments

        const style = segment.focusStyle || {
          color: segment.color,
          strokeColor: segment.color,
          opacity: 1,
          emphasis: 'focus'
        };

        const bandY = yScale(row.__key);
        const bandHeight = yScale.bandwidth();
        const rectX = xScale(xOffset);
        const rectWidth = xScale(xOffset + value) - xScale(xOffset);

        // Create rectangle for this segment
        const rect = svg.append('rect')
          .attr('data-element-id', segment.id || 'band-' + segmentIndex)
          .attr('x', rectX)
          .attr('y', bandY)
          .attr('width', shouldAnimate ? 0 : rectWidth)
          .attr('height', bandHeight)
          .attr('fill', style.color)
          .attr('fill-opacity', shouldAnimate ? 0 : (style.emphasis === 'focus' ? 0.7 : Math.max(0.3, style.opacity ?? 0.5)))
          .attr('stroke', style.emphasis === 'keyword_highlight' ? '#FFD700' : DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', style.emphasis === 'keyword_highlight' ? 7 : (style.emphasis === 'focus' ? 2 : 1))
          .classed('keyword-highlight-blink', style.emphasis === 'keyword_highlight');

        if (shouldAnimate) {
          rect
            .transition()
            .duration(900)
            .attr('width', rectWidth)
            .attr('fill-opacity', style.emphasis === 'focus' ? 0.7 : Math.max(0.3, style.opacity ?? 0.5));
        }

        // Add interactivity
        attachInteractiveHandlers(rect, {
          getElementId: () => segment.id,
          getPayload: () => ({
            title: segment.label,
            subtitle: `${row.label}: ${value}`,
            detailLines: [t('ig.chart.valueColon', { value: value })],
            icon: segment.emoji || getSmartEmojiForLabel(segment.label)
          }),
          isInteractive: () => effectiveShowAll || segment.visible,
          onDrilldown: () => ({
            title: t('ig.chart.detail', { label: segment.label }),
            description: stageData?.stage_summary || t('ig.chart.bandChangeHint'),
            detailLines: sortedCategories.map((category) => {
              const val = segment.pointMap.get(category.key)?.value ?? 0;
              return `${category.label}: ${val}`;
            }),
            icon: segment.emoji || getSmartEmojiForLabel(segment.label),
            variant: 'bar'
          })
        });

        xOffset += value; // Move to next position for stacking
      });
      });
    }

    const legendItems = segments.map((segment) => ({
      id: segment.id,
      label: segment.label,
      emoji: segment.emoji,
      color: segment.color,
      visible: effectiveShowAll || segment.visible,
      detail: t('ig.chart.pointCount', { count: segment.points.length })
    }));
    // トレンド線描画（帯グラフ・実線）
    if (stageData.show_trend_line && segments.length > 1) {
      const segmentTotals = segments.map(seg => {
        const total = Array.from(seg.pointMap.values()).reduce((sum, p) => sum + (p.value || 0), 0);
        return { label: seg.label, total };
      });
      const trendXScale = d3.scaleLinear()
        .domain([0, segmentTotals.length - 1])
        .range([margin.left + 40, width - margin.right - 40]);
      const maxTotal = d3.max(segmentTotals, d => d.total) || 1;
      const trendYScale = d3.scaleLinear()
        .domain([0, maxTotal])
        .range([height - margin.bottom - 20, margin.top + 20]);
      const trendPoints = segmentTotals.map((d, i) => ({
        x: trendXScale(i),
        y: trendYScale(d.total)
      }));
      const lineGen = d3.line()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveMonotoneX);
      svg.append('path')
        .datum(trendPoints)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 2)
        .attr('opacity', 0.8);
    }

    if (showLegend) {
      renderColorLegend(svg, layout, legendItems, { title: t('ig.chart.bandSegment') });
    }
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    getElementColor,
    getElementFocusStyle,
    graphData,
    renderGenericChart,
    renderUpcomingChartPlaceholder,
    resolvedLabelColor,
    studentProfile
  ]);

  const renderVennDiagram = useCallback((svg, stageData, layout) => {
    const { width, height, margin } = layout;
    const actualGraphData = graphData.graph_data || graphData || {};
    const circleElements = (actualGraphData.elements || []).filter((element) =>
      VENN_CIRCLE_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );

    if (!circleElements.length) {
      renderUpcomingChartPlaceholder(svg, stageData, layout);
      return;
    }

    const visibleElements = stageData.visible_elements || [];
    const stageVisibility = createStageVisibilityChecker(visibleElements);
    const hasVisibilityConstraints = visibleElements.length > 0;
    const isShowAllStage = stageData.show_all_with_original_style === true || !hasVisibilityConstraints;

    const drawingWidth = width - margin.left - margin.right;
    const drawingHeight = height - margin.top - margin.bottom;
    const relativeLayouts = {
      1: [{ x: 0, y: 0 }],
      2: [{ x: -0.4, y: 0 }, { x: 0.4, y: 0 }],
      3: [{ x: 0, y: -0.25 }, { x: -0.35, y: 0.35 }, { x: 0.35, y: 0.35 }],
      // Ikigai 4-circle overlapping layout - tighter spacing for better overlap
      4: [{ x: -0.2, y: -0.2 }, { x: 0.2, y: -0.2 }, { x: -0.2, y: 0.2 }, { x: 0.2, y: 0.2 }]
    };
    const forceIkigaiLayout = circleElements.length === 4;
    const ikigaiLabelAnchors = [
      { dxFactor: -0.45, dyFactor: -0.78, anchor: 'end' }, // top-left
      { dxFactor: 0.45, dyFactor: -0.78, anchor: 'start' }, // top-right
      { dxFactor: -0.45, dyFactor: 0.82, anchor: 'end' }, // bottom-left
      { dxFactor: 0.45, dyFactor: 0.82, anchor: 'start' } // bottom-right
    ];
    const defaultLayout = relativeLayouts[circleElements.length] ||
      Array.from({ length: circleElements.length }).map((_, index) => {
        const angle = (index / circleElements.length) * Math.PI * 2;
        return { x: Math.cos(angle) * 0.45, y: Math.sin(angle) * 0.35 };
      });
    const appliedLayout = forceIkigaiLayout ? relativeLayouts[4] : defaultLayout;

    const baseRadius = Math.min(drawingWidth, drawingHeight) / (circleElements.length >= 3 ? 3.2 : 2.4);
    const resolvePosition = (value, fallback, span, offset) => {
      if (Number.isFinite(value)) {
        if (value >= 0 && value <= 1) {
          return offset + value * span;
        }
        return offset + value;
      }
      return offset + span / 2 + fallback * span / 2;
    };
    const resolveRadius = (value) => {
      if (Number.isFinite(value)) {
        if (value > 0 && value <= 1.5) {
          return baseRadius * value;
        }
        return Math.max(20, value);
      }
      return baseRadius;
    };

    const circles = circleElements.map((element, index) => {
      const color = getElementColor(element.id, element.color || EXTENDED_COLOR_PALETTE[index % EXTENDED_COLOR_PALETTE.length]);
      const fallbackPosition = appliedLayout[index] || { x: 0, y: 0 };
      const radius = forceIkigaiLayout ? baseRadius * 0.95 : resolveRadius(element.radius ?? element.r);
      const useFallbackOnly = forceIkigaiLayout;
      const cx = resolvePosition(
        useFallbackOnly ? null : (element.cx ?? element.center?.x),
        fallbackPosition.x,
        drawingWidth,
        margin.left
      );
      const cy = resolvePosition(
        useFallbackOnly ? null : (element.cy ?? element.center?.y),
        fallbackPosition.y,
        drawingHeight,
        margin.top
      );
      return {
        id: element.id,
        label: element.label || t('ig.chart.setN', { n: index + 1 }),
        value: element.value ?? element.count ?? element.percentage,
        color,
        radius,
        cx,
        cy,
        visible: stageVisibility(element.id, element.label, element.type),
        emoji: element.emoji || element.icon || element.symbol || null,
        opacity: element.opacity ?? 0.35
      };
    });

    const clampCoord = (value, min, max) => Math.min(Math.max(value, min), max);
    const adjustLabelPosition = (circle, placedLabels) => {
      let candidateY = clampCoord(circle.cy + 4, circle.cy - circle.radius + 16, circle.cy + circle.radius - 10);
      let attempts = 0;
      while (
        placedLabels.some((label) =>
          Math.abs(label.x - circle.cx) < circle.radius * 0.6 &&
          Math.abs(label.y - candidateY) < 16
        ) && attempts < 6
      ) {
        candidateY += 16;
        if (candidateY > circle.cy + circle.radius - 10) {
          candidateY = circle.cy - circle.radius + 16;
        }
        attempts += 1;
      }
      placedLabels.push({ x: circle.cx, y: candidateY });
      return candidateY;
    };

    let circlesToRender = isShowAllStage ? circles : circles.filter((circle) => circle.visible);
    if (!circlesToRender.length) {
      circlesToRender = circles;
    }
    const effectiveShowAll = isShowAllStage || !hasVisibilityConstraints || circlesToRender.length === circles.length;

    // Create isolated group for blend mode to work correctly
    const circleGroup = svg.append('g')
      .style('isolation', 'isolate');

      const placedLabels = [];
      const registerPlacedLabel = (x, y, width = 0, height = 16) => placedLabels.push({ x, y, width, height });
    circlesToRender.forEach((circle) => {
      const originalIndex = circleElements.findIndex((base) => base.id === circle.id);
      // In show-all stage, use higher opacity to make overlaps more visible
      const finalOpacity = effectiveShowAll ? 0.6 : circle.opacity;
      circleGroup.append('circle')
        .attr('data-element-id', circle.id || 'venn-' + circlesToRender.indexOf(circle))
        .attr('cx', circle.cx)
        .attr('cy', circle.cy)
        .attr('r', circle.radius)
        .attr('fill', circle.color)
        .attr('fill-opacity', finalOpacity)
        .attr('stroke', circle.color)
        .attr('stroke-width', effectiveShowAll ? 3 : 2)
        .style('mix-blend-mode', 'multiply')
        .call((selection) => {
          attachInteractiveHandlers(selection, {
            getElementId: () => circle.id,
            getPayload: () => ({
              title: circle.label,
              subtitle: circle.value != null ? t('ig.chart.valueColon', { value: circle.value }) : undefined,
              detailLines: [],
              icon: circle.emoji || getSmartEmojiForLabel(circle.label)
            }),
            isInteractive: () => effectiveShowAll || circle.visible,
            onDrilldown: () => ({
              title: t('ig.chart.detail', { label: circle.label }),
              description: stageData?.stage_summary || t('ig.chart.setOverlapHint'),
              detailLines: circle.value != null ? [t('ig.chart.valueColon', { value: circle.value })] : [],
              icon: circle.emoji || getSmartEmojiForLabel(circle.label),
              variant: 'venn'
            })
          });
        });

      const ikigaiAnchor = forceIkigaiLayout && originalIndex !== -1 ? ikigaiLabelAnchors[originalIndex] : null;
      const anchoredX = ikigaiAnchor ? clampCoord(
        circle.cx + ikigaiAnchor.dxFactor * circle.radius,
        margin.left + 8,
        width - margin.right - 8
      ) : null;
      const anchoredY = ikigaiAnchor ? clampCoord(
        circle.cy + ikigaiAnchor.dyFactor * circle.radius,
        margin.top + 14,
        height - margin.bottom - 14
      ) : null;
      const labelX = anchoredX != null ? anchoredX : circle.cx;
      const labelY = anchoredY != null
        ? (registerPlacedLabel(labelX, anchoredY, circle.radius, 18), anchoredY)
        : adjustLabelPosition(circle, placedLabels);
      if (ikigaiAnchor) {
        circleGroup.append('line')
          .attr('x1', circle.cx)
          .attr('y1', circle.cy + Math.sign(ikigaiAnchor.dyFactor) * Math.min(circle.radius * 0.35, 24))
          .attr('x2', labelX)
          .attr('y2', labelY - 10)
          .attr('stroke', circle.color)
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.9);
      }
      const labelText = circleGroup.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', ikigaiAnchor?.anchor || 'middle')
        .style('font-size', `${20 * vlScale(layout)}px`)
        .style('font-weight', '600')
        .style('fill', circle.visible ? '#0f172a' : '#475569')
        .style('paint-order', 'stroke')
        .style('stroke', '#fff')
        .style('stroke-width', effectiveShowAll ? 2 : 1)
        .text(circle.label);

      // 集合(circle)の要素数: show_value_tags overlay target. The on-canvas circle text
      // above is the categorical label; circle.value is otherwise tooltip/drilldown-only,
      // which would leave the producer-grounded set_* id inert. Append a hidden value text
      // tagged data-value-for=<circle id>; the handler reveals (display:null) + bolds it.
      if (circle.value != null) {
        circleGroup.append('text')
          .attr('data-value-for', circle.id || ('venn-' + circlesToRender.indexOf(circle)))
          .attr('x', labelX)
          .attr('y', labelY + 18)
          .attr('text-anchor', ikigaiAnchor?.anchor || 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('fill', '#475569')
          .style('paint-order', 'stroke')
          .style('stroke', '#fff')
          .style('stroke-width', effectiveShowAll ? 2 : 1)
          .style('display', 'none')
          .text(circle.value);
      }

      const clampToCircle = (x, y) => {
        const dx = x - circle.cx;
        const dy = y - circle.cy;
        const dist = Math.hypot(dx, dy);
        if (dist <= circle.radius - 6 || dist === 0) return { x, y };
        const scale = (circle.radius - 6) / dist;
        return { x: circle.cx + dx * scale, y: circle.cy + dy * scale };
      };
      const dragState = { x: labelX, y: labelY };
      const applyDrag = () => {
        labelText.attr('x', dragState.x).attr('y', dragState.y);
        registerPlacedLabel(dragState.x, dragState.y, circle.radius * 0.5, 18);
      };
      const dragBehavior = d3.drag()
        .on('start', () => {
          labelText.style('cursor', 'grabbing');
        })
        .on('drag', (event) => {
          const clamped = clampToCircle(event.x, event.y);
          dragState.x = clamped.x;
          dragState.y = clamped.y;
          applyDrag();
        })
        .on('end', () => {
          labelText.style('cursor', 'grab');
        });
      labelText.classed('ig-draggable', true).style('cursor', 'grab').call(dragBehavior);
    });

    const regionElements = (actualGraphData.elements || []).filter((element) =>
      VENN_REGION_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );
    const regionLabels = [];
    regionElements.forEach((region) => {
      const targetSets = region.sets || region.circles || region.labels || [];
      const matchedCircles = circlesToRender.filter((circle) =>
        targetSets.includes(circle.id) || targetSets.includes(circle.label)
      );
      if (!matchedCircles.length) return;

      const avgX = d3.mean(matchedCircles, (circle) => circle.cx) ?? (margin.left + drawingWidth / 2);
      const avgY = d3.mean(matchedCircles, (circle) => circle.cy) ?? (margin.top + drawingHeight / 2);
      regionLabels.push({
        id: region.id,
        label: region.label || targetSets.join(' ∩ '),
        value: region.value,
        x: avgX,
        y: avgY
      });
    });
    // Simple vertical repel to reduce overlap of region labels
    regionLabels.sort((a, b) => a.y - b.y);
    const minRegionGap = 18;
    for (let i = 1; i < regionLabels.length; i += 1) {
      const prev = regionLabels[i - 1];
      const curr = regionLabels[i];
      if (curr.y - prev.y < minRegionGap) {
        regionLabels[i].y = prev.y + minRegionGap;
      }
    }
    regionLabels.forEach((region) => {
      const text = circleGroup.append('text')
        .attr('x', region.x)
        .attr('y', region.y)
        .attr('text-anchor', 'middle')
        .style('font-size', `${20 * vlScale(layout)}px`)
        .style('font-weight', '600')
        .style('fill', '#1f2937')
        .text(region.label);
      if (region.value != null) {
        // region/intersection count: always-visible; show_value_tags overlay bolds it via
        // data-value-for=<region id> (the producer grounds region ids in all_element_ids).
        circleGroup.append('text')
          .attr('data-value-for', region.id)
          .attr('x', region.x)
          .attr('y', region.y + 14)
          .attr('text-anchor', 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('fill', '#475569')
          .text(region.value);
      }
      text.classed('ig-draggable', true).style('cursor', 'grab').call(d3.drag()
        .on('start', () => text.style('cursor', 'grabbing'))
        .on('drag', (event) => {
          text.attr('x', event.x).attr('y', event.y);
        })
        .on('end', () => text.style('cursor', 'grab')));
    });

    const legendItems = circles.map((circle) => ({
      id: circle.id,
      label: circle.label,
      emoji: circle.emoji,
      color: circle.color,
      visible: effectiveShowAll || circle.visible,
      detail: circle.value != null ? t('ig.chart.valueColon', { value: circle.value }) : undefined
    }));
    renderColorLegend(svg, layout, legendItems, { title: t('ig.chart.set'), rowHeight: 20 });
  }, [attachInteractiveHandlers, getElementColor, graphData, renderColorLegend, renderUpcomingChartPlaceholder, studentProfile]);

  const renderTreeDiagram = useCallback((svg, stageData, layout) => {
    const { width, height, margin } = layout;
    const actualGraphData = graphData.graph_data || graphData || {};
    const nodeElements = (actualGraphData.elements || []).filter((element) =>
      TREE_NODE_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );
    const branchElements = (actualGraphData.elements || []).filter((element) =>
      TREE_BRANCH_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );

    if (!nodeElements.length && !branchElements.length) {
      renderUpcomingChartPlaceholder(svg, stageData, layout);
      return;
    }

    const nodesMap = new Map();
    nodeElements.forEach((node, index) => {
      nodesMap.set(node.id, {
        id: node.id,
        label: node.label || node.name || t('ig.chart.nodeN', { n: index + 1 }),
        parentId: node.parent_id || node.parent || null,
        value: node.value ?? node.weight ?? null,
        probability: node.probability ?? node.chance ?? node.percent ?? null,
        color: node.color,
        children: [],
        raw: node
      });
    });

    branchElements.forEach((branch, index) => {
      const parentId = branch.parent_id || branch.parent || branch.source || null;
      const childId = branch.child_id || branch.child || branch.target || `branch_child_${index}`;
      if (!nodesMap.has(parentId) && parentId) {
        nodesMap.set(parentId, {
          id: parentId,
          label: branch.parent_label || parentId,
          parentId: null,
          children: [],
          raw: branch
        });
      }
      if (!nodesMap.has(childId)) {
        nodesMap.set(childId, {
          id: childId,
          label: branch.child_label || t('ig.chart.nodeN', { n: nodesMap.size + 1 }),
          parentId,
          value: branch.value ?? branch.weight ?? null,
          probability: branch.probability ?? branch.chance ?? null,
          children: [],
          raw: branch
        });
      } else {
        const existing = nodesMap.get(childId);
        if (!existing.parentId && parentId) {
          existing.parentId = parentId;
        }
      }
    });

    if (!nodesMap.size) {
      renderUpcomingChartPlaceholder(svg, stageData, layout);
      return;
    }

    nodesMap.forEach((node) => {
      if (node.parentId && nodesMap.has(node.parentId)) {
        const parent = nodesMap.get(node.parentId);
        parent.children = parent.children || [];
        if (!parent.children.includes(node)) {
          parent.children.push(node);
        }
      }
    });

    const roots = Array.from(nodesMap.values()).filter((node) => !node.parentId);
    const rootNode = roots[0] || nodesMap.values().next().value;
    if (!rootNode) {
      renderUpcomingChartPlaceholder(svg, stageData, layout);
      return;
    }

    const hierarchyRoot = d3.hierarchy(rootNode, (node) => node.children || []);
    const treeLayout = d3.tree().size([height - margin.top - margin.bottom, width - margin.left - margin.right]);
    treeLayout(hierarchyRoot);

    const visibleElements = stageData.visible_elements || [];
    const stageVisibility = createStageVisibilityChecker(visibleElements);
    const hasVisibilityConstraints = visibleElements.length > 0;
    const isShowAllStage = stageData.show_all_with_original_style === true || !hasVisibilityConstraints;

    const linkGenerator = d3.linkHorizontal()
      .x((d) => margin.left + d.y)
      .y((d) => margin.top + d.x);

    const linksGroup = svg.append('g');
    linksGroup.selectAll('path.tree-link')
      .data(hierarchyRoot.links())
      .join('path')
      .attr('d', linkGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 2)
      .attr('opacity', (link) => {
        const targetVisible = stageVisibility(link.target.data.id, link.target.data.label);
        return (isShowAllStage || targetVisible) ? 0.85 : 0.25;
      });

    const nodesGroup = svg.append('g');
    const defaultColor = '#38bdf8';
    nodesGroup.selectAll('g.tree-node')
      .data(hierarchyRoot.descendants())
      .join('g')
      .attr('data-element-id', (node, index) => node.data.id || 'tree-node-' + index)
      .attr('transform', (node) => `translate(${margin.left + node.y}, ${margin.top + node.x})`)
      .each(function(node) {
        const nodeColor = getElementColor(node.data.id, node.data.color || defaultColor);
        const isVisible = stageVisibility(node.data.id, node.data.label);
        const showNode = isShowAllStage || !hasVisibilityConstraints || isVisible;
        const emphasis = showNode ? 'focus' : 'muted';
        const focusStyle = getElementFocusStyle({
          baseColor: nodeColor,
          fallbackColor: '#94a3b8',
          isVisible: showNode,
          isHighlighted: node.depth <= 1 && showNode,
          isShowAllStage: showNode,
          difficultyType: getDifficultyTypeForStage(stageData, graphData, studentProfile),
          elementId: node.data.id
        });

        d3.select(this).append('circle')
          .attr('r', 16)
          .attr('fill', focusStyle.color || nodeColor)
          .attr('fill-opacity', emphasis === 'focus' ? 1 : 0.5)
          .attr('stroke', focusStyle.strokeColor || nodeColor)
          .attr('stroke-width', 2)
          .style('cursor', 'pointer')
          .call((selection) => {
            attachInteractiveHandlers(selection, {
              getElementId: () => node.data.id,
              getPayload: () => ({
                title: node.data.label,
                subtitle: node.data.probability != null ? t('ig.chart.probabilityColon', { value: node.data.probability }) : undefined,
                detailLines: node.data.value != null ? [t('ig.chart.valueColon', { value: node.data.value })] : [],
                icon: getSmartEmojiForLabel(node.data.label)
              }),
              isInteractive: () => showNode,
              onDrilldown: () => ({
                title: t('ig.chart.detail', { label: node.data.label }),
                description: node.data.raw?.description || stageData?.stage_summary || '',
                detailLines: [
                  node.data.value != null ? t('ig.chart.valueColon', { value: node.data.value }) : null,
                  node.data.probability != null ? t('ig.chart.probabilityColon', { value: node.data.probability }) : null
                ].filter(Boolean),
                icon: getSmartEmojiForLabel(node.data.label),
                variant: 'tree'
              })
            });
          });

        d3.select(this).append('text')
          .attr('x', 24)
          .attr('y', 4)
          .style('font-size', `${20 * vlScale(layout)}px`)
          .style('font-weight', showNode ? '700' : '400')
          .style('fill', showNode ? '#0f172a' : '#94a3b8')
          .text(node.data.label);

        if (node.data.probability != null) {
          d3.select(this).append('text')
            .attr('data-value-for', node.data.id)
            .attr('x', 24)
            .attr('y', 18)
            .style('font-size', `${20 * vlScale(layout)}px`)
            .style('fill', '#475569')
            .text(t('ig.chart.probabilityColon', { value: node.data.probability }));
        }
      });

    const legendItems = hierarchyRoot.children
      ? hierarchyRoot.children.map((node) => ({
          id: node.data.id,
          label: node.data.label,
          color: getElementColor(node.data.id, node.data.color || '#38bdf8'),
          detail: node.data.probability != null ? t('ig.chart.probabilityColon', { value: node.data.probability }) : undefined,
          visible: isShowAllStage || !hasVisibilityConstraints || stageVisibility(node.data.id, node.data.label)
        }))
      : [];
    if (legendItems.length) {
      renderColorLegend(svg, layout, legendItems, { title: t('ig.chart.mainBranches') });
    }
  }, [attachInteractiveHandlers, getElementColor, getElementFocusStyle, graphData, renderColorLegend, renderUpcomingChartPlaceholder, studentProfile]);

  const renderDotPlot = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis } = layout;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const actualGraphData = graphData.graph_data || graphData || {};
    let dotElements = (actualGraphData.elements || []).filter((element) =>
      DOT_ELEMENT_TYPES.has((element.type || '').toLowerCase())
    );

    if (!dotElements.length) {
      dotElements = (actualGraphData.elements || []).filter((element) => Array.isArray(element?.data_points) && element.data_points.length > 0);
    }

    if (!dotElements.length) {
      renderGenericChart(svg, stageData, layout);
      return;
    }

    const axisElements = (actualGraphData.elements || []).filter(
      (element) => (element.type || '').toLowerCase() === 'axis'
    );
    const xAxisElement = axisElements.find(
      (axisElement) => (axisElement.axis || axisElement.orientation || '').toLowerCase() === 'x'
    );
    const normalizeNumericAxisValue = (value) => {
      if (Number.isFinite(value)) return Number(value);
      if (typeof value === 'string') {
        const cleaned = value.replace(/[^\d.\-]/g, '');
        const parsed = Number(cleaned);
        if (Number.isFinite(parsed)) return parsed;
      }
      return null;
    };
    const candidateAxisLists = [];
    if (Array.isArray(xAxisElement?.values)) candidateAxisLists.push(xAxisElement.values);
    if (Array.isArray(xAxisElement?.ticks)) candidateAxisLists.push(xAxisElement.ticks);
    if (Array.isArray(stageData?.x_values)) candidateAxisLists.push(stageData.x_values);
    if (Array.isArray(actualGraphData?.axes?.x_values)) candidateAxisLists.push(actualGraphData.axes.x_values);
    const normalizedAxisValues = candidateAxisLists.length
      ? Array.from(
        new Set(
          candidateAxisLists.flat()
            .map((value) => normalizeNumericAxisValue(value))
            .filter((value) => Number.isFinite(value))
        )
      ).sort((a, b) => a - b)
      : [];

    const visibleElements = stageData.visible_elements || [];
    const stageFlags = getStageVisibilityFlags(stageData);
    const stageVisibility = createStageVisibilityChecker(visibleElements);
    const hasVisibilityConstraints = visibleElements.length > 0;
    const axesVisible = stageFlags.showAxisLines || stageData.show_all_with_original_style === true;
    const showAxisLabels = stageFlags.showAxisLabels || stageData.show_all_with_original_style === true;
    const showTickLabels = stageFlags.showTickLabels || stageData.show_all_with_original_style === true;
    const showLegend = stageFlags.showLegend || stageData.show_all_with_original_style === true;
    const showData = stageFlags.showData || stageData.show_all_with_original_style === true;
    const isShowAllStage = stageData.show_all_with_original_style === true || !hasVisibilityConstraints;

    const normalizeDotElement = (element, fallbackIndex) => {
      const baseColor = getElementColor(element.id, element.color || EXTENDED_COLOR_PALETTE[getConsistentIndexFromId(element.id) % EXTENDED_COLOR_PALETTE.length]);
      const directValue = normalizeNumericAxisValue(
        element.value ?? element.x ?? element.x_value ?? element.y
      );
      const baseDataPoints = Array.isArray(element.data_points) && element.data_points.length
        ? element.data_points
        : null;
      if (directValue != null && !baseDataPoints) {
        const stackIndex = Number.isFinite(element.stack_index)
          ? element.stack_index
          : Number.isFinite(element.y)
            ? element.y
            : 0;
        return [{
          id: element.id,
          label: element.label || t('ig.chart.dataN', { n: fallbackIndex + 1 }),
          value: directValue,
          stackIndex,
          color: baseColor,
          emoji: element.emoji || element.icon || element.symbol || null,
          element
        }];
      }
      if (!baseDataPoints) {
        return [{
          id: element.id,
          label: element.label || t('ig.chart.dataN', { n: fallbackIndex + 1 }),
          value: directValue ?? fallbackIndex + 1,
          stackIndex: fallbackIndex,
          color: baseColor,
          emoji: element.emoji || element.icon || element.symbol || null,
          element
        }];
      }
      return baseDataPoints.map((point, pointIndex) => {
        const numericValue = normalizeNumericAxisValue(point.value ?? point.x ?? point.x_value ?? element.value);
        const stackIndex = Number.isFinite(point.stack_index)
          ? point.stack_index
          : Number.isFinite(point.y)
            ? point.y
            : pointIndex;
        return {
          id: point.id || `${element.id}_p${pointIndex}`,
          label: point.label || element.label || t('ig.chart.dataN', { n: pointIndex + 1 }),
          value: numericValue ?? pointIndex,
          stackIndex,
          color: baseColor,
          emoji: element.emoji || element.icon || element.symbol || null,
          element
        };
      });
    };

    const dotElementMap = new Map(dotElements.map((element) => [element.id, element]));
    const stageVisibleDotElements = new Set(
      (stageData.visible_elements || [])
        .map((elementId) => (dotElementMap.has(elementId) ? elementId : null))
        .filter(Boolean)
    );

    const normalizedDots = dotElements
      .map((element, index) => normalizeDotElement(element, index))
      .flat()
      .filter((dot) => Number.isFinite(dot.value));

    const allDotValues = normalizedDots.map((dot) => dot.value);
    const axisDomainReference = normalizedAxisValues.length ? normalizedAxisValues : allDotValues;
    const fallbackDomain = axisDomainReference.length ? axisDomainReference : [0, 1];
    const xMin = Math.min(...fallbackDomain);
    const xMax = Math.max(...fallbackDomain);
    const domainSpan = Math.max(xMax - xMin, 0.0001);
    const padding = normalizedAxisValues.length
      ? Math.max(domainSpan * 0.12, 0.15)
      : Math.max(domainSpan * 0.15, 1.2);

    let dotsToRender = (showData || isShowAllStage)
      ? normalizedDots
      : normalizedDots.filter((dot) => {
        const matchesDotId = stageVisibility(dot.id, dot.label, dot.element?.id);
        const matchesElementId = dot.element && stageVisibleDotElements.has(dot.element.id);
        return matchesDotId || matchesElementId;
      });

    if (!dotsToRender.length && normalizedDots.length && stageVisibleDotElements.size) {
      dotsToRender = normalizedDots.filter((dot) => dot.element && stageVisibleDotElements.has(dot.element.id));
    }
    const groupedDots = dotsToRender.reduce((map, dot) => {
      const key = dot.value.toFixed(4);
      if (!map.has(key)) {
        map.set(key, { value: dot.value, dots: [] });
      }
      map.get(key).dots.push(dot);
      return map;
    }, new Map());

    const sortedGroups = Array.from(groupedDots.values()).sort((a, b) => a.value - b.value);
    const legendGroups = Array.from(
      normalizedDots.reduce((map, dot) => {
        const key = dot.value.toFixed(4);
        if (!map.has(key)) {
          map.set(key, {
            id: `dot-legend-${key}`,
            label: t('ig.chart.valueSpace', { value: dot.value }),
            count: 0,
            detail: t('ig.chart.countItems', { count: 0 }),
            color: dot.color || '#38bdf8',
            emoji: dot.emoji || getSmartEmojiForLabel(dot.label)
          });
        }
        const entry = map.get(key);
        entry.count += 1;
        entry.detail = t('ig.chart.countItems', { count: entry.count });
        return map;
      }, new Map()).values()
    );
    const linearXScale = d3.scaleLinear()
      .domain([xMin - padding, xMax + padding])
      .range([margin.left + 40, width - margin.right]);
    const valuesForScale = sortedGroups.map((group) => group.value);
    const pointScale = d3.scalePoint()
      .domain(valuesForScale)
      .range([margin.left + 40, width - margin.right])
      .padding(Math.min(0.9, Math.max(0.4, 1 / Math.max(valuesForScale.length, 1))));
    const getXPos = (value) => pointScale(value) ?? linearXScale(value);
    const baselineY = height - margin.bottom - 40;
    const dotSpacing = 18;

    if (showData) {
      sortedGroups.forEach((group) => {
      const dots = group.dots
        .sort((a, b) => (a.stackIndex ?? 0) - (b.stackIndex ?? 0))
        .map((dot, index) => ({ ...dot, stackIndex: dot.stackIndex ?? index }));
      dots.forEach((dot) => {
        const cy = baselineY - dot.stackIndex * dotSpacing;
        const showLine = dot.stackIndex === 0;
        if (showLine) {
          svg.append('line')
            .attr('x1', getXPos(group.value) - 8)
            .attr('x2', getXPos(group.value) + 8)
            .attr('y1', baselineY + 2)
            .attr('y2', baselineY + 2)
            .attr('stroke', '#0f172a')
            .attr('stroke-width', 1.5);
        }
        svg.append('circle')
          .attr('data-element-id', dot.id || 'dot-' + dotsToRender.indexOf(dot))
          .attr('cx', getXPos(group.value))
          .attr('cy', cy)
          .attr('r', 8)
          .attr('fill', dot.color || '#ef4444')
          .attr('stroke', DEFAULT_STAT_OUTLINE_COLOR)
          .attr('stroke-width', 1.5)
          .style('cursor', 'pointer')
          .call((selection) => {
            attachInteractiveHandlers(selection, {
              getElementId: () => dot.id,
              getPayload: () => ({
                title: dot.label,
                subtitle: t('ig.chart.valueColon', { value: group.value }),
                detailLines: [t('ig.chart.peopleCount', { value: dot.stackIndex + 1 })],
                icon: dot.emoji || getSmartEmojiForLabel(dot.label)
              }),
              isInteractive: () => true,
              onDrilldown: () => ({
                title: t('ig.chart.detail', { label: dot.label }),
                description: t('ig.chart.visualAcuityHint', { value: group.value }),
                detailLines: [t('ig.chart.peopleCount', { value: dot.stackIndex + 1 })],
                icon: dot.emoji || getSmartEmojiForLabel(dot.label),
                variant: 'dot'
              })
            });
          });
      });
      });
    }

    // トレンド線描画（実線）
    if (stageData.show_trend_line && sortedGroups.length > 1) {
      const trendPoints = sortedGroups.map(group => ({
        x: getXPos(group.value),
        y: baselineY - (group.dots.length - 1) * dotSpacing
      }));
      const lineGen = d3.line()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveMonotoneX);
      svg.append('path')
        .datum(trendPoints)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 2)
        .attr('opacity', 0.8);
    }

    const axisTickValues = normalizedAxisValues.length ? normalizedAxisValues : sortedGroups.map((group) => group.value);
    const axisGenerator = d3.axisBottom(pointScale);
    if (axisTickValues.length) {
      axisGenerator.tickValues(axisTickValues).tickFormat((tick) => `${tick}`);
    } else {
      axisGenerator.ticks(6);
    }

    if (axesVisible) {
      const xAxis = svg.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(axisGenerator)
        .style('font-size', `${axis?.xTickFontSize || 20}px`);
      xAxis.selectAll('line').attr('stroke', '#94a3b8');
      xAxis.selectAll('path').attr('stroke', '#94a3b8').attr('stroke-width', 2);
      applyAxisTextStyles(xAxis.selectAll('text'), labelColor, axisWeight);
      const minTickSpacingPx = 34;
      const baseYOffset = 14;
      const extraRowOffset = 12;
      if (!showTickLabels) {
        xAxis.selectAll('text').text('');
      } else {
        xAxis.selectAll('text')
          .attr('text-anchor', 'middle')
          .attr('transform', function(d, index) {
            const currentX = getXPos(d);
            const lastPlaced = xAxis
              .selectAll('text')
              .filter((_, i) => i < index)
              .nodes()
              .map((node) => {
                const datum = d3.select(node).datum();
                return Number.isFinite(datum) ? getXPos(datum) : null;
              })
              .filter((value) => Number.isFinite(value));
            const isCrowded = lastPlaced.some((prev) => Math.abs(prev - currentX) < minTickSpacingPx);
            const yOffset = isCrowded ? baseYOffset + extraRowOffset : baseYOffset;
            return `translate(0,${yOffset}) rotate(32)`;
          });
      }

      if (showAxisLabels) {
        const xAxisLabel = xAxisElement?.label || stageData?.x_label || t('ig.chart.value');
        const yAxisLabel = stageData?.y_label || t('ig.chart.count');
        svg.append('text')
          .attr('x', (width - margin.left - margin.right) / 2 + margin.left)
          .attr('y', height - margin.bottom + 56)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(xAxisLabel);
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(height / 2))
          .attr('y', margin.left - 52)
          .attr('text-anchor', 'middle')
          .style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('font-weight', axisWeight)
          .style('fill', labelColor)
          .text(yAxisLabel);
      }
    }

    if (showLegend && legendGroups.length > 0) {
      renderColorLegend(svg, layout, legendGroups, {
        title: t('ig.chart.dotDistribution'),
        rowHeight: 18,
        extraHeight: 34,
        bottomOffset: 28
      });
    }
  }, [
    attachInteractiveHandlers,
    axisFontWeight,
    getElementColor,
    graphData,
    renderGenericChart,
    renderColorLegend,
    renderUpcomingChartPlaceholder,
    resolvedLabelColor,
    studentProfile
  ]);

  // ─── renderUnitCircle ──────────────────────────────────────────────────────
  const renderUnitCircle = useCallback((svg, stageData, layout, trigModeOverride = null) => {
    const { width, height, margin, axis } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const trigMode = trigModeOverride || stageData?.trig_display_mode || 'curve_only';

    // Square chart area
    const cw = width - margin.left - margin.right;
    const ch = height - margin.top - margin.bottom;
    const side = Math.min(cw, ch);
    const cx = margin.left + cw / 2;
    const cy = margin.top + ch / 2;
    const r = side * 0.35;  // circle radius in px

    const scale = d3.scaleLinear().domain([-1.5, 1.5]).range([-side / 2, side / 2]);

    // Axes through origin
    svg.append('line')
      .attr('x1', cx - side / 2).attr('y1', cy)
      .attr('x2', cx + side / 2).attr('y2', cy)
      .attr('stroke', '#64748b').attr('stroke-width', 1.5);
    svg.append('line')
      .attr('x1', cx).attr('y1', cy - side / 2)
      .attr('x2', cx).attr('y2', cy + side / 2)
      .attr('stroke', '#64748b').attr('stroke-width', 1.5);

    // Axis labels
    ['1', '-1'].forEach((lbl, i) => {
      const v = i === 0 ? 1 : -1;
      svg.append('text').attr('x', cx + scale(v)).attr('y', cy + 18)
        .attr('text-anchor', 'middle').style('font-size', `${axis?.xTickFontSize || 20}px`).style('fill', '#64748b').text(lbl);
      svg.append('text').attr('x', cx - 18).attr('y', cy - scale(v) + 4)
        .attr('text-anchor', 'middle').style('font-size', `${axis?.yTickFontSize || 20}px`).style('fill', '#64748b').text(lbl);
    });
    svg.append('text').attr('x', cx + side / 2 - 10).attr('y', cy - 8)
      .style('font-size', `${axis?.xLabelFontSize || 20}px`).style('fill', '#334155').style('font-weight', '600').text('x');
    svg.append('text').attr('x', cx + 10).attr('y', cy - side / 2 + 14)
      .style('font-size', `${axis?.yLabelFontSize || 20}px`).style('fill', '#334155').style('font-weight', '600').text('y');

    // Unit circle
    svg.append('circle')
      .attr('cx', cx).attr('cy', cy).attr('r', r)
      .attr('fill', 'none').attr('stroke', '#2563eb').attr('stroke-width', 2);

    if (trigMode === 'axes_only') return;

    // Arc from 0 to π/2 (highlighted)
    const arc = d3.arc()
      .innerRadius(r).outerRadius(r)
      .startAngle(-Math.PI / 2).endAngle(0);  // SVG angles start at top
    svg.append('path')
      .attr('d', arc)
      .attr('transform', `translate(${cx},${cy})`)
      .attr('fill', 'none').attr('stroke', '#f97316').attr('stroke-width', 3)
      .attr('opacity', shouldAnimate ? 0 : 1)
      .call((selection) => {
        if (shouldAnimate) {
          selection.transition().duration(600).attr('opacity', 1);
        }
      });

    // Point at π/4 (45°) on circle
    const angle = Math.PI / 4;
    const px = cx + r * Math.cos(angle);
    const py = cy - r * Math.sin(angle);
    svg.append('circle')
      .attr('cx', px).attr('cy', py).attr('r', 5)
      .attr('fill', '#ef4444').attr('stroke', '#fff').attr('stroke-width', 1.5);

    if (trigMode === 'curve_only') return;

    // Key angle dots (0°, 90°, 180°, 270°)
    const keyAngles = [
      { a: 0,            name: '0',   coord: '(1, 0)' },
      { a: Math.PI / 2,  name: 'π/2', coord: '(0, 1)' },
      { a: Math.PI,      name: 'π',   coord: '(-1, 0)' },
      { a: 3 * Math.PI / 2, name: '3π/2', coord: '(0, -1)' },
    ];
    const showCoords = trigMode && ['show_keypoint_coords', 'show_all'].includes(trigMode);
    keyAngles.forEach((ka) => {
      const kx = cx + r * Math.cos(ka.a);
      const ky = cy - r * Math.sin(ka.a);
      svg.append('circle')
        .attr('cx', kx).attr('cy', ky).attr('r', 5)
        .attr('fill', '#2563eb').attr('stroke', '#fff').attr('stroke-width', 1.5)
        .attr('opacity', shouldAnimate ? 0 : 1)
        .call((selection) => {
          if (shouldAnimate) {
            selection.transition().duration(400).attr('opacity', 1);
          }
        });
      if (showCoords) {
        svg.append('text')
          .attr('x', kx + (Math.cos(ka.a) > 0.5 ? 12 : Math.cos(ka.a) < -0.5 ? -12 : 0))
          .attr('y', ky + (Math.sin(ka.a) > 0.5 ? -12 : Math.sin(ka.a) < -0.5 ? 14 : -10))
          .attr('text-anchor', 'middle')
          .style('font-size', `${20 * vlScale(layout)}px`).style('fill', '#334155')
          .text(ka.coord);
      }
    });

    if (trigMode !== 'show_all') return;

    // sin/cos projection lines
    svg.append('line')
      .attr('x1', px).attr('y1', py).attr('x2', px).attr('y2', cy)
      .attr('stroke', '#ef4444').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,3');
    svg.append('line')
      .attr('x1', px).attr('y1', py).attr('x2', cx).attr('y2', py)
      .attr('stroke', '#10b981').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,3');

    // Labels
    svg.append('text')
      .attr('x', px + 8).attr('y', (py + cy) / 2)
      .style('font-size', `${20 * vlScale(layout)}px`).style('fill', '#ef4444').style('font-weight', '600')
      .text('sin θ');
    svg.append('text')
      .attr('x', (px + cx) / 2).attr('y', py - 8)
      .attr('text-anchor', 'middle')
      .style('font-size', `${20 * vlScale(layout)}px`).style('fill', '#10b981').style('font-weight', '600')
      .text('cos θ');
    svg.append('text')
      .attr('x', cx + r * 0.35).attr('y', cy + side * 0.06)
      .style('font-size', `${20 * vlScale(layout)}px`).style('fill', '#f97316').style('font-weight', '600')
      .text('θ = π/4');
  }, []);

  // ─── renderFunctionGraph (曲線のみモード + all function types) ─────────────
  const renderFunctionGraph = useCallback((svg, stageData, layout) => {
    const { width, height, margin, axis } = layout;
    const shouldAnimate = layout.shouldAnimate !== false;
    const labelColor = resolvedLabelColor;
    const axisWeight = axisFontWeight;
    const difficultyType = getDifficultyTypeForStage(stageData, graphData, studentProfile);
    const isShowAllStage = stageData.show_all_with_original_style === true;
    const graphType = (graphData?.graph_data?.graph_type || graphData?.graph_type || '').toLowerCase();
    const isTrig = graphType === 'trigonometric_function';
    const isFunction = FUNCTION_GRAPH_TYPE_SET.has(graphType);
    const resolvedTrigMode = resolveTrigDisplayMode({
      stageData,
      currentStageNum,
      isTrig
    });
    const resolvedFuncMode = resolveFuncDisplayMode({
      stageData,
      isTrig,
      isShowAllStage
    });

    // Check if unit circle mode — delegate to renderUnitCircle
    if (isTrig && trigViewMode === 'unit_circle') {
      renderUnitCircle(svg, stageData, layout, resolvedTrigMode);
      return;
    }

    // Check if dual_pane mode — render both unit circle (left 45%) and trig curve (right 55%)
    if (isTrig && trigViewMode === 'dual_pane') {
      const leftWidth = Math.floor(width * 0.45);
      const rightWidth = width - leftWidth;
      // Left pane: unit circle
      const leftG = svg.append('g').attr('clip-path', 'none');
      const leftLayout = { ...layout, width: leftWidth, margin: { ...margin, right: 10 } };
      renderUnitCircle(leftG, stageData, leftLayout, resolvedTrigMode);
      // Right pane: trig curve — offset via transform
      const rightG = svg.append('g').attr('transform', `translate(${leftWidth}, 0)`);
      const rightLayout = { ...layout, width: rightWidth, margin: { ...margin, left: 40 } };
      // Manually render axes + curves into rightG
      const rWidth = rightLayout.width;
      const rMargin = rightLayout.margin;
      const rActualData = graphData.graph_data || graphData;
      let rFuncElems = (rActualData.elements || []).filter(e =>
        ['function_curve', 'function', 'line'].includes((e.type || '').toLowerCase())
      );
      if (rFuncElems.length > 0) {
        const hasTrigParams = rFuncElems.some(e => {
          const p = e.parameters || e.function_parameters || {};
          return p.kind || p.type || p.amplitude || p.frequency;
        });
        if (!hasTrigParams) rFuncElems = [];
      }
      if (!rFuncElems.length) {
        const defaultParams = { kind: 'sin', amplitude: 1, frequency: 1, phase: 0, shift: 0 };
        const pts = generateMathCurve(defaultParams, 200);
        rFuncElems = [{ id: 'synthetic_func_0', type: 'function_curve', label: 'y = sin x', color: '#2563eb', parameters: defaultParams, data_points: pts }];
      }
      const rFunctionData = rFuncElems.map((func) => {
        const funcColor = getElementColor(func.id, func.color || '#2563eb');
        const mathParams = func.parameters || func.function_parameters || { kind: 'sin', amplitude: 1, frequency: 1, phase: 0, shift: 0 };
        return { id: func.id, label: func.label || 'y = sin x', color: funcColor, curvePoints: generateMathCurve(mathParams, 200), params: mathParams };
      });
      const rAllPts = rFunctionData.flatMap(f => f.curvePoints);
      if (rAllPts.length) {
        const rxExtent = d3.extent(rAllPts, p => p.x);
        const ryExtent = d3.extent(rAllPts, p => p.y);
        const rxPad = ((rxExtent[1] || 1) - (rxExtent[0] || -1)) * 0.08 || 1;
        const ryPad = ((ryExtent[1] || 1) - (ryExtent[0] || -1)) * 0.12 || 1;
        const rxScale = d3.scaleLinear().domain([rxExtent[0] - rxPad, rxExtent[1] + rxPad]).range([rMargin.left, rWidth - rMargin.right]);
        const ryScale = d3.scaleLinear().domain([ryExtent[0] - ryPad, ryExtent[1] + ryPad]).nice().range([height - rMargin.bottom, rMargin.top]);
        // Axes
        const rxAxisY = Math.max(rMargin.top, Math.min(ryScale(0), height - rMargin.bottom));
        const ryAxisX = Math.max(rMargin.left, Math.min(rxScale(0), rWidth - rMargin.right));
        rightG.append('line').attr('x1', rMargin.left).attr('x2', rWidth - rMargin.right).attr('y1', rxAxisY).attr('y2', rxAxisY).attr('stroke', '#cbd5e1').attr('stroke-width', 1);
        rightG.append('line').attr('x1', ryAxisX).attr('x2', ryAxisX).attr('y1', rMargin.top).attr('y2', height - rMargin.bottom).attr('stroke', '#cbd5e1').attr('stroke-width', 1);
        const ryAxisG = rightG.append('g').attr('transform', `translate(${ryAxisX},0)`).call(d3.axisLeft(ryScale).ticks(5));
        ryAxisG.selectAll('text').style('fill', labelColor).style('font-size', `${axis?.yTickFontSize || 20}px`);
        ryAxisG.selectAll('line').attr('stroke', '#64748b');
        ryAxisG.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
        const rxRange = (rxExtent[1] || Math.PI) - (rxExtent[0] || -Math.PI);
        const rTickStep = rxRange > 4 * Math.PI ? Math.PI : Math.PI / 2;
        const rPiTicks = [];
        for (let n = Math.ceil((rxExtent[0] || -Math.PI) / rTickStep); n <= Math.floor((rxExtent[1] || Math.PI) / rTickStep); n++) rPiTicks.push(n * rTickStep);
        const rxAxisG = rightG.append('g').attr('transform', `translate(0,${rxAxisY})`).call(d3.axisBottom(rxScale).tickValues(rPiTicks).tickFormat(d => formatRadianLabel(d)));
        rxAxisG.selectAll('text').style('fill', labelColor).style('font-size', `${axis?.xTickFontSize || 20}px`);
        rxAxisG.selectAll('line').attr('stroke', '#64748b');
        rxAxisG.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
        // Curves
        const rLineGen = d3.line().x(d => rxScale(d.x)).y(d => ryScale(d.y)).curve(d3.curveLinear);
        rFunctionData.forEach(fi => {
          if (fi.curvePoints.length) {
            rightG.append('path').attr('data-element-id', fi.id).attr('fill', 'none').attr('stroke', fi.color).attr('stroke-width', 3).attr('d', rLineGen(fi.curvePoints));
          }
        });
        // Labels
        rightG.append('text').attr('x', rWidth - rMargin.right + 10).attr('y', rxAxisY + 5).style('font-size', `${axis?.xLabelFontSize || 20}px`).style('fill', labelColor).style('font-style', 'italic').text('x');
        rightG.append('text').attr('x', ryAxisX + 8).attr('y', rMargin.top - 6).style('font-size', `${axis?.yLabelFontSize || 20}px`).style('fill', labelColor).style('font-style', 'italic').text('y');
      }
      // Divider line between panes
      svg.append('line').attr('x1', leftWidth).attr('x2', leftWidth).attr('y1', 0).attr('y2', height).attr('stroke', '#cbd5e1').attr('stroke-width', 1).attr('stroke-dasharray', '4,4');
      return;
    }

    const actualData = graphData.graph_data || graphData;
    const allElements = actualData.elements || [];
    let funcElems = allElements.filter(e =>
      ['function_curve', 'function', 'line'].includes((e.type || '').toLowerCase())
    );

    // For trig override: if none of the matched elements have trig params,
    // they're from a non-trig detection (e.g., unit_circle lines) — discard them
    // so the synthetic fallback triggers a proper sine curve
    if (isTrig && funcElems.length > 0) {
      const hasTrigParams = funcElems.some(e => {
        const p = e.parameters || e.function_parameters || {};
        return p.kind || p.type || p.amplitude || p.frequency;
      });
      if (!hasTrigParams) funcElems = [];
    }

    const visibleElements = stageData.visible_elements || [];
    const axesVisible = visibleElements.some(id => id.includes('axis'));

    // Synthesize curve when no function elements (graph-type override case)
    let isSynthetic = false;
    if (!funcElems.length && FUNCTION_GRAPH_TYPE_SET.has(graphType)) {
      isSynthetic = true;
      const defaultParams = isTrig ? { kind: 'sin', amplitude: 1, frequency: 1, phase: 0, shift: 0 } : {};
      const pts = isTrig ? generateMathCurve(defaultParams, 200) : generateSyntheticCurve(graphType);
      funcElems = [{
        id: 'synthetic_func_0', type: 'function_curve',
        label: isTrig ? 'y = sin x' : t('ig.chart.function'),
        color: '#2563eb',
        parameters: defaultParams,
        data_points: pts,
      }];
    }

    if (!funcElems.length) {
      renderGenericChart(svg, stageData, layout);
      return;
    }

    const normalizeCurvePoints = (points = []) => {
      if (!points.length) return [];
      const sorted = [...points].sort((a, b) => {
        if (a.x === b.x) return a.y - b.y;
        return a.x - b.x;
      });
      const deduped = [];
      const xEpsilon = 1e-6;
      sorted.forEach((point) => {
        const prev = deduped[deduped.length - 1];
        if (prev && Math.abs(point.x - prev.x) <= xEpsilon) {
          deduped[deduped.length - 1] = point;
        } else {
          deduped.push(point);
        }
      });
      return deduped;
    };

    // Build function data array
    const defaultColors = getCUDPalette(settings?.colorAccessibilityPreset);
    const functionData = funcElems.map((func, i) => {
      const isVisible = isSynthetic || visibleElements.includes(func.id);
      const funcColor = getElementColor(func.id, func.color || defaultColors[i % defaultColors.length]);
      const focusStyle = getElementFocusStyle({
        baseColor: funcColor, fallbackColor: '#94a3b8',
        isVisible: isSynthetic || isShowAllStage || isVisible,
        isHighlighted: isVisible, isShowAllStage: isShowAllStage || isSynthetic,
        difficultyType,
        elementId: func.id
      });
      const curvePoints = (func.data_points || [])
        .map(p => ({ x: Number(p.x), y: Number(p.y) }))
        .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));
      return {
        id: func.id,
        label: func.label || func.expression || t('ig.chart.functionN', { n: i + 1 }),
        color: funcColor,
        focusStyle,
        curvePoints: normalizeCurvePoints(curvePoints),
        params: func.parameters || func.function_parameters || {},
        visible: isVisible,
      };
    });

    // For trig: always replace with mathematically exact curve
    // Use detected params if available, otherwise default to sin(x)
    if (isTrig) {
      functionData.forEach(fi => {
        const p = fi.params;
        const hasTrigInfo = p && (p.kind || p.type || p.amplitude || p.frequency);
        const mathParams = hasTrigInfo ? p : { kind: 'sin', amplitude: 1, frequency: 1, phase: 0, shift: 0 };
        fi.curvePoints = generateMathCurve(mathParams, 200);
      });
    }

    const allPts = functionData.flatMap(f => f.curvePoints);
    if (!allPts.length) { renderGenericChart(svg, stageData, layout); return; }

    // Y-clamp for trig (avoids tan asymptote blowout)
    let yClamp = Infinity;
    if (isTrig) {
      const amps = functionData.map(f => {
        const a = parseFloat(f.params.amplitude) || parseFloat(f.params.a) || 1;
        const s = parseFloat(f.params.shift) || parseFloat(f.params.c) || 0;
        return Math.abs(a) + Math.abs(s);
      });
      yClamp = Math.max(...amps) * 3;
      if (yClamp < 1) yClamp = 3;
    }
    const domainPts = yClamp < Infinity ? allPts.filter(p => Math.abs(p.y) <= yClamp) : allPts;
    const xExtentRaw = d3.extent(allPts, p => p.x);
    const yExtentRaw = d3.extent(domainPts.length > 0 ? domainPts : allPts, p => p.y);
    const xExtent = (Number.isFinite(xExtentRaw[0]) && Number.isFinite(xExtentRaw[1])) ? xExtentRaw : [-1, 1];
    const yExtent = (Number.isFinite(yExtentRaw[0]) && Number.isFinite(yExtentRaw[1])) ? yExtentRaw : [-1, 1];
    const xPad = (xExtent[1] - xExtent[0]) * 0.08 || 1;
    const yPad = (yExtent[1] - yExtent[0]) * 0.12 || 1;

    const xScale = d3.scaleLinear().domain([xExtent[0] - xPad, xExtent[1] + xPad]).range([margin.left, width - margin.right]);
    const yScale = d3.scaleLinear().domain([yExtent[0] - yPad, yExtent[1] + yPad]).nice(5).range([height - margin.bottom, margin.top]);

    // ── Axis positions: centered at origin for all function types, standard edges for others ──
    let yAxisX, xAxisY;
    if (isFunction) {
      yAxisX = Math.max(margin.left, Math.min(xScale(0), width - margin.right));
      xAxisY = Math.max(margin.top, Math.min(yScale(0), height - margin.bottom));
    } else {
      yAxisX = margin.left;
      xAxisY = height - margin.bottom;
    }

    // ── Axes ──
    if (isSynthetic || resolvedTrigMode !== null || resolvedFuncMode !== null || axesVisible || !visibleElements.length) {
      const yTFS = axis?.yTickFontSize || 20;
      const xTFS = axis?.xTickFontSize || 20;

      // For all function types: draw subtle full-width cross lines first (below axis groups)
      if (isFunction) {
        svg.append('line')
          .attr('x1', margin.left).attr('x2', width - margin.right)
          .attr('y1', xAxisY).attr('y2', xAxisY)
          .attr('stroke', '#cbd5e1').attr('stroke-width', 1);
        svg.append('line')
          .attr('x1', yAxisX).attr('x2', yAxisX)
          .attr('y1', margin.top).attr('y2', height - margin.bottom)
          .attr('stroke', '#cbd5e1').attr('stroke-width', 1);
      }

      const yAxisG = svg.append('g').attr('class', 'axis axis-y').attr('data-element-id', 'axis_y').attr('transform', `translate(${yAxisX},0)`).call(d3.axisLeft(yScale).ticks(5));
      yAxisG.selectAll('line').attr('stroke', '#64748b');
      yAxisG.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      yAxisG.selectAll('text').style('fill', labelColor).style('font-weight', axisWeight).style('font-size', `${yTFS}px`);
      // For all function types: suppress "0" tick on Y-axis to avoid overlap at origin crossing
      if (isFunction) {
        yAxisG.selectAll('.tick text').filter(function(d) { return Math.abs(d) < 1e-9; }).style('display', 'none');
      }

      const xAxisG = svg.append('g').attr('class', 'axis axis-x').attr('data-element-id', 'axis_x').attr('transform', `translate(0,${xAxisY})`);
      if (isTrig) {
        const xRange = xExtent[1] - xExtent[0];
        const tickStep = xRange > 4 * Math.PI ? Math.PI : Math.PI / 2;
        const piTicks = [];
        const sp = Math.ceil(xExtent[0] / tickStep);
        const ep = Math.floor(xExtent[1] / tickStep);
        for (let n = sp; n <= ep; n++) piTicks.push(n * tickStep);
        if (piTicks.length > 0) xAxisG.call(d3.axisBottom(xScale).tickValues(piTicks).tickFormat(d => formatRadianLabel(d)));
        else xAxisG.call(d3.axisBottom(xScale).ticks(6));
      } else {
        xAxisG.call(d3.axisBottom(xScale).ticks(8));
      }
      xAxisG.selectAll('line').attr('stroke', '#64748b');
      xAxisG.selectAll('path').attr('stroke', '#64748b').attr('stroke-width', 2);
      xAxisG.selectAll('text').style('fill', labelColor).style('font-weight', axisWeight).style('font-size', `${xTFS}px`);

      // Axis labels
      if (isFunction) {
        // For all function types: always use "x" and "y" labels at axis endpoints (ignore backend labels)
        svg.append('text')
          .attr('x', width - margin.right + 15).attr('y', xAxisY + 5)
          .attr('text-anchor', 'start').style('font-size', `${axis?.xLabelFontSize || 20}px`)
          .style('fill', labelColor).style('font-weight', axisWeight)
          .style('font-style', 'italic').text('x');
        svg.append('text')
          .attr('x', yAxisX + 8).attr('y', margin.top - 8)
          .attr('text-anchor', 'start').style('font-size', `${axis?.yLabelFontSize || 20}px`)
          .style('fill', labelColor).style('font-weight', axisWeight)
          .style('font-style', 'italic').text('y');
      } else {
        const xAx = allElements.find(e => e.id === 'axis_x' || (e.type === 'axis' && e.axis === 'x'));
        const yAx = allElements.find(e => e.id === 'axis_y' || (e.type === 'axis' && e.axis === 'y'));
        if (yAx?.label) svg.append('text').attr('transform', 'rotate(-90)').attr('x', -(height / 2)).attr('y', yAxisX - 45)
          .attr('text-anchor', 'middle').style('font-size', `${axis?.yLabelFontSize || 20}px`).style('fill', labelColor).style('font-weight', axisWeight).text(yAx.label);
        if (xAx?.label) svg.append('text').attr('x', (width - margin.left - margin.right) / 2 + margin.left).attr('y', xAxisY + 50)
          .attr('text-anchor', 'middle').style('font-size', `${axis?.xLabelFontSize || 20}px`).style('fill', labelColor).style('font-weight', axisWeight).text(xAx.label);
      }
    }

    // ── ClipPath ──
    const clipId = `func-clip-${Math.random().toString(36).slice(2, 8)}`;
    svg.append('defs').append('clipPath').attr('id', clipId).append('rect')
      .attr('x', margin.left).attr('y', margin.top)
      .attr('width', width - margin.left - margin.right).attr('height', height - margin.top - margin.bottom);

    // ── Curves (gated: not for axes_only) ──
    const showCurve = resolvedTrigMode ? resolvedTrigMode !== 'axes_only' : true;
    if (showCurve) {
      // With 200 math-generated points, curveLinear is smooth and has zero overshoot.
      // curveMonotoneX for non-trig (preserves data monotonicity for bar/line data).
      const lineGen = d3.line()
        .x(d => xScale(d.x))
        .y(d => yScale(d.y))
        .curve(isTrig ? d3.curveLinear : d3.curveMonotoneX);

      const yRange = (yExtent[1] - yExtent[0]) || 1;
      const clampThreshold = yClamp < Infinity ? yClamp * 0.95 : Infinity;

      // Split curve at discontinuities (only needed for tan and non-trig)
      const splitByContinuity = (points) => {
        const segments = [];
        let current = [];
        const flush = () => {
          if (current.length >= 2) segments.push(current);
          current = [];
        };
        for (let i = 0; i < points.length; i++) {
          const pt = points[i];
          if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y) || Math.abs(pt.y) > clampThreshold) {
            flush(); continue;
          }
          if (!current.length) { current.push(pt); continue; }
          const prev = current[current.length - 1];
          const dx = pt.x - prev.x;
          const dy = pt.y - prev.y;
          // Split at sign-change + large jump (tan asymptote) or non-increasing x
          const signChange = (prev.y > 0 && pt.y < 0) || (prev.y < 0 && pt.y > 0);
          const bigJump = Math.abs(dy) > yRange * 0.5;
          if (dx <= 0 || (signChange && bigJump)) {
            flush(); current.push(pt); continue;
          }
          current.push(pt);
        }
        flush();
        return segments;
      };

      const curveG = svg.append('g').attr('clip-path', `url(#${clipId})`);

      functionData.forEach(fi => {
        if (!fi.visible && !isShowAllStage && !isSynthetic && resolvedTrigMode === null) return;
        if (!fi.curvePoints.length) return;
        const st = fi.focusStyle;

        // sin/cos are continuous — render as single segment, no splitting
        const fiKind = (fi.params.kind || fi.params.type || '').toLowerCase();
        const isContinuous = isTrig && (fiKind.startsWith('sin') || fiKind.startsWith('cos') || !fiKind);
        const segments = isContinuous ? [fi.curvePoints] : splitByContinuity(fi.curvePoints);

        segments.forEach(seg => {
          const pathData = lineGen(seg);
          if (!pathData) return;

          const path = curveG.append('path')
            .attr('class', 'function-curve')
            .attr('data-element-id', fi.id)
            .attr('fill', 'none').attr('stroke', st.color)
            .attr('stroke-width', st.emphasis === 'focus' ? 4 : 2.5)
            .attr('opacity', st.opacity).attr('d', pathData);

          const node = path.node();
          if (!node || typeof node.getTotalLength !== 'function') return;
          try {
            const len = node.getTotalLength();
            if (!Number.isFinite(len) || len <= 0) return;
            if (shouldAnimate) {
              path
                .attr('stroke-dasharray', `${len} ${len}`)
                .attr('stroke-dashoffset', len)
                .transition()
                .duration(800)
                .ease(d3.easeLinear)
                .attr('stroke-dashoffset', 0);
            }
          } catch (error) {
            // Skip animation when the browser cannot compute a valid path length.
          }
        });
      });
    }

    // ── Trig keypoint dots (Stage 3+) ──
    const showDots = isTrig && resolvedTrigMode && ['show_keypoint_dots', 'show_keypoint_coords', 'show_all'].includes(resolvedTrigMode);
    if (showDots) {
      functionData.forEach(fi => {
        const kps = computeTrigKeypoints(fi.params);
        kps.forEach(pt => {
          svg.append('circle')
            .attr('cx', xScale(pt.x)).attr('cy', yScale(pt.y)).attr('r', 6)
            .attr('fill', '#ef4444').attr('stroke', '#fff').attr('stroke-width', 1.5)
            .attr('opacity', shouldAnimate ? 0 : 1)
            .call((selection) => {
              if (shouldAnimate) {
                selection.transition().duration(400).attr('opacity', 1);
              }
            });
          attachInteractiveHandlers(svg.append('circle')
            .attr('cx', xScale(pt.x)).attr('cy', yScale(pt.y)).attr('r', 12)
            .attr('fill', 'transparent').attr('cursor', 'pointer'), {
            getElementId: () => pt.id || pt.name,
            getPayload: () => ({ title: pt.nameKey ? t('ig.chart.kp.' + pt.nameKey) : pt.name, subtitle: `(${formatRadianLabel(pt.x)}, ${pt.y.toFixed(1)})`, detailLines: [], icon: '📐' }),
            isInteractive: () => true,
          });
        });
      });
    }

    // ── Coordinate labels (Stage 4+) ──
    // Trig keypoints are all "always keep"; a greedy no-overlap pass just prevents
    // adjacent keypoint labels (e.g. 最高点 next to an x軸交点) from stacking.
    const showCoords = isTrig && resolvedTrigMode && ['show_keypoint_coords', 'show_all'].includes(resolvedTrigMode);
    if (showCoords) {
      const trigFontSize = 20 * vlScale(layout);
      functionData.forEach(fi => {
        const cands = computeTrigKeypoints(fi.params).map(pt => ({
          x: xScale(pt.x), y: yScale(pt.y), offsetY: computeKeypointLabelOffsets(pt).coordY,
          text: `(${formatRadianLabel(pt.x)}, ${pt.y.toFixed(1)})`, keypoint: true,
        }));
        placeFunctionLabels(cands, { fontSize: trigFontSize, yMin: margin.top + trigFontSize, yMax: height - margin.bottom }).forEach(lbl => {
          if (lbl.leader) {
            svg.append('line')
              .attr('x1', lbl.anchorX).attr('y1', lbl.anchorY)
              .attr('x2', lbl.x).attr('y2', lbl.y - trigFontSize * 0.35)
              .attr('stroke', '#94a3b8').attr('stroke-width', 1).attr('stroke-dasharray', '2,2');
          }
          svg.append('text')
            .attr('x', lbl.x).attr('y', lbl.y)
            .attr('text-anchor', 'middle').style('font-size', `${trigFontSize}px`).style('fill', '#334155')
            .text(lbl.text);
        });
      });
    }

    // ── Point names (Stage 5) ──
    const showNames = isTrig && resolvedTrigMode === 'show_all';
    if (showNames) {
      functionData.forEach(fi => {
        computeTrigKeypoints(fi.params).forEach(pt => {
          const off = computeKeypointLabelOffsets(pt);
          svg.append('text')
            .attr('x', xScale(pt.x)).attr('y', yScale(pt.y) + off.nameY)
            .attr('text-anchor', 'middle').style('font-size', `${20 * vlScale(layout)}px`).style('font-weight', '700').style('fill', '#1e40af')
            .text(pt.nameKey ? t('ig.chart.kp.' + pt.nameKey) : pt.name);
        });
      });
    }

    // ── Non-trig: sample dots (func_display_mode: show_dots / show_coords / show_all) ──
    const funcShowDots = !isTrig && resolvedFuncMode &&
      ['show_dots', 'show_coords', 'show_all'].includes(resolvedFuncMode);
    if (funcShowDots) {
      functionData.forEach(fi => {
        // ~10 evenly-spaced sample dots (function color, r=4)
        const sampleStep = Math.max(1, Math.floor(fi.curvePoints.length / 10));
        fi.curvePoints.filter((_, idx) => idx % sampleStep === 0).forEach(pt => {
          svg.append('circle')
            .attr('cx', xScale(pt.x)).attr('cy', yScale(pt.y)).attr('r', 4)
            .attr('fill', fi.color).attr('stroke', '#fff').attr('stroke-width', 1)
            .attr('opacity', shouldAnimate ? 0 : 1)
            .call((selection) => {
              if (shouldAnimate) {
                selection.transition().duration(400).attr('opacity', 1);
              }
            });
        });
        // Special keypoint dots (red, r=6)
        computeFuncKeypoints(fi.curvePoints, graphType).forEach(kp => {
          svg.append('circle')
            .attr('cx', xScale(kp.x)).attr('cy', yScale(kp.y)).attr('r', 6)
            .attr('fill', '#ef4444').attr('stroke', '#fff').attr('stroke-width', 1.5)
            .attr('opacity', shouldAnimate ? 0 : 1)
            .call((selection) => {
              if (shouldAnimate) {
                selection.transition().duration(400).attr('opacity', 1);
              }
            });
        });
      });
    }

    // ── Non-trig: coordinate labels (func_display_mode: show_coords / show_all) ──
    // Decoupled from the stage flag for show_value_tags: always append + tag
    // data-value-for=<curve id> (the producer grounds func ids in all_element_ids).
    // Visible when func_display_mode requests them; display:none otherwise so the
    // show_value_tags handler can reveal them independently of the stage flag.
    //
    // Density: the always-kept keypoints (max/min/intercepts/origin) are ALWAYS
    // labelled; the number of EXTRA sampled labels is capped by
    // layout.functionLabelBudget (AI default, manual-overridable) and 0 when the
    // "show extra coordinates" toggle is off. A greedy no-overlap pass
    // (placeFunctionLabels) then guarantees no label collides — including
    // keypoint↔keypoint — so the numbers never stack on top of each other.
    const funcShowCoords = !isTrig && resolvedFuncMode &&
      ['show_coords', 'show_all'].includes(resolvedFuncMode);
    if (!isTrig) {
      const extraBudget = layout?.functionLabelsVisible === false
        ? 0
        : Math.max(0, Math.round(layout?.functionLabelBudget ?? 6));
      const funcFontSize = 20 * vlScale(layout);
      const pickEvenly = (arr, n) => {
        if (n <= 0 || arr.length === 0) return [];
        if (arr.length <= n) return arr;
        const out = [];
        for (let i = 0; i < n; i++) out.push(arr[Math.round((i * (arr.length - 1)) / (n - 1 || 1))]);
        return out;
      };
      functionData.forEach(fi => {
        const keypointCands = computeFuncKeypoints(fi.curvePoints, graphType).map(kp => ({
          x: xScale(kp.x), y: yScale(kp.y), offsetY: computeFuncLabelOffsets(kp).coordY,
          text: `(${kp.x.toFixed(1)}, ${kp.y.toFixed(1)})`, keypoint: true,
        }));
        const sampleStep = Math.max(1, Math.floor(fi.curvePoints.length / 10));
        const sampled = fi.curvePoints.filter((_, idx) => idx % sampleStep === 0);
        const sampledCands = pickEvenly(sampled, extraBudget).map(pt => ({
          x: xScale(pt.x), y: yScale(pt.y), offsetY: computeFuncLabelOffsets(pt).coordY,
          text: `(${pt.x.toFixed(1)}, ${pt.y.toFixed(1)})`, keypoint: false,
        }));
        placeFunctionLabels([...keypointCands, ...sampledCands], { fontSize: funcFontSize, yMin: margin.top + funcFontSize, yMax: height - margin.bottom })
          .forEach(lbl => {
            // Connector back to the point for any label the collision pass had to
            // offset — so a re-stacked keypoint number is never left detached.
            if (lbl.leader) {
              const _lead = svg.append('line')
                .attr('x1', lbl.anchorX).attr('y1', lbl.anchorY)
                .attr('x2', lbl.x).attr('y2', lbl.y - funcFontSize * 0.35)
                .attr('stroke', '#94a3b8').attr('stroke-width', 1).attr('stroke-dasharray', '2,2');
              if (!funcShowCoords) _lead.style('display', 'none');
            }
            const _fvlt = svg.append('text')
              .attr('data-value-for', fi.id)
              .attr('x', lbl.x).attr('y', lbl.y)
              .attr('text-anchor', 'middle')
              .style('font-size', `${funcFontSize}px`)
              .style('fill', lbl.keypoint ? '#334155' : '#475569');
            if (lbl.keypoint) _fvlt.style('font-weight', '600');
            _fvlt.text(lbl.text);
            if (!funcShowCoords) _fvlt.style('display', 'none');
          });
      });
    }

    // ── Non-trig: special point names (func_display_mode: show_all only) ──
    const funcShowNames = !isTrig && resolvedFuncMode === 'show_all';
    if (funcShowNames) {
      functionData.forEach(fi => {
        computeFuncKeypoints(fi.curvePoints, graphType).forEach(kp => {
          const off = computeFuncLabelOffsets(kp);
          svg.append('text')
            .attr('x', xScale(kp.x)).attr('y', yScale(kp.y) + off.nameY)
            .attr('text-anchor', 'middle').style('font-size', `${20 * vlScale(layout)}px`)
            .style('font-weight', '700').style('fill', '#1e40af')
            .text(kp.nameKey ? t('ig.chart.kp.' + kp.nameKey) : kp.name);
        });
      });
    }

  // ── Tier 2 overlay rendering (function graph) ──
  const _t2gridF = tier2ActionsRef.current.find(a => a.type === 'show_grid');
  if (_t2gridF) {
    yScale.ticks(5).forEach(tick => {
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yScale(tick)).attr('y2', yScale(tick))
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
    const xTicksF = xScale.ticks ? xScale.ticks(5) : [];
    xTicksF.forEach(tick => {
      svg.append('line').attr('class', 'overlay-grid-line')
        .attr('x1', xScale(tick)).attr('x2', xScale(tick))
        .attr('y1', margin.top).attr('y2', height - margin.bottom)
        .attr('stroke', '#e2e8f0').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
    });
  }
  const _t2projF = tier2ActionsRef.current.find(a => a.type === 'show_projection_lines');
  if (_t2projF) {
    const targetIds = new Set(_t2projF.element_ids || []);
    // Ghost-surface guard — see renderBarChart's _t2proj block (same factory
    // + context as applyOverlay; axis nodes pass via rule 5; read-only).
    // renderFunctionGraph has no local actualGraphData: derive elements from
    // graphData (already in this callback's dep array — zero dep changes).
    const effectiveVisible = createEffectiveVisibilityPredicate({
      ...overlayVisibilityContextRef.current,
      elements: ((graphData.graph_data || graphData)?.elements) || [],
    });
    svg.selectAll('[data-element-id]').each(function() {
      const el = d3.select(this);
      const id = el.attr('data-element-id');
      if ((targetIds.size === 0 || targetIds.has(id)) && effectiveVisible(id)) {
        const bbox = this.getBBox();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y;
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', margin.left).attr('x2', cx).attr('y1', cy).attr('y2', cy)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
        svg.append('line').attr('class', 'overlay-projection')
          .attr('x1', cx).attr('x2', cx).attr('y1', cy).attr('y2', height - margin.bottom)
          .attr('stroke', '#94a3b8').attr('stroke-dasharray', '4,4').attr('stroke-width', 1);
      }
    });
  }
  const _t2pinF = tier2ActionsRef.current.find(a => a.type === 'pin_reference_value');
  if (_t2pinF && _t2pinF.params) {
    const refValue = _t2pinF.params.reference_value;
    if (refValue !== undefined && yScale) {
      const yPos = yScale(refValue);
      svg.append('line').attr('class', 'overlay-pin-reference')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', yPos).attr('y2', yPos)
        .attr('stroke', '#843F1F').attr('stroke-dasharray', '6,3').attr('stroke-width', 1.5);
      svg.append('text').attr('class', 'overlay-pin-label')
        .attr('x', width - margin.right + 4).attr('y', yPos + 4)
        .style('font-size', `${20 * vlScale(layout)}px`).style('fill', 'var(--accent)').style('font-weight', 'bold')
        .text(refValue);
    }
  }

  }, [attachInteractiveHandlers, axisFontWeight, getElementColor, getElementFocusStyle, graphData,
      currentStageNum, renderGenericChart, renderUnitCircle, resolvedLabelColor, studentProfile, trigViewMode]);

  const renderLegend = useCallback((svg, stageData, layout) => {
    const actualGraphData = graphData.graph_data || graphData;
    const lines = actualGraphData.elements?.filter(e => e.type === 'line') || [];
    const visibleElements = stageData.visible_elements || [];
    const showLegend = getStageVisibilityFlags(stageData).showLegend || stageData?.show_all_with_original_style === true;

    if (lines.length === 0 || !showLegend) return;

    const cappedVScale = legendVScaleCapped(layout, lines.length, 24, 18);
    const legendMetrics = getLegendMetrics(layout, lines.length, {
      defaultWidth: layout.legend?.width || 140,
      rowHeight: 24 * cappedVScale,
      extraHeight: 36,
      bottomOffset: 30
    });
    legendMetricsRef.current = legendMetrics;

    console.log('✅ renderLegend: 开始绘制折线图图例');

    const legendFontScale = computeLegendFontScale(legendMetrics) * (layout.legend?.fontScale ?? 1);
    const legendTextColor = legendVisualContext.textColor;
    const legendBgColor = legendVisualContext.backgroundColor;
    const legendHighlightShadow = legendVisualContext.highlightShadow;

    const legendGroup = svg.append('g')
      .attr('class', 'line-legend')
      .attr('transform', `translate(${legendMetrics.x}, ${legendMetrics.y})`);

    legendGroup.append('rect')
      .attr('width', legendMetrics.width)
      .attr('height', legendMetrics.height)
      .attr('fill', legendBgColor)
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 2)
      .attr('rx', 8)
      .style('cursor', 'move')
      .style('touch-action', 'none')
      .on('pointerdown', (event) => startDrag('legend-move', event, { legendMetrics }));

    legendGroup.append('text')
      .attr('x', legendMetrics.position === 'bottom' ? legendMetrics.width / 2 : 0)
      .attr('y', 0)
      .attr('text-anchor', legendMetrics.position === 'bottom' ? 'middle' : 'start')
      .style('font-size', `${10 * legendFontScale}px`)
      .style('font-weight', highlightOptions.legend ? '800' : 'bold')
      .style('fill', legendTextColor)
      .style('text-shadow', legendHighlightShadow)
      .text(`📊 ${t('ig.chart.legend')}`);

    lines.forEach((line, i) => {
      const isVisible = visibleElements.includes(line.id);
      const lineColor = getElementColor(line.id, line.color || '#94a3b8');

      const legendItem = legendGroup.append('g')
        .attr('transform', `translate(0, ${(i * 24 + 18) * cappedVScale})`);

      const statusIcon = isVisible ? '👉' : '⋯';
      legendItem.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('text-anchor', 'start')
        .style('font-size', `${12 * legendFontScale}px`)
        .style('fill', legendTextColor)
        .text(statusIcon);

      legendItem.append('line')
        .attr('x1', 18)
        .attr('y1', -4)
        .attr('x2', 38)
        .attr('y2', -4)
        .attr('stroke', lineColor)
        .attr('stroke-width', 3)
        .attr('opacity', isVisible ? 1 : 0.3);

      const emoji = getSmartEmojiForLabel({
        label: line.label || t('ig.chart.seriesN', { n: i+1 }),
        emoji: line.emoji || line.icon || line.symbol
      });
      legendItem.append('text')
        .attr('x', 42)
        .attr('y', 2)
        .style('font-size', `${12 * legendFontScale}px`)
        .style('fill', legendTextColor)
        .text(emoji);

      const displayLabel = line.label || t('ig.chart.seriesN', { n: i+1 });
      legendItem.append('text')
        .attr('x', 58)
        .attr('y', 2)
        .style('font-size', `${10 * legendFontScale}px`)
        .style('font-weight', isVisible ? 'bold' : 'normal')
        .style('fill', isVisible ? legendTextColor : '#94a3b8')
        .text(displayLabel.substring(0, 18) + (displayLabel.length > 18 ? '...' : ''));
    });

    legendGroup.append('rect')
      .attr('x', legendMetrics.width - 16)
      .attr('y', legendMetrics.height - 16)
      .attr('width', 16)
      .attr('height', 16)
      .attr('fill', 'rgba(37, 99, 235, 0.6)')
      .attr('rx', 3)
      .style('cursor', 'nwse-resize')
      .style('touch-action', 'none')
      .attr('tabindex', 0)
      .attr('role', 'slider')
      .attr('aria-label', t('ig.chart.legendResize'))
      .attr('aria-valuemin', MIN_LEGEND_WIDTH)
      .attr('aria-valuemax', layout.width - MIN_MARGIN)
      .on('pointerdown', (event) => startDrag('legend-resize', event, { legendMetrics }))
      .on('keydown', (event) => handleLegendKeyboard(event, 'resize'));

    decorateLegendGroup(legendGroup, legendMetrics);
  }, [decorateLegendGroup, getElementColor, graphData, handleLegendKeyboard, legendVisualContext, startDrag, highlightOptions.legend, handleElementDrilldown, setIsLayoutPanelOpen]);

  // P0 fix: requestAnimationFrame で複数回の再描画をフレーム単位で合体し、
  // アニメーション重複再生を防止
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
    renderGenerationRef.current += 1;
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);

    // Keyboard focus lives on a D3-owned node, and the clear below destroys
    // it: the browser then drops focus to <body>, and whatever the student
    // presses next — the Enter that should open the drilldown, an Arrow that
    // should move along the bars — goes nowhere. The chart redraws more than
    // once around first paint and again on every stage/overlay change, so
    // this is not a corner case. Remember the element by its id and hand
    // focus to its successor once the draw is done (see applyFurigana, which
    // already runs on every exit path).
    // Line and scatter charts draw several focusable points per series, all
    // sharing that series' data-element-id — so the id alone would send
    // focus to the series' FIRST point after a redraw, and the Enter that
    // follows would open a different point's details (Codex). Draw order is
    // deterministic, so (id, ordinal among same-id nodes) is the stable
    // identity.
    const activeBefore = typeof document !== 'undefined' ? document.activeElement : null;
    let focusedElementId = null;
    let focusedOrdinal = 0;
    if (activeBefore && svgRef.current.contains(activeBefore)) {
      focusedElementId = activeBefore.getAttribute('data-element-id');
      if (focusedElementId) {
        focusedOrdinal = Array.from(svgRef.current.querySelectorAll('.ig-kbd-item'))
          .filter((node) => node.getAttribute('data-element-id') === focusedElementId)
          .indexOf(activeBefore);
        if (focusedOrdinal < 0) focusedOrdinal = 0;
      }
    }

    svg.selectAll('*').interrupt();
    svg.selectAll('*').remove();

    const restoreChartFocus = () => {
      if (!focusedElementId || !svgRef.current) return;
      const sameId = Array.from(svgRef.current.querySelectorAll('.ig-kbd-item'))
        .filter((node) => node.getAttribute('data-element-id') === focusedElementId);
      const successor = sameId[focusedOrdinal] || sameId[0];
      if (!successor) return;
      // Make it the roving entry, exactly as moveRovingFocus does, so Tab
      // order stays a single stop.
      const entry = svgRef.current.querySelector('.ig-kbd-item[tabindex="0"]');
      if (entry && entry !== successor) entry.setAttribute('tabindex', '-1');
      successor.setAttribute('tabindex', '0');
      successor.focus({ preventScroll: true });
    };

    // SVG-native reading aid: run AFTER text is emitted on every exit path below.
    // The toggle is read from a ref, not a dependency: the engine arriving late
    // or the toggle flipping is handled in place by the effect below, which only
    // asks for this full redraw (readingRedrawTick) in its overlay case.
    // The reading pass adds ruby AFTER the caption was placed from a measured
    // bbox, and ruby is often wider than the kanji and inherits the tick
    // rotation — so the band can grow past the caption. Re-measure here, which
    // covers every exit path below for free.
    const applyFurigana = () => {
      renderSvgFurigana(svgRef.current, { enabled: furiganaEnabledRef.current });
      realignAxisTitles(svgRef.current);
      restoreChartFocus();
    };

    // A1 (Tier-1 a11y / Gate B): re-add <title>/<desc> immediately after the
    // full clear above, unconditionally, so every branch below (no-data /
    // upcoming placeholder / real renderer) keeps them. `chartSummaryText`
    // (see buildChartSummary usage near stageSummaryText) is the same string
    // used for the <svg> element's own aria-label (set via JSX — safe from
    // this selectAll('*').remove(), since it targets the ref'd node's
    // attributes, not its children).
    svg.append('title').text(chartSummaryText);
    svg.append('desc').text(chartSummaryText);

    if (!graphData || !stageDataForDisplay) {
      svg
        .append('text')
        .attr('x', '50%')
        .attr('y', '50%')
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', '#94a3b8')
        .text(t('ig.chart.noGraphData'));
      applyFurigana();
      return;
    }

    svg
      .attr('width', finalLayout.width)
      .attr('height', finalLayout.height);

    const stageAnimationKey = buildStageAnimationKey(
      graphData,
      stageDataForDisplay,
      currentStageNum,
      trigViewMode
    );
    const shouldAnimateStage = !playedStageAnimationKeysRef.current.has(stageAnimationKey);
    playedStageAnimationKeysRef.current.add(stageAnimationKey);

    const layoutCopy = {
      ...finalLayout,
      margin: { ...finalLayout.margin },
      legend: { ...finalLayout.legend },
      shouldAnimate: shouldAnimateStage
    };

    const graphType = (stageDataForDisplay.graph_type || graphData.graph_type || '').toLowerCase();

    if (UPCOMING_CHART_TYPES.has(graphType)) {
      renderUpcomingChartPlaceholder(svg, stageDataForDisplay, layoutCopy);
      applyFurigana();
      return;
    }

    const rendererMap = {
      bar_chart: renderBarChart,
      line_chart: renderLineChart,
      pie_chart: renderPieChart,
      scatter_plot: renderScatterPlot,
      histogram: renderHistogram,
      box_plot: renderBoxPlot,
      area_chart: renderAreaChart,
      band_graph: renderBandGraph,
      dot_plot: renderDotPlot,
      venn_diagram: renderVennDiagram,
      tree_diagram: renderTreeDiagram,
      trigonometric_function: renderFunctionGraph,
      linear_function: renderFunctionGraph,
      quadratic_function: renderFunctionGraph,
      exponential_function: renderFunctionGraph,
      logarithmic_function: renderFunctionGraph,
      inverse_proportion: renderFunctionGraph,
      proportion: renderFunctionGraph,
    };

    // Tier 1 / Tier 2 overlay action partitioning
    const TIER2_TYPES = new Set(TIER2_ACTION_TYPES);
    const allUiActions = stageDataForDisplay.ui_actions || [];
    const tier2Actions = [...tier2UiActions, ...allUiActions.filter((action) => TIER2_TYPES.has(action.type))];
    tier2ActionsRef.current = tier2Actions;

    // Auto-switch to dual_pane for trig if backend hints
    if (stageDataForDisplay.trig_layout_hint === 'dual_pane' && isTrigFunction && trigViewMode !== 'dual_pane') {
      setTrigViewMode('dual_pane');
    }

    const renderer = rendererMap[graphType] || renderGenericChart;
    renderer(svg, stageDataForDisplay, layoutCopy);
    if (graphType === 'line_chart') {
      renderLegend(svg, stageDataForDisplay, layoutCopy);
    }

    // ── Post-render stroke enforcement ──────────────────────────────
    // Ensure all visible data elements have a consistent dark outline.
    enforceStrokes(svg);

    // Reading aid over the freshly-emitted axis / legend / label <text>.
    applyFurigana();

    });
    return () => cancelAnimationFrame(rafId);
  }, [
    axisFontWeight,
    chartSummaryText,
    colorOverrides,
    finalLayout,
    getElementColor,
    graphData,
    highlightOptions,
    legendVisualContext,
    renderAreaChart,
    renderBarChart,
    renderBoxPlot,
    renderBandGraph,
    renderDotPlot,
    renderFunctionGraph,
    renderGenericChart,
    renderHistogram,
    renderLegend,
    renderLineChart,
    renderPieChart,
    renderTreeDiagram,
    renderVennDiagram,
    renderScatterPlot,
    renderUpcomingChartPlaceholder,
    currentStageNum,
    isTrigFunction,
    readingRedrawTick,
    stageDataForDisplay,
    tier2UiActions,
    trigViewMode
  ]);

  // Reading aid on the chart that is already drawn. The kuromoji engine finishes
  // building 1–5 s after every page load (longer on a slow CPU), well after the
  // student can start using the chart, and the toggle can flip at any time.
  // Neither changes what the chart shows, so instead of a full redraw — which
  // recreates every D3 node, dropping the hovered element and swapping the
  // focused one — strip the previous reading pass and run it again on these
  // nodes. Turning the aid off removes the readings the same way. The caption
  // goes back to its render-time position before it is re-measured.
  // Exception: overlays sized or placed from text boxes when they were applied
  // (bold_axes backgrounds, show_micro_step panels, annotate_element labels) would
  // no longer fit once readings change those boxes, and the overlay pass cannot be
  // re-run in place exactly (its reset does not undo every style it applies, e.g.
  // the font size it gives the labels inside a highlighted group). While any of
  // them is on screen, fall back to the full redraw — the behaviour before this
  // change — and let the overlay effect re-apply them on the fresh chart.
  // The ref is updated here, in the commit phase, rather than during render: the
  // render and overlay passes run in later animation frames, so they always read
  // the value of the last committed render.
  useEffect(() => {
    furiganaEnabledRef.current = furiganaEnabled;
    const svgNode = svgRef.current;
    if (!svgNode) return;
    if (svgNode.querySelector('.overlay-bold-bg, .overlay-tag')) {
      setReadingRedrawTick((tick) => tick + 1);
      return;
    }
    refreshSvgFurigana(svgNode, { enabled: furiganaEnabled });
    resetAxisTitles(svgNode);
    realignAxisTitles(svgNode);
  }, [furiganaEnabled, furiganaReady]);

  // Overlay useEffect: double-RAF ensures overlays apply AFTER the rendering
  // useEffect's single-RAF completes its SVG clear+redraw cycle.
  //   Frame N:   rendering RAF queued → overlay outerRaf queued (runs after rendering RAF)
  //   Frame N+1: overlay innerRaf fires → restoreOverlay + applyOverlay on fresh SVG
  //
  // Additionally, a delayed re-application (500ms) is scheduled to survive any
  // cascading render cycles that wipe the SVG shortly after the first application.
  // This ensures overlays remain visible for at least OVERLAY_MIN_DISPLAY_MS.
  useEffect(() => {
    if (!svgRef.current) return;
    if (replayState && replayState.status === 'pause') return;

    // Cancel any pending overlay RAF from previous render cycle
    if (overlayOuterRafRef.current) cancelAnimationFrame(overlayOuterRafRef.current);
    if (overlayInnerRafRef.current) cancelAnimationFrame(overlayInnerRafRef.current);

    const doApplyOverlays = () => {
      if (!svgRef.current) return;
      // force=true: clear tier1 overlays + reset data elements before
      // re-applying. preserveTier2: tier2 visuals (grid / projection / pin)
      // are RENDERER-owned (drawn from tier2ActionsRef during the rendering
      // useEffect) — this overlay pass cannot redraw them, so it must not
      // remove what it cannot restore (P4-1: chat-delivered tier2 used to be
      // erased here ~0.4-0.9s after every render). Stage-nav/reset callers
      // keep full clearing — the renderer re-runs right after them.
      restoreOverlay(true, { preserveTier2: true });
      const replayActions = replayState ? (replayState.actions || []) : [];
      // Skip stale uiActions if the user manually navigated stages: element-
      // targeting highlights AND one-shot stage jumps (a stale set_stage here
      // would yank the user away from their chosen stage — second line of
      // defense behind consumedOneShotActionsRef). The flag resets when a new
      // AI batch arrives, so fresh actions are never suppressed.
      const MANUAL_NAV_SUPPRESSED_TYPES = ['highlight_elements', 'focus_single_element', 'dim_irrelevant', 'show_value_tags', 'set_stage', 'restore_stage'];
      const filteredUiActions = stageManuallyChangedRef.current
        ? (uiActions || []).filter((a) => !MANUAL_NAV_SUPPRESSED_TYPES.includes(a?.type))
        : (uiActions || []);
      // WARNING: tier2 actions (TIER2_ACTION_TYPES) are renderer-owned and
      // renderer-consumed ONLY — every rendering-effect pass redraws them from
      // tier2ActionsRef, and the sole legitimate eraser is restoreOverlay's
      // non-preserve pass. Do NOT add a tier2 case to applyOverlay:
      // preserveTier2 (P4-1) assumes the renderer is the only producer, so an
      // applyOverlay-side producer would have no eraser and would accumulate
      // on every re-application pass.
      const combinedActions = [...filteredUiActions, ...replayActions].filter(
        (action) => !TIER2_ACTION_TYPES.includes(action?.type)
      );
      if (combinedActions.length > 0) {
        applyOverlay(combinedActions);
      }
      // Re-enforce base strokes after overlay application — ensures non-highlighted
      // shapes keep their dark outline even if overlay modified sibling elements.
      const svg = d3.select(svgRef.current);
      enforceStrokes(svg);
      // Overlay just appended AI-guidance labels (show_micro_step / annotate_element)
      // as new SVG <text>; give them readings too. Idempotent — chart text already
      // stamped data-furigana in the render effect is skipped. The toggle is not a
      // dependency: readings change in place, and when the in-place effect has to
      // fall back to a redraw, readingRedrawTick re-runs this effect after it.
      renderSvgFurigana(svgRef.current, { enabled: furiganaEnabledRef.current });
    };

    const genAtSchedule = renderGenerationRef.current;

    overlayOuterRafRef.current = requestAnimationFrame(() => {
      overlayInnerRafRef.current = requestAnimationFrame(() => {
        if (renderGenerationRef.current !== genAtSchedule) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[overlay] skipped: renderGeneration changed', genAtSchedule, '->', renderGenerationRef.current);
          }
          return;
        }
        doApplyOverlays();
      });
    });

    // Schedule a debounced re-application to survive cascading rendering cycles.
    // When the rendering useEffect fires multiple times in quick succession
    // (e.g., stage change → stageDataForDisplay recompute), the double-RAF
    // overlay gets wiped. This timer re-applies overlays after the dust settles.
    let debounceTimer = null;
    const hasActions = (uiActions && uiActions.length > 0) ||
      (replayState?.actions && replayState.actions.length > 0);
    if (hasActions) {
      debounceTimer = setTimeout(() => {
        const genAtDebounce = renderGenerationRef.current;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (renderGenerationRef.current !== genAtDebounce) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[overlay] debounce skipped: renderGeneration changed', genAtDebounce, '->', renderGenerationRef.current);
              }
              return;
            }
            doApplyOverlays();
          });
        });
      }, 500);
    }

    return () => {
      if (overlayOuterRafRef.current) cancelAnimationFrame(overlayOuterRafRef.current);
      if (overlayInnerRafRef.current) cancelAnimationFrame(overlayInnerRafRef.current);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    applyOverlay,
    currentStageNum,
    overlayRenderTick,
    readingRedrawTick,
    replayState,
    restoreOverlay,
    stageDataForDisplay,
    trigViewMode,
    uiActions
  ]);

  const parseNumericOverride = (raw, clampRange) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return null;
    if (clampRange) {
      const [min, max] = clampRange;
      return Math.min(Math.max(numeric, min), max);
    }
    return numeric;
  };

  const handleDimensionOverrideChange = (key, rawValue, clampRange) => {
    const parsed = parseNumericOverride(rawValue, clampRange);
    setLayoutOverrides((prev) => ({
      ...prev,
      [key]: parsed
    }));
    // 寸法変更で SVG 再描画 → active overlay クリアのため再適用（overlay-reapply unification、font sliders 同型）。
    setOverlayRenderTick((t) => t + 1);
  };

  const handleMarginOverrideChange = (side, rawValue) => {
    const parsed = parseNumericOverride(rawValue, [20, 400]);
    setLayoutOverrides((prev) => ({
      ...prev,
      margin: {
        ...prev.margin,
        [side]: parsed
      }
    }));
    // active overlay 再適用（overlay-reapply unification、font sliders 同型）。
    setOverlayRenderTick((t) => t + 1);
  };

  // 軸文字サイズを次元別に調整: dimension ∈ {xTick, yTick, xLabel, yLabel} → field = `${dimension}FontSize`。
  // 横軸/縦軸 × 目盛り数字/ラベル の 2×2 を独立制御（4 slider が同 handler を共用）。
  const handleAxisFontSizeChange = (dimension, rawValue) => {
    if (!['xTick', 'yTick', 'xLabel', 'yLabel'].includes(dimension)) return;
    const field = `${dimension}FontSize`;
    const parsed = parseNumericOverride(rawValue, [8, 32]);
    setLayoutOverrides((prev) => ({
      ...prev,
      axis: {
        ...prev.axis,
        [field]: parsed
      }
    }));
    // 軸サイズ変更で SVG が再描画され active overlay がクリアされるため、
    // overlay effect を再実行して再適用する（value-label slider と同型、overlay-reapply unification、v2.14.11 保持 / F1）。
    setOverlayRenderTick((t) => t + 1);
  };

  // 凡例の文字サイズ倍率（自動スケールに対する相対倍率、0.6〜2.0）。
  const handleLegendFontScaleChange = (rawValue) => {
    const parsed = parseNumericOverride(rawValue, [0.6, 2]);
    setLayoutOverrides((prev) => ({
      ...prev,
      legendFontScale: parsed
    }));
    // active overlay 再適用（overlay-reapply unification、value-label slider 同型）。
    setOverlayRenderTick((t) => t + 1);
  };

  // 凡例の行間倍率（0.8〜2.5）。行の縦間隔と凡例ボックス高さに適用。
  const handleLegendLineSpacingChange = (rawValue) => {
    const parsed = parseNumericOverride(rawValue, [0.8, 2.5]);
    setLayoutOverrides((prev) => ({
      ...prev,
      legendLineSpacing: parsed
    }));
    // active overlay 再適用（overlay-reapply unification、value-label slider 同型）。
    setOverlayRenderTick((t) => t + 1);
  };

  // データ値ラベル（要素上の数値）の文字サイズ倍率（0.6〜2.5）。focus/非focus 比率は保持。
  const handleValueLabelScaleChange = (rawValue) => {
    const parsed = parseNumericOverride(rawValue, [0.6, 2.5]);
    setLayoutOverrides((prev) => ({
      ...prev,
      valueLabelScale: parsed
    }));
    // P3: 値ラベル倍率変更で SVG が再描画され active overlay がクリアされるため、
    // overlay effect を再実行して新倍率で再適用する（show_value_tags 等の after-trigger 整合）。
    setOverlayRenderTick((t) => t + 1);
  };

  // 関数グラフの「追加座標ラベル数」（0〜MAX）。キーポイント（最高/最低/軸交点/原点）は
  // 常時保持し、それ以外の間引き対象サンプル点の表示数のみを制御する。
  const handleFunctionLabelDensityChange = (rawValue) => {
    const parsed = parseNumericOverride(rawValue, [0, FUNCTION_LABEL_BUDGET_MAX]);
    setLayoutOverrides((prev) => ({
      ...prev,
      functionLabelDensity: parsed === null ? null : Math.round(parsed),
    }));
    setOverlayRenderTick((t) => t + 1);
  };

  // 追加座標ラベルの表示/非表示トグル。OFF でもキーポイントラベルは常に表示のまま。
  const handleFunctionLabelsVisibleToggle = (visible) => {
    setLayoutOverrides((prev) => ({
      ...prev,
      functionLabelsVisible: visible,
    }));
    setOverlayRenderTick((t) => t + 1);
  };

  const handleToggleElementVisibility = useCallback((elementId) => {
    if (!stageDataForDisplay || !elementId) return;
    const currentVisibleSet = new Set(stageDataForDisplay.visible_elements || []);
    const nextVisible = !currentVisibleSet.has(elementId);
    setElementVisibilityOverrides((prev) => {
      const baselineVisible = baselineVisibleSet.has(elementId);
      const nextOverrides = { ...prev, [elementId]: nextVisible };
      if (nextVisible === baselineVisible) {
        delete nextOverrides[elementId];
      }
      return nextOverrides;
    });
  }, [baselineVisibleSet, stageDataForDisplay]);

  const handleResetElementVisibility = useCallback(() => {
    setElementVisibilityOverrides({});
  }, []);

  const handleJumpToStage = useCallback((targetIndex) => {
    if (!Array.isArray(stageCatalog) || !stageCatalog[targetIndex]) return;
    overlayLockedUntilRef.current = 0; // Clear lock on explicit user navigation
    stageManuallyChangedRef.current = true; // Suppress stale uiActions highlights
    restoreOverlay(true);
    const targetStage = stageCatalog[targetIndex];
    setCurrentStageData(targetStage);
    setCurrentStageNum(targetIndex);
    setElementVisibilityOverrides({});
    onStageChange?.(targetIndex);
    persistStageJump(targetIndex); // F2: arrow-key / local-stage jump must persist too
  }, [onStageChange, persistStageJump, restoreOverlay, stageCatalog]);

  const handleNextStage = useCallback(async () => {
    overlayLockedUntilRef.current = 0;
    stageManuallyChangedRef.current = true; // Suppress stale uiActions highlights
    restoreOverlay(true);
    setIsLoading(true);
    try {
      if (!graphData?.graph_id || !studentProfile?.student_id) {
        console.warn('Graph ID or student ID is missing, cannot request next stage.');
        return;
      }
      const requestedGraphId = graphData.graph_id;
      const response = await api.post(
        `/api/graphs/${graphData.graph_id}/next-stage`,
        null,
        { params: { session_id: sessionId || undefined } }
      );
      // A response for the previous chart (switched while in flight) is dropped (Codex B-R10).
      if (currentGraphIdRef.current !== requestedGraphId) return;

      setCurrentStageData(response.data.data);
      setCurrentStageNum(response.data.stage);
      setElementVisibilityOverrides({});
      onStageChange?.(response.data.stage);
    } catch (error) {
      logClientError('次の段階取得失敗:', error);
      alert(t('ig.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  }, [graphData?.graph_id, onStageChange, restoreOverlay, studentProfile?.student_id, sessionId, t]);

  const handlePrevStage = useCallback(async () => {
    overlayLockedUntilRef.current = 0;
    stageManuallyChangedRef.current = true; // Suppress stale uiActions highlights
    restoreOverlay(true);
    setIsLoading(true);
    try {
      if (!graphData?.graph_id || !studentProfile?.student_id) {
        console.warn('Graph ID or student ID is missing, cannot request previous stage.');
        return;
      }
      const requestedGraphId = graphData.graph_id;
      const response = await api.post(
        `/api/graphs/${graphData.graph_id}/prev-stage`,
        null,
        { params: { session_id: sessionId || undefined } }
      );
      if (currentGraphIdRef.current !== requestedGraphId) return;

      setCurrentStageData(response.data.data);
      setCurrentStageNum(response.data.stage);
      setElementVisibilityOverrides({});
      onStageChange?.(response.data.stage);
    } catch (error) {
      logClientError('前の段階取得失敗:', error);
      alert(t('ig.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  }, [graphData?.graph_id, onStageChange, restoreOverlay, studentProfile?.student_id, sessionId, t]);

  const handleStageKeyboard = useCallback((event) => {
    const tag = event.target;
    const tagName = (tag?.tagName || '').toUpperCase();
    if (['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON'].includes(tagName) || tag?.isContentEditable) {
      return;
    }
    // A4 (Tier-1 a11y): when focus sits on a keyboard-wired chart element,
    // Arrow keys belong to the roving-tabindex navigation INSIDE the chart
    // (attachInteractiveHandlers keydown handler). This window listener is
    // CAPTURE-phase — it fires before the element handler and cannot be
    // stopped from there — so it must yield here, or every Arrow press on a
    // chart element would also jump the stage and re-render the svg out from
    // under the user's focus.
    if (tag?.classList?.contains('ig-kbd-item')) {
      return;
    }
    // Same rationale for the magnified chart viewport: while zoomed it owns the
    // Arrow keys for panning (see handleZoomKeyDown), so this capture-phase
    // listener must yield or a keyboard pan would ALSO jump — and persist — the
    // learning stage (Codex P1). Guard on data-zoomed so that at fit (100%) the
    // Arrow keys still navigate stages even if focus lingers on the viewport
    // after a reset (Codex P2).
    if (tag?.classList?.contains('gt-zoom-viewport') && tag?.getAttribute?.('data-zoomed') === 'true') {
      return;
    }
    const totalStages = graphData?.total_stages || stageCatalog.length;
    if (totalStages <= 1) return;
    const hasLocalStages = Array.isArray(stageCatalog) && stageCatalog.length > 0;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (hasLocalStages) {
        const next = Math.min((currentStageNum ?? 0) + 1, stageCatalog.length - 1);
        if (next !== currentStageNum) handleJumpToStage(next);
      } else {
        handleNextStage();
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (hasLocalStages) {
        const prev = Math.max((currentStageNum ?? 0) - 1, 0);
        if (prev !== currentStageNum) handleJumpToStage(prev);
      } else {
        handlePrevStage();
      }
    }
  }, [currentStageNum, handleJumpToStage, handleNextStage, handlePrevStage, stageCatalog, graphData?.total_stages]);

  useEffect(() => {
    window.addEventListener('keydown', handleStageKeyboard, true);
    return () => window.removeEventListener('keydown', handleStageKeyboard, true);
  }, [handleStageKeyboard]);

  const handleColorOverrideChange = useCallback((key, value) => {
    setColorOverrides((prev) => ({
      ...prev,
      [key]: value || null
    }));
  }, []);

  const handleElementColorChange = useCallback((elementId, value) => {
    if (!elementId) return;
    setColorOverrides((prev) => ({
      ...prev,
      elements: {
        ...(prev.elements || {}),
        [elementId]: value || null
      }
    }));
  }, []);

  const toggleElementVisibility = useCallback((elementId) => {
    setElementVisibilityOverrides(prev => ({ ...prev, [elementId]: !prev[elementId] }));
  }, []);

  const handleRetryTts = useCallback(() => {
    setExplanationAudioError(null);
    setTtsRetryToken((prev) => prev + 1);
  }, []);

  const handleToggleHighlight = useCallback((key) => {
    setHighlightOptions((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const handleResetColorPreferences = useCallback(() => {
    setColorOverrides(createDefaultColorOverrides());
    setHighlightOptions(createDefaultHighlightOptions());
    setHighlightAccentColor(DEFAULT_HIGHLIGHT_ACCENT_COLOR);
  }, []);

  const handleLegendPositionChange = (position) => {
    if (position === 'auto') {
      setLayoutOverrides((prev) => ({
        ...prev,
        legendPosition: null,
        legendManual: null
      }));
    } else {
      setLayoutOverrides((prev) => ({
        ...prev,
        legendPosition: position,
        legendManual: null
      }));
    }
  };

  const resetLayoutOverrides = () => {
    setLayoutOverrides({
      width: null,
      height: null,
      margin: { top: null, right: null, bottom: null, left: null },
      legendPosition: null,
      legendManual: null,
      legendFontScale: null,
      legendLineSpacing: null,
      valueLabelScale: null,
      functionLabelDensity: null,
      functionLabelsVisible: null,
      axis: { xTickFontSize: null, yTickFontSize: null, xLabelFontSize: null, yLabelFontSize: null }
    });
    // P3: リセットも SVG 再描画で active overlay をクリアするため、overlay を再適用する。
    setOverlayRenderTick((t) => t + 1);
  };

  const handleReset = useCallback(async () => {
    if (!graphData?.graph_id) return;
    overlayLockedUntilRef.current = 0;
    restoreOverlay(true);

    // Sync with backend so next-stage/prev-stage start from stage 0
    try {
      await api.post(`/api/graphs/${graphData.graph_id}/set-stage`, null, {
        params: { stage: 0, session_id: sessionId || undefined }
      });
    } catch (e) {
      logClientWarn('Failed to sync reset stage to backend:', e);
    }

    // Set frontend state
    if (graphData?.first_stage) {
      setCurrentStageData(graphData.first_stage);
    } else if (stageCatalog[0]) {
      setCurrentStageData(stageCatalog[0]);
    }
    setCurrentStageNum(0);
    setElementVisibilityOverrides({});
    onStageChange?.(0);
  }, [graphData, sessionId, restoreOverlay, stageCatalog, onStageChange]);

  const startHeightResize = useCallback((pointerEvent) => {
    if (!pointerEvent) return;
    pointerEvent.preventDefault();
    const startY = pointerEvent.clientY;
    const startHeight = layoutOverrides.height ?? finalLayout.height ?? adaptiveLayout.height;
    const onMove = (event) => {
      const dy = event.clientY - startY;
      const nextHeight = clampValue(startHeight + dy, MIN_CHART_HEIGHT, MAX_CHART_HEIGHT);
      setLayoutOverrides((prev) => ({ ...prev, height: nextHeight }));
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  }, [adaptiveLayout.height, finalLayout.height, layoutOverrides.height]);

  const chartHeight = finalLayout.height;

  return (
    <div className="rounded-xl p-3 flex flex-col gap-2" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--primary-bg)', color: 'var(--primary)', boxShadow: 'var(--shadow-sm)' }}>
            Stage {currentStageNum + 1} / {graphData?.total_stages || (stageCatalog.length || '?')}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <button
            onClick={handlePrevStage}
            disabled={isLoading || currentStageNum === 0}
            className="btn-secondary rounded-full px-2.5 py-1 text-xs disabled:cursor-not-allowed shadow-sm"
          >
            <FuriganaText text={t('ig.prevStage')} />
          </button>
          <button
            onClick={handleNextStage}
            disabled={isLoading || currentStageNum >= (graphData?.total_stages - 1)}
          className="btn-primary rounded-full px-3 py-1 text-xs disabled:cursor-not-allowed shadow-sm"
        >
          <FuriganaText text={isLoading ? t('common.loading') :
           currentStageNum >= (graphData?.total_stages - 1) ? t('ig.complete') : t('ig.nextStage')} />
        </button>
          <button
            onClick={handleReset}
          className="btn-secondary rounded-full px-2.5 py-1 text-xs shadow-sm"
        >
          <FuriganaText text={t('ig.restart')} />
        </button>
        <button
          onClick={() => setIsLayoutPanelOpen((prev) => !prev)}
          className="px-2.5 py-1 text-xs rounded-full font-semibold transition-colors shadow-sm"
          style={isLayoutPanelOpen
            ? { background: 'var(--primary)', color: 'var(--text-on-primary)' }
            : { background: 'var(--primary-bg)', color: 'var(--primary)' }
          }
        >
          <FuriganaText text={t('ig.adjustGraph')} />
        </button>
        <div className="flex items-center gap-1" role="group" aria-label={t('ig.zoom.groupLabel')}>
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={!chartZoom.canZoomOut(zoomState)}
            aria-label={t('ig.zoom.out')}
            title={fa(t('ig.zoom.out'))}
            className="btn-secondary rounded-full flex items-center justify-center text-base leading-none shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <span aria-hidden="true">−</span>
          </button>
          <span
            className="text-xs tabular-nums text-center"
            style={{ minWidth: '3.25rem' }}
            aria-live="polite"
          >
            {chartZoom.formatZoomPercent(zoomState.scale)}
          </span>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={!chartZoom.canZoomIn(zoomState)}
            aria-label={t('ig.zoom.in')}
            title={fa(t('ig.zoom.in'))}
            className="btn-secondary rounded-full flex items-center justify-center text-base leading-none shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <span aria-hidden="true">+</span>
          </button>
          <button
            type="button"
            onClick={handleZoomReset}
            disabled={!chartZoom.isZoomed(zoomState)}
            aria-label={t('ig.zoom.reset')}
            title={fa(t('ig.zoom.reset'))}
            className="btn-secondary rounded-full px-3 text-xs shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minHeight: 44 }}
          >
            <FuriganaText text={t('ig.zoom.resetShort')} />
          </button>
        </div>
      </div>
    </div>

      {isTrigFunction && (
        <div className="flex items-center gap-2 px-1 pb-2">
          <button
            onClick={() => setTrigViewMode('unit_circle')}
            className="px-3 py-1.5 text-sm rounded-full font-semibold transition-colors shadow-sm"
            style={trigViewMode === 'unit_circle'
              ? { background: 'var(--primary)', color: 'var(--text-on-primary)' }
              : { background: 'var(--bg-muted)', color: 'var(--text-muted)' }
            }
          ><FuriganaText text={t('ig.unitCircle')} /></button>
          <button
            onClick={() => setTrigViewMode('curve_only')}
            className="px-3 py-1.5 text-sm rounded-full font-semibold transition-colors shadow-sm"
            style={trigViewMode === 'curve_only'
              ? { background: 'var(--primary)', color: 'var(--text-on-primary)' }
              : { background: 'var(--bg-muted)', color: 'var(--text-muted)' }
            }
          ><FuriganaText text={t('ig.curveOnly')} /></button>
          <button
            onClick={() => setTrigViewMode('dual_pane')}
            className="px-3 py-1.5 text-sm rounded-full font-semibold transition-colors shadow-sm"
            style={trigViewMode === 'dual_pane'
              ? { background: 'var(--primary)', color: 'var(--text-on-primary)' }
              : { background: 'var(--bg-muted)', color: 'var(--text-muted)' }
            }
          ><FuriganaText text={t('ig.dual')} /></button>
        </div>
      )}

      {/* The visible intent-framing line + "グラフの要約 / Chart summary" panel were removed as
          duplicates of the below-chart real-time stage explanation. The NON-VISUAL a11y that reused the
          same data survives untouched: chartSummaryText still feeds the <svg> aria-label + D3
          title/desc, and stageAnnounceTitle still feeds the sr-only aria-live stage announcer. */}

      <div className="rounded-2xl" style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}>
        <div
          ref={graphContainerRef}
          style={{
            position: 'relative',
            width: '100%',
            height: `${chartHeight}px`,
            minHeight: `${MIN_CHART_HEIGHT}px`,
            maxHeight: `${MAX_CHART_HEIGHT}px`,
            overflow: 'hidden',
            borderRadius: '18px',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            background: 'linear-gradient(145deg, rgba(236, 244, 255, 0.65), rgba(255, 255, 255, 0.92))'
          }}
        >
          <div className="absolute right-4 top-4 flex flex-col items-end gap-2 z-30">
            <button
              onClick={() => setIsElementPanelOpen((prev) => !prev)}
              className="rounded-full px-4 py-1.5 text-xs font-semibold shadow"
              style={isElementPanelOpen
                ? { background: 'var(--primary)', color: 'var(--text-on-primary)' }
                : { background: 'var(--bg-surface)', color: 'var(--primary)' }
              }
            >
              <FuriganaText text={isElementPanelOpen ? t('ig.closePanel') : `${t('ig.visibleElements')} (${visibleElementCount})`} />
            </button>
            {isElementPanelOpen && (
              <div className="w-[260px] max-w-[92vw] max-h-[70vh] overflow-y-auto rounded-2xl border p-4 shadow-2xl flex flex-col gap-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}>
                <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}>
                  <div className="flex items-center justify-between text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    <span><FuriganaText text={t('ig.colorCustomize')} /></span>
                    {(colorOverrides.label || colorOverrides.legendText || colorOverrides.legendBackground || colorOverrides.explanation) && (
                      <button
                        onClick={() => setColorOverrides((prev) => ({
                          ...prev,
                          label: null,
                          legendText: null,
                          legendBackground: null,
                          explanation: null
                        }))}
                        className="text-xs" style={{ color: 'var(--primary)' }}
                      >
                        <FuriganaText text={t('ig.clear')} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[
                      { key: 'label', label: t('ig.color.label'), defaultColor: DEFAULT_LABEL_COLOR },
                      { key: 'legendText', label: t('ig.color.legendText'), defaultColor: DEFAULT_LEGEND_TEXT_COLOR },
                      { key: 'legendBackground', label: t('ig.color.legendBackground'), defaultColor: DEFAULT_LEGEND_BACKGROUND },
                      { key: 'explanation', label: t('ig.color.explanation'), defaultColor: DEFAULT_EXPLANATION_COLOR }
                    ].map((item) => (
                      <label key={item.key} className="flex items-center justify-between gap-2 rounded-lg border px-2 py-1" style={{ borderColor: 'var(--border)' }}>
                        <span className="font-semibold" style={{ color: 'var(--text)' }}><FuriganaText text={item.label} /></span>
                        <input
                          type="color"
                          value={colorOverrides[item.key] || item.defaultColor}
                          onChange={(e) => handleColorOverrideChange(item.key, e.target.value)}
                          className="h-7 w-14 cursor-pointer rounded border" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
                        />
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {[
                      { key: 'labels', label: t('ig.emphasis.labels') },
                      { key: 'legend', label: t('ig.emphasis.legend') }
                    ].map((toggle) => (
                      <button
                        key={toggle.key}
                        onClick={() => handleToggleHighlight(toggle.key)}
                        style={
                          highlightOptions[toggle.key]
                            ? { backgroundColor: toRgba(highlightAccentColor, 0.22), color: highlightAccentColor }
                            : {}
                        }
                        className={`rounded-full px-3 py-1 font-semibold transition ${
                          highlightOptions[toggle.key]
                            ? 'border border-transparent'
                            : 'border'
                        }
                        style={
                          highlightOptions[toggle.key]
                            ? {}
                            : { background: 'var(--bg-muted)', color: 'var(--text-muted)', borderColor: 'var(--border)' }
                        }`}
                      >
                        <FuriganaText text={toggle.label} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                      <FuriganaText>{t('ig.visibleElements')} ({visibleElementCount})</FuriganaText>
                    </span>
                    {hasElementOverrides && (
                      <button
                        onClick={handleResetElementVisibility}
                        className="text-xs" style={{ color: 'var(--primary)' }}
                      >
                        <FuriganaText text={t('ig.reset')} />
                      </button>
                    )}
                  </div>
                  <div className="relative max-h-[320px] overflow-y-auto space-y-2 pr-1">
                    {sidebarElements.length === 0 && (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('ig.noElements')} /></p>
                    )}
                    <div className="space-y-2">
                      {groupedSidebarElements.map((group) => (
                        <div key={`element-group-${group.key}`} className="space-y-1">
                          <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                            <span>{group.accent}</span>
                            <span><FuriganaText text={group.label} /></span>
                            <span style={{ color: 'var(--text-muted)' }}>({group.elements.length})</span>
                          </div>
                          <div className="space-y-1">
                            {group.elements.map((element) => (
                              <div
                                key={element.id}
                                className={`flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-[11px] transition ${
                                  ''
                                }
                                style={element.isVisible
                                  ? { borderColor: 'var(--primary-light)', background: 'var(--bg-surface)', boxShadow: '0 4px 10px rgba(61,90,110,0.08)' }
                                  : { borderColor: 'var(--border)', background: 'var(--bg-muted)', color: 'var(--text-muted)' }
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span
                                    className="h-3 w-3 rounded"
                                    style={{ backgroundColor: element.color, boxShadow: element.isVisible ? `0 0 0 4px ${element.color}22` : 'none' }}
                                  />
                                  <div className="flex flex-col leading-tight">
                                    <span className="font-semibold"><FuriganaText text={element.label} /></span>
                                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('ig.elementType.' + element.type, { defaultValue: element.type })} /></span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    value={colorOverrides.elements?.[element.id] || element.color || '#94a3b8'}
                                    onChange={(e) => handleElementColorChange(element.id, e.target.value)}
                                    className="h-6 w-8 cursor-pointer rounded border" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
                                  />
                                  <button
                                    onClick={() => toggleElementVisibility(element.id)}
                                    className={`rounded-full border px-2 py-1 text-[10px] font-semibold transition ${
                                      ''
                                    }
                                    style={element.isVisible
                                      ? { borderColor: 'var(--primary-light)', background: 'var(--primary-bg)', color: 'var(--primary)' }
                                      : { borderColor: 'var(--border)', background: 'var(--bg-surface)', color: 'var(--text-muted)' }
                                    }`}
                                  >
                                    <FuriganaText text={element.isVisible ? t('ig.visible') : t('ig.hidden')} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* role="group" (NOT "img"): per WAI-ARIA, role="img" makes ALL
              descendants presentational, which would strip the A4
              role="button" chart elements out of the accessibility tree —
              screen readers could Tab to them but never expose them as
              controls. A labelled group keeps the chart summary as the
              accessible name AND preserves the interactive descendants'
              semantics. The full summary also lives in <title>/<desc>
              (D3-appended at render). */}
          <div
            className="gt-zoom-viewport"
            // Focusable for keyboard pan ONLY while magnified — at fit it must
            // not add a Tab stop before the chart's roving-tabindex elements
            // (zoom itself is driven by the always-focusable toolbar buttons).
            tabIndex={chartZoom.isZoomed(zoomState) ? 0 : -1}
            data-zoomed={chartZoom.isZoomed(zoomState) ? 'true' : 'false'}
            role="group"
            aria-label={t('ig.zoom.paneLabel')}
            onKeyDown={handleZoomKeyDown}
            onPointerDown={handleZoomPointerDown}
            onPointerMove={handleZoomPointerMove}
            onPointerUp={handleZoomPointerUp}
            onPointerCancel={handleZoomPointerUp}
            style={{
              width: '100%',
              height: '100%',
              transform: chartZoom.toTransform(zoomState),
              transformOrigin: 'center center',
              transition: isZoomDragging ? 'none' : 'transform 0.12s ease-out',
              touchAction: chartZoom.isZoomed(zoomState) ? 'none' : 'auto',
              cursor: chartZoom.isZoomed(zoomState) ? (isZoomDragging ? 'grabbing' : 'grab') : 'default',
              willChange: 'transform',
            }}
          >
            <svg
              ref={svgRef}
              data-chart-root=""
              role="group"
              aria-label={chartSummaryText}
              style={{ width: '100%', height: '100%', display: 'block' }}
            ></svg>
          </div>
          {/* A2 (Tier-1 a11y / Gate B): visually-hidden aria-live region for
              stage-transition announcements. Content is empty by default
              (toggle OFF, see ariaLiveEnabled above) and is updated ONLY by
              the throttled announce effect near stageAnnounceMessage —
              never directly from highlight/dim/focus/overlay handlers. */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {stageAnnounceText}
          </div>
          {tooltipInfo && (
            <div
              className="pointer-events-none absolute z-20 max-w-xs rounded-xl border px-3 py-2 text-xs"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)', left: `${tooltipInfo.x}px`, top: `${tooltipInfo.y}px` }}
            >
              <div className="flex items-center gap-1 font-semibold" style={{ color: 'var(--text)' }}>
                <span>{tooltipInfo.icon}</span>
                <span><FuriganaText text={tooltipInfo.title} /></span>
              </div>
              {tooltipInfo.subtitle && (
                <div className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}><FuriganaText text={tooltipInfo.subtitle} /></div>
              )}
              {tooltipInfo.detailLines && tooltipInfo.detailLines.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-[11px] space-y-0.5" style={{ color: 'var(--text-muted)' }}>
                  {tooltipInfo.detailLines.map((line, idx) => (
                    <li key={`tooltip-line-${idx}`}><FuriganaText text={line} /></li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div
            className="absolute inset-x-4 bottom-2 h-3 cursor-ns-resize rounded-full opacity-80 hover:opacity-100"
            style={{ background: 'var(--border-strong)' }}
            onPointerDown={startHeightResize}
            title={fa(t('ig.dragResize'))}
          />
        </div>
      </div>
      {drilldownInfo && (
        <div className="rounded-2xl border p-4 space-y-2" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center gap-2 font-semibold" style={{ color: 'var(--primary)' }}>
            <span>{drilldownInfo.icon}</span>
            <span><FuriganaText text={drilldownInfo.title} /></span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{drilldownInfo.timestamp}</span>
          </div>
          {drilldownInfo.description && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}><FuriganaText text={drilldownInfo.description} /></p>
          )}
          {drilldownInfo.detailLines && drilldownInfo.detailLines.length > 0 && (
            <ul className="list-disc pl-5 text-sm space-y-1" style={{ color: 'var(--text-muted)' }}>
              {drilldownInfo.detailLines.map((line, idx) => (
                <li key={`drilldown-line-${idx}`}><FuriganaText text={line} /></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ステップ説明パネル - 新機能 */}
      {explanationLinesWithEmoji.length > 0 && (
        <div className="mb-6 p-6 rounded-2xl" style={{ background: 'var(--primary-bg)', border: '2px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
          <div
            className="text-lg whitespace-pre-wrap mb-4 rounded-2xl px-3 py-2 transition"
            style={{
              fontFamily: resolveFontFamily(settings?.fontFamily),
              fontSize: `${settings?.fontSize || 12}px`,
              letterSpacing: `${settings?.letterSpacing || 0.05}em`,
              color: explanationStyles.color,
              backgroundColor: explanationStyles.backgroundColor,
              boxShadow: explanationStyles.boxShadow
            }}
            ref={explanationRef}
            data-stage-explanation="true"
            onMouseUp={handleExplanationSelection}
          >
            {renderExplanationWithHighlight(stageExplanation)}
          </div>

          {explanationHighlights.length > 0 && (
            <div className="mb-2 space-y-2">
              <div className="flex flex-wrap gap-2">
                {explanationHighlights.map((text, idx) => (
                  <button
                    key={`exp-chip-${idx}`}
                    onClick={() => removeExplanationHighlight(text)}
                    className="flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold transition"
                    style={{ background: 'rgba(109,69,23,0.2)', color: 'var(--warning)' }}
                    title={fa(t('ig.clickToRemove'))}
                  >
                    <span className="max-w-[180px] truncate"><FuriganaFragment source={stageExplanation} text={text} /></span>
                    <span>×</span>
                  </button>
                ))}
              </div>
              <button
                onClick={clearExplanationHighlights}
                className="rounded-full px-3 py-1 text-[11px] font-semibold transition"
                style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
              >
                <FuriganaText text={t('ig.clearAll')} />
              </button>
            </div>
          )}

          {/* 説明音声プレーヤー */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}><FuriganaText text={t('ig.listenExplanation')} /></span>
              {!finalExplanationAudioUrl && voiceOutputEnabled && isGeneratingExplanationAudio && (
                <span className="text-xs flex items-center gap-1" style={{ color: 'var(--primary-light)' }}>
                  <svg className="animate-spin h-3 w-3" style={{ color: 'var(--primary-light)' }} viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z"></path>
                  </svg>
                  <FuriganaText text={t('ig.generating')} />
                </span>
              )}
              {!voiceOutputEnabled && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('ig.audioDisabled')} /></span>
              )}
            </div>
            {voiceOutputEnabled && isTtsSlowWarning && (
              <div className="text-xs flex items-center gap-1" style={{ color: 'var(--warning)' }}>
                <span>⚠️</span>
                <span><FuriganaText text={t('ig.ttsSlowWarning')} /></span>
              </div>
            )}
            {voiceOutputEnabled && explanationAudioError && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--danger)' }}><FuriganaText text={t('ig.ttsError')} /></span>
                <button
                  onClick={handleRetryTts}
                  className="text-xs font-semibold underline-offset-2 hover:underline"
                  style={{ color: 'var(--primary)' }}
                >
                  <FuriganaText text={t('ig.retry')} />
                </button>
              </div>
            )}
            {voiceOutputEnabled && (networkInfo || isMobileDevice) && (
              <div className="text-[11px] flex flex-wrap gap-2" style={{ color: 'var(--text-muted)' }}>
                {networkInfo && <span><FuriganaText text={networkStatusLabel} /></span>}
                {isNetworkLikelySlow && <span><FuriganaText text={t('ig.weakConnection')} /></span>}
                {isMobileDevice && <span><FuriganaText text={t('ig.mobileWifi')} /></span>}
              </div>
            )}
            {voiceOutputEnabled && finalExplanationAudioUrl && (
              <AudioPlayer
                audioUrl={finalExplanationAudioUrl}
                onEnded={() => console.log('説明音声再生完了')}
              />
            )}
          </div>
        </div>
      )}

      {/* 布局调整面板 — settings 同型のモーダル: フレックス中央寄せ + max-h + 内部スクロール
          + 背景クリック/Esc で閉じる（縦に長くてもヘッダー/✕ がビューポート外に出ない）*/}
      {isLayoutPanelOpen && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm px-4 py-6"
          style={{ backgroundColor: 'rgba(30,45,56,0.4)' }}
          onClick={() => setIsLayoutPanelOpen(false)}
        >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('ig.adjustGraph')}
          className="w-full max-w-5xl rounded-2xl p-6 shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto"
          style={{ background: 'var(--bg-surface, #fff)', border: '1px solid var(--border)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <FuriganaText text={t('ig.adjustGraph')} />
            </h3>
            <button
              onClick={() => setIsLayoutPanelOpen(false)}
              className="text-lg px-2 py-0.5 rounded-lg hover:bg-gray-200 transition-colors"
              style={{ color: 'var(--text-muted)' }}
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('ig.layoutPanelDesc')} />
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.width', { px: finalLayout.width })} />
              </label>
              <input
                type="range"
                min="640"
                max="1200"
                step="10"
                value={layoutOverrides.width ?? adaptiveLayout.width}
                onChange={(e) => handleDimensionOverrideChange('width', e.target.value, [640, 1200])}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.height', { px: finalLayout.height })} />
              </label>
              <input
                type="range"
                min="320"
                max="900"
                step="10"
                value={layoutOverrides.height ?? adaptiveLayout.height}
                onChange={(e) => handleDimensionOverrideChange('height', e.target.value, [320, 900])}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.xTick', { px: finalLayout.axis?.xTickFontSize ?? 20 })} />
              </label>
              <input
                type="range"
                min="8"
                max="32"
                step="1"
                value={finalLayout.axis?.xTickFontSize ?? 20}
                onChange={(e) => handleAxisFontSizeChange('xTick', e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.xTick')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.xLabel', { px: finalLayout.axis?.xLabelFontSize ?? 20 })} />
              </label>
              <input
                type="range"
                min="8"
                max="32"
                step="1"
                value={finalLayout.axis?.xLabelFontSize ?? 20}
                onChange={(e) => handleAxisFontSizeChange('xLabel', e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.xLabel')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.yTick', { px: finalLayout.axis?.yTickFontSize ?? 20 })} />
              </label>
              <input
                type="range"
                min="8"
                max="32"
                step="1"
                value={finalLayout.axis?.yTickFontSize ?? 20}
                onChange={(e) => handleAxisFontSizeChange('yTick', e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.yTick')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.yLabel', { px: finalLayout.axis?.yLabelFontSize ?? 20 })} />
              </label>
              <input
                type="range"
                min="8"
                max="32"
                step="1"
                value={finalLayout.axis?.yLabelFontSize ?? 20}
                onChange={(e) => handleAxisFontSizeChange('yLabel', e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.yLabel')}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('ig.axisFontDesc')} />
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.legendFontSize', { scale: (finalLayout.legend?.fontScale ?? 1).toFixed(1) })} />
              </label>
              <input
                type="range"
                min="0.6"
                max="2"
                step="0.1"
                value={finalLayout.legend?.fontScale ?? 1}
                onChange={(e) => handleLegendFontScaleChange(e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.legendFontSize')}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('ig.legendFontDesc')} />
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.legendLineSpacing', { scale: (finalLayout.legend?.lineSpacing ?? 1).toFixed(1) })} />
              </label>
              <input
                type="range"
                min="0.8"
                max="2.5"
                step="0.1"
                value={finalLayout.legend?.lineSpacing ?? 1}
                onChange={(e) => handleLegendLineSpacingChange(e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.legendLineSpacing')}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('ig.legendLineSpacingDesc')} />
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.valueLabelSize', { scale: (finalLayout.valueLabelScale ?? 1).toFixed(1) })} />
              </label>
              <input
                type="range"
                min="0.6"
                max="2.5"
                step="0.1"
                value={finalLayout.valueLabelScale ?? 1}
                onChange={(e) => handleValueLabelScaleChange(e.target.value)}
                className="w-full"
                style={{ accentColor: 'var(--primary)' }}
                aria-label={t('ig.aria.valueLabelSize')}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('ig.valueLabelDesc')} />
              </p>
            </div>
            {isNonTrigFunctionType(graphData?.graph_data?.graph_type || graphData?.graph_type) && (
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                  <FuriganaText text={t('ig.functionLabelDensity', { count: Math.round(finalLayout.functionLabelBudget ?? 0) })} />
                </label>
                <input
                  type="range"
                  min="0"
                  max={FUNCTION_LABEL_BUDGET_MAX}
                  step="1"
                  value={Math.round(finalLayout.functionLabelBudget ?? 0)}
                  onChange={(e) => handleFunctionLabelDensityChange(e.target.value)}
                  className="w-full"
                  style={{ accentColor: 'var(--primary)' }}
                  aria-label={t('ig.aria.functionLabelDensity')}
                />
                <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer" style={{ color: 'var(--text)' }}>
                  <input
                    type="checkbox"
                    checked={finalLayout.functionLabelsVisible !== false}
                    onChange={(e) => handleFunctionLabelsVisibleToggle(e.target.checked)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  <FuriganaText text={t('ig.functionLabelsVisible')} />
                </label>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('ig.functionLabelDensityDesc')} />
                </p>
              </div>
            )}
            {['top', 'bottom', 'left', 'right'].map((side) => (
              <div key={side}>
                <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                  {side === 'top' && <FuriganaText text={t('ig.margin.top')} />}
                  {side === 'bottom' && <FuriganaText text={t('ig.margin.bottom')} />}
                  {side === 'left' && <FuriganaText text={t('ig.margin.left')} />}
                  {side === 'right' && <FuriganaText text={t('ig.margin.right')} />}
                </label>
                <input
                  type="number"
                  min="20"
                  max="400"
                  value={
                    (layoutOverrides.margin?.[side] ??
                      adaptiveLayout.margin?.[side]) ?? ''
                  }
                  onChange={(e) => handleMarginOverrideChange(side, e.target.value)}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', '--tw-ring-color': 'var(--primary-light)' }}
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('ig.legendPosition')} />
              </label>
              <select
                value={layoutOverrides.legendPosition ?? 'auto'}
                onChange={(e) => handleLegendPositionChange(e.target.value)}
                className="w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2"
                style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', '--tw-ring-color': 'var(--primary-light)' }}
              >
                <option value="auto">{t('ig.legendPos.auto')}</option>
                <option value="right">{t('ig.legendPos.right')}</option>
                <option value="bottom">{t('ig.legendPos.bottom')}</option>
              </select>
            </div>
            <div>
              {/* A2 (Tier-1 a11y / Gate B): 図の調整 panel opt-in toggle for
                  the stage-transition aria-live announcer. Persisted via
                  ARIA_LIVE_ENABLED_STORAGE_KEY ('graphTutor::ariaLiveEnabled::v1'),
                  same load-once/save-on-change idiom as the axis/legend
                  font-size prefs above. Default OFF. */}
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  checked={ariaLiveEnabled}
                  onChange={(e) => setAriaLiveEnabled(e.target.checked)}
                  style={{ accentColor: 'var(--primary)' }}
                />
                <FuriganaText text={t('ig.a11y.announceToggleLabel')} />
              </label>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('ig.a11y.announceToggleDesc')} />
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={resetLayoutOverrides}
              className="btn-secondary px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
            >
              <FuriganaText text={t('ig.resetToAuto')} />
            </button>
          </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InteractiveGraph;
export { getSmartEmojiForLabel };
